import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";
import { promptDraftName } from "../shared/draftNameDialog";
import { rewriteAssetReferences, subscribeToAssetRelocations } from "../features/assets/relocation";
import {
  FaApple,
  FaCheck,
  FaChevronRight,
  FaCopy,
  FaEdit,
  FaExpand,
  FaFileCode,
  FaFlag,
  FaImage,
  FaLayerGroup,
  FaListUl,
  FaMagic,
  FaGlobe,
  FaHistory,
  FaLock,
  FaPlus,
  FaArchive,
  FaFileWord,
  FaRegEdit,
  FaSave,
  FaSearch,
  FaStar,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaWindows,
} from "react-icons/fa";
import * as questionBankApi from "../api/mathBank";
import QuestionCategorySelect, { subjectCategories } from "../components/math/QuestionCategorySelect";
import SubjectWorkspace from "../components/math/SubjectWorkspace";
import QuestionFamilyWorkspace from "../components/math/QuestionFamilyWorkspace";
import QuestionFamilyAnalysis from "../components/math/QuestionFamilyAnalysis";
import SearchableSelect from "../components/math/SearchableSelect";
import QuestionFilterSelect from "../components/math/QuestionFilterSelect";
import { filterMatches, filterParam, reconcileUnitFilter } from "../shared/questionFilters";
import { questionDifficulties } from "../components/document-processing/mathBankQuestionOptions";
import QuestionFamilyFields, { familyFormValues, familyPayload } from "../components/math/QuestionFamilyFields";
import { createGrade, createUnit } from "../api/mathBank";
import { parseWordDocument } from "../api/documentProcessing";
import { extractOriginalFigureAssets } from "../features/document-processing/handoutFigures";
import { collectQuestionPromptImages, getQuestionPromptImageSource } from "../features/document-processing/questionSolutionImages";
import ExpandedEditorModal from "../components/document-processing/ExpandedEditorModal";
import QuestionAssetInputMode, { QuestionAssetRoleButtons } from "../components/math/QuestionAssetInputMode";
import QuestionAssetEditorPreview from "../components/math/QuestionAssetEditorPreview";
import MatplotlibQuickPreview from "../components/document-processing/MatplotlibQuickPreview";
import QuestionAssetPreviewStrip, {
  getPreviewableQuestionAssets,
} from "../components/document-processing/QuestionAssetPreviewStrip";
import MathText from "../components/math/MathText";
import QuestionPreviewModal from "../components/math/QuestionPreviewModal";
import QuestionDifficultyBadge from "../components/math/QuestionDifficultyBadge";
import { hasAnswerContent, getAnswerDisplayText } from "../components/math/QuestionAnswerState";
import { getClipboardFiles, isVisiblePasteRoot } from "../shared/clipboardFiles";
import {
  getCachedMatplotlibPreview,
  getOrRenderMatplotlibPreview,
} from "../shared/matplotlibPreviewCache";
import {
  calculateAnchoredScrollTop,
  selectViewportAnchor,
} from "../shared/scrollAnchor";
import {
  clearDocumentBulkImportTransfer,
  DOCUMENT_BULK_IMPORT_TRANSFER_EVENT,
  readDocumentBulkImportTransfer,
} from "../features/document-processing/bulkImportTransfer";
import { useAuth } from "../utils";
import {
  PageShell,
  Header,
  TitleGroup,
  Eyebrow,
  PageTitle,
  HeaderActions,
  CountText,
  TabBar,
  TabButton,
  Notice,
  SpinningIcon,
  LoadingContainer,
  LoadingText,
  InlineLoading,
  QuestionWorkspace,
  QuestionLookupSection,
  QuestionLookupHeader,
  SectionCount,
  FilterBar,
  SearchBox,
  AnswerStateButton,
  CompactSelect,
  SplitLayout,
  TaxonomyGrid,
  EditorPanel,
  TablePanel,
  TaxonomyTableHeader,
  PanelHeader,
  PanelTitleGroup,
  PanelTitle,
  InlineIdButton,
  FieldGrid,
  Field,
  FieldHeader,
  InlineToolButton,
  TwoColumnText,
  ChoiceOptionGrid,
  ChoiceOptionRow,
  QuestionPreviewPanel,
  PreviewHeader,
  PreviewGrid,
  PreviewBlock,
  PreviewLabel,
  PreviewContent,
  ToggleLabel,
  AssetSection,
  AssetHeader,
  AssetList,
  AssetRow,
  AssetSourcePanel,
  AssetMetadata,
  AssetWorkspace,
  AssetCodeToolbar,
  AssetDetails,
  FormActions,
  IconTextButton,
  IconOnlyButton,
  TableScroll,
  DataTable,
  QuestionText,
  QuestionDetailBlock,
  QuestionDetailLabel,
  QuestionDetailText,
  QuestionListFooter,
  QuestionIdLine,
  QuestionMetaLine,
  SmallIconButton,
  SmallMuted,
  AuthorBadge,
  GoodQuestionBadge,
  StatusBadge,
  VisibilityBadge,
  RowActions,
  EmptyLine,
  ApiGuideCard,
  ApiGuideGrid,
  ApiGuideIntro,
  ApiGuidePanel,
  ApiStepList,
  ApiKeyField,
  ApiCodeBlock,
  CodeHeader,
  DuplicateActionRow,
  DuplicateWarningPanel,
  DuplicateWarningText,
  DuplicateScanPanel,
  DuplicateScanToolbar,
  DuplicateScanSummary,
  DuplicateReviewNavigation,
  DuplicateHistoryButton,
  DuplicateScanProgress,
  DuplicateScanProgressBar,
  DuplicatePairList,
  DuplicatePairCard,
  DuplicatePairHeader,
  DuplicateScore,
  DuplicatePairGrid,
  DuplicateQuestionCard,
  DuplicateQuestionHeader,
  DuplicateDeleteButton,
  BulkImportGrid,
  BulkImportPanel,
  BulkImportToolbar,
  BulkDraftToolbar,
  BulkImportSource,
  BulkWordDropZone,
  BulkWordProgress,
  BulkWordProgressBar,
  BulkWordProgressFooter,
  BulkWordProgressHeader,
  BulkWordProgressMeta,
  BulkWordProgressPanel,
  BulkWordProgressTitle,
  HiddenFileInput,
  BulkQuestionList,
  BulkQuestionCard,
  BulkQuestionHeader,
  BulkQuestionMeta,
  BulkQuestionBody,
  BulkQuestionFlags,
  BulkFlag,
  BulkProgress,
  BulkModalBackdrop,
  BulkModal,
  BulkModalBody,
  BulkModalFooter,
  BulkActionBar,
  BulkSelectionSummary,
  BulkSelectionControls,
  BulkActionGroup,
  BulkActionField,
  BulkDangerAction,
  CheckboxCell,
  QuestionSelectionLabel,
  AssetCodePreviewPane,
  AssetCodePreviewStatus,
  AssetCodePreviewFrame,
} from "./QuestionBankAdminPage.styles";

const QUESTION_SOLUTION_JOB_POLL_INTERVAL_MS =
  process.env.NODE_ENV === "test" ? 0 : 1200;
const QUESTION_SOLUTION_JOB_TERMINAL_STATUSES = new Set(["success", "error"]);
const DUPLICATE_SCAN_POLL_INTERVAL_MS = process.env.NODE_ENV === "test" ? 0 : 1000;
const duplicateScanPhaseLabels = {
  queued: "等待開始掃描",
  reading: "讀取題目",
  comparing: "比對重複題目",
  saving: "整理掃描結果",
  done: "掃描完成",
};

export const waitForQuestionSolutionJob = async ({
  initialJob,
  getAiProcessJob,
  onProgress,
  isCurrent = () => true,
}) => {
  let job = initialJob;
  const startedAt = Date.now();
  let polls = 0;
  let networkFailures = 0;
  if (!isCurrent()) return null;
  onProgress?.(job);

  while (!QUESTION_SOLUTION_JOB_TERMINAL_STATUSES.has(job.status)) {
    if (!isCurrent()) return null;
    if (polls >= 250 || Date.now() - startedAt >= 10 * 60 * 1000) {
      throw new Error("詳解工作等待逾時，請稍後重新產生詳解。");
    }
    await new Promise((resolve) =>
      setTimeout(resolve, QUESTION_SOLUTION_JOB_POLL_INTERVAL_MS),
    );
    if (!isCurrent()) return null;
    polls += 1;
    const result = await getAiProcessJob(job.id);
    if (!isCurrent()) return null;
    if (Date.now() - startedAt >= 10 * 60 * 1000) throw new Error("詳解工作等待逾時，請稍後重新產生詳解。");
    if (!result?.success || !result.job) {
      if (Number(result?.status || 0) === 0) {
        networkFailures += 1;
        if (networkFailures >= 10) throw new Error("詳解工作連線持續失敗，請檢查網路後重新產生詳解。");
        continue;
      }
      throw new Error(result?.error || "詳解工作查詢失敗，請稍後再試。");
    }
    networkFailures = 0;
    job = result.job;
    onProgress?.(job);
  }

  if (job.status !== "success" || !String(job.text || "").trim()) {
    throw new Error(job.error || "詳解工作沒有回傳內容。");
  }
  return job;
};

const questionTypes = [
  { value: "choice", label: "選擇題" },
  { value: "fill", label: "填充題" },
  { value: "calculation", label: "計算題" },
  { value: "proof", label: "證明題" },
  { value: "application", label: "應用題" },
  { value: "short_answer", label: "簡答題" },
  { value: "essay", label: "申論題" },
];

const questionStatuses = [
  { value: "draft", label: "草稿" },
  { value: "published", label: "已發布" },
  { value: "archived", label: "封存" },
];

const questionVisibilities = [
  { value: "public", label: "公開題目" },
  { value: "private", label: "私有題目" },
];

const questionGoodStarFilters = [
  { value: "", label: "無篩選" },
  { value: "true", label: "好題星星" },
];

const choiceOptionLabels = ["A", "B", "C", "D", "E"];

const createEmptyChoiceOptions = () =>
  choiceOptionLabels.map((label) => ({ label, text: "" }));

const questionAnswerStates = [
  { value: "", label: "全部" },
  { value: "missing_answer", label: "缺答案" },
  { value: "missing_solution", label: "缺詳解" },
  { value: "missing_any", label: "缺任一" },
];

const assetRoles = [
  { value: "prompt", label: "題目" },
  { value: "answer", label: "答案" },
  { value: "solution", label: "詳解" },
];

const assetLanguages = [
  { value: "", label: "未指定" },
  { value: "python", label: "Python" },
  { value: "svg", label: "SVG" },
  { value: "tikz", label: "TikZ" },
  { value: "latex", label: "LaTeX" },
  { value: "manim", label: "Manim" },
  { value: "mermaid", label: "Mermaid" },
  { value: "javascript", label: "JavaScript" },
  { value: "other", label: "其他" },
];

const getMarkdownTextareaStyle = (value, minRows = 4) => {
  const normalizedValue = String(value || "").replace(/\r\n?/g, "\n");
  const lineCount = normalizedValue.split("\n").length;
  const visualRows = normalizedValue
    .split("\n")
    .reduce(
      (total, line) => total + Math.max(1, Math.ceil(line.length / 74)),
      0,
    );
  const rows = Math.max(minRows, lineCount, visualRows);

  return { minHeight: `${rows * 22 + 28}px` };
};

const emptyGradeForm = {
  name: "",
  order_index: 0,
  is_active: true,
};

const emptyUnitForm = {
  grade_id: "",
  parent_id: "",
  name: "",
  order_index: 0,
  is_active: true,
};

const emptySourceForm = {
  name: "",
  order_index: 0,
  is_active: true,
};

const emptyQuestionForm = {
  ...familyFormValues(),
  grade_id: "",
  new_grade_name: "",
  unit_id: "",
  new_unit_name: "",
  type: "calculation",
  difficulty: "U",
  category: "",
  prompt_md: "",
  choice_options: createEmptyChoiceOptions(),
  answer_md: "",
  solution_md: "",
  review_note: "",
  is_good_question: false,
  status: "published",
  visibility: "private",
  thinking: "",
  author_label: "",
  question_source: "",
  assets: [],
};

const emptyBulkImportScope = {
  grade_id: "",
  unit_id: "",
  type: "calculation",
  difficulty: "U",
  category: "",
  is_good_question: false,
  status: "published",
  visibility: "private",
  thinking: "",
  author_label: "",
  question_source: "",
};

const bulkSplitModeOptions = [
  { value: "auto", label: "自動辨識" },
  { value: "markers", label: "題號 / 問題" },
  { value: "separators", label: "分隔線" },
  { value: "blank-lines", label: "空行" },
  { value: "single", label: "整份一題" },
];

const filterNoneValue = "__none__";
const filterAllValue = "__all__";
const newGradeValue = "__new_grade__";
const newUnitValue = "__new_unit__";
const questionPageSize = 30;

const emptyFilters = {
  family_id: "",
  search: "",
  grade_id: filterNoneValue,
  unit_id: filterNoneValue,
  type: "",
  difficulty: "",
  category: "",
  status: "",
  visibility: "",
  is_good_question: "",
  answer_state: "",
  question_source: "",
};

const toInt = (value, fallback = 0) => {
  const number = Number.parseInt(value, 10);
  return Number.isNaN(number) ? fallback : number;
};

const toQuestionFilterParam = filterParam;

const toSelectValue = (value) =>
  value === undefined || value === null ? "" : String(value);

const normalizeSearchText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeQuestionSourceValue = (value) => String(value || "").trim();

const normalizeQuestionSourceOptions = (data) => {
  const values = Array.isArray(data) ? data : data?.results || [];
  return Array.from(new Set(values.map(normalizeQuestionSourceValue).filter(Boolean)));
};

const getRecordId = (record) => toSelectValue(record?.id || record?.uuid);

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuidSearch = (value) => uuidPattern.test(String(value || "").trim());

const getUnitGradeId = (unit) =>
  toSelectValue(unit?.grade?.id || unit?.grade_id || unit?.grade);

const getUnitParentId = (unit) =>
  toSelectValue(unit?.parent?.id || unit?.parent_id || unit?.parent);

const getQuestionGradeId = (question) =>
  toSelectValue(question?.grade?.id || question?.grade_id || question?.grade);

const getQuestionUnitId = (question) =>
  toSelectValue(question?.unit?.id || question?.unit_id || question?.unit);

const isDataImageUrl = (value) =>
  /^data:image\//i.test(String(value || "").trim());

const getPersistableAssetUrl = (asset) => {
  const url = String(asset?.url || "").trim();
  return isDataImageUrl(url) ? "" : url;
};

const getAssetDisplayImageUrl = (asset) =>
  String(asset?._preview_url || asset?._previewUrl || asset?.url || "").trim();

const hasAssetContent = (asset) =>
  Boolean(
    getPersistableAssetUrl(asset) ||
    (asset?.storage_key || "").trim() ||
    (asset?.source_code || "").trim(),
  );

const hasPromptAsset = (form) =>
  form.assets.some(
    (asset) => asset.role === "prompt" && hasAssetContent(asset),
  );

const normalizeChoiceLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/[Ａ-Ｅ]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xfee0),
    )
    .toUpperCase();

const splitPromptChoiceOptions = (value) => {
  const optionMap = Object.fromEntries(
    createEmptyChoiceOptions().map((option) => [option.label, ""]),
  );
  const stemLines = [];
  let activeOptionLabel = "";

  String(value || "")
    .split(/\r?\n/)
    .forEach((line) => {
      const match = line.match(/^\s*[（(]\s*([A-EＡ-Ｅ])\s*[）)]\s*(.*)$/i);
      const label = normalizeChoiceLabel(match?.[1]);

      if (match && Object.prototype.hasOwnProperty.call(optionMap, label)) {
        activeOptionLabel = label;
        optionMap[label] = [optionMap[label], match[2].trim()]
          .filter(Boolean)
          .join("\n");
      } else if (activeOptionLabel) {
        optionMap[activeOptionLabel] += `\n${line}`;
      } else {
        stemLines.push(line);
      }
    });

  return {
    prompt_md: stemLines.join("\n").trim(),
    choice_options: choiceOptionLabels.map((label) => ({
      label,
      text: optionMap[label].trim(),
    })),
  };
};

const buildChoiceOptionLines = (options = []) =>
  options
    .map((option) => ({
      label: normalizeChoiceLabel(option.label),
      text: String(option.text || "").trim(),
    }))
    .filter((option) => option.label && option.text)
    .map((option) => `(${option.label}) ${option.text}`);

const getQuestionPrompt = (form) => {
  const prompt = form.prompt_md.trim();
  if (form.type !== "choice") return prompt;

  const optionLines = buildChoiceOptionLines(form.choice_options);
  return [prompt, ...optionLines].filter(Boolean).join("\n");
};

const getSolutionInputSignature = (form) => JSON.stringify([
  getQuestionPrompt(form), form.answer_md, form.solution_md, form.grade_id, form.unit_id,
  (form.assets || []).filter((asset) => asset.role === "prompt").map(getQuestionPromptImageSource),
]);

const hasQuestionContent = (form) =>
  Boolean(getQuestionPrompt(form) || hasPromptAsset(form));

const getQuestionMissingFields = (form) =>
  [
    !form.grade_id.trim() ? "年級" : "",
    form.grade_id === newGradeValue && !form.new_grade_name.trim()
      ? "新年級名稱"
      : "",
    !form.unit_id.trim() ? "單元" : "",
    (form.unit_id === newUnitValue || form.grade_id === newGradeValue) &&
      !form.new_unit_name.trim()
      ? "新單元名稱"
      : "",
    !hasQuestionContent(form) ? "題目文字或題目圖片" : "",
    form.type === "choice" &&
      buildChoiceOptionLines(form.choice_options).length < 2
      ? "至少兩個選項"
      : "",
  ].filter(Boolean);

const shortId = (id) => {
  const value = toSelectValue(id);
  return value ? `${value.slice(0, 8)}...${value.slice(-6)}` : "";
};

const splitThinkingInput = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatThinking = (value) => splitThinkingInput(value).join(", ");

const formatQuestionAuthor = (question) => {
  const label = question.created_by_label || question.created_by_key_name;
  return label || "未標記";
};

const getVisibilityLabel = (visibility) =>
  visibility === "private" ? "私有題目" : "公開題目";

const getStatusLabel = (status) =>
  questionStatuses.find((item) => item.value === status)?.label || status || "";

const getStatusIcon = (status) => {
  if (status === "published") return <FaCheck />;
  if (status === "archived") return <FaArchive />;
  return <FaRegEdit />;
};

const hasQuestionAnswer = (question) =>
  Boolean(String(question.answer_md || "").trim());

const hasQuestionSolution = (question) =>
  Boolean(String(question.solution_md || "").trim());

const questionMatchesAnswerState = (question, answerState) => {
  if (Array.isArray(answerState)) return !answerState.length || answerState.some((state) => questionMatchesAnswerState(question, state));
  if (!answerState) return true;

  const hasAnswer = hasQuestionAnswer(question);
  const hasSolution = hasQuestionSolution(question);

  if (answerState === "missing_answer") return !hasAnswer;
  if (answerState === "missing_solution") return !hasSolution;
  if (answerState === "missing_any") return !hasAnswer || !hasSolution;

  return true;
};

const questionMatchesLocalFilters = (question, filters) => {
  const keyword = normalizeSearchText(filters.search);
  const matchesSearch =
    !keyword ||
    [
      getRecordId(question),
      question.prompt_md,
      question.answer_md,
      question.solution_md,
      formatThinking(question.thinking),
      question.question_source,
      question.grade?.name,
      question.unit?.name,
      question.family?.name,
    ]
      .map(normalizeSearchText)
      .some((value) => value.includes(keyword));

  return (
    matchesSearch &&
    filterMatches(filters.family_id, question.family?.id || "unassigned") &&
    filterMatches(filters.grade_id, getQuestionGradeId(question)) &&
    filterMatches(filters.unit_id, getQuestionUnitId(question)) &&
    filterMatches(filters.type, question.type) &&
    filterMatches(filters.category, question.category || "unassigned") &&
    filterMatches(filters.difficulty, question.difficulty) &&
    filterMatches(filters.status, question.status) &&
    filterMatches(filters.visibility, question.visibility) &&
    filterMatches(filters.is_good_question, String(Boolean(question.is_good_question))) &&
    questionMatchesAnswerState(question, filters.answer_state) &&
    filterMatches(filters.question_source, normalizeQuestionSourceValue(question.question_source))
  );
};

const toQuestionPayload = (form) => {
  const assets = form.assets
    .filter(
      (asset) => hasAssetContent(asset) || (asset.marking_guide || "").trim(),
    )
    .map((asset) => {
      const sourceCode = (asset.source_code || "").trim();
      const useSourceCode = Boolean(sourceCode);
      return {
        role: asset.role,
        url: useSourceCode ? "" : getPersistableAssetUrl(asset),
        storage_key: useSourceCode ? "" : (asset.storage_key || "").trim(),
        alt_text: asset.alt_text.trim(),
        mime_type: useSourceCode ? "" : (asset.mime_type || "").trim(),
        source_language: useSourceCode
          ? (asset.source_language || "python").trim()
          : "",
        source_code: sourceCode,
        marking_guide: (asset.marking_guide || "").trim(),
        sort_order: toInt(asset.sort_order, 0),
      };
    });

  return {
    grade_id: form.grade_id.trim(),
    ...familyPayload(form),
    unit_id: form.unit_id.trim(),
    type: form.type,
    difficulty: form.difficulty,
    category: form.category || "",
    prompt_md: getQuestionPrompt(form),
    answer_md: form.answer_md,
    solution_md: form.solution_md,
    review_note: form.review_note || "",
    is_good_question: Boolean(form.is_good_question),
    status: form.status,
    visibility: form.visibility,
    thinking: splitThinkingInput(form.thinking),
    author_label: form.author_label.trim(),
    question_source: normalizeQuestionSourceValue(form.question_source),
    assets,
  };
};

const getFirstErrorValue = (value) => {
  if (Array.isArray(value)) return value[0] || "";
  return value || "";
};

const getDuplicateError = (error) => {
  const duplicateQuestionId = getFirstErrorValue(
    error?.data?.duplicate_question_id,
  );

  if (!duplicateQuestionId) return null;

  return {
    duplicateQuestionId: String(duplicateQuestionId),
    message:
      String(getFirstErrorValue(error?.data?.prompt_md) || "").trim() ||
      "偵測到可能重複題目。",
  };
};

const getQuestionDuplicateSignature = (form) =>
  JSON.stringify({
    grade_id: form.grade_id.trim(),
    unit_id: form.unit_id.trim(),
    prompt_md: getQuestionPrompt(form).trim(),
  });

const getQuestionForm = (question) => {
  const type = question.type || "calculation";
  const choiceParts =
    type === "choice"
      ? splitPromptChoiceOptions(question.prompt_md || "")
      : {
        prompt_md: question.prompt_md || "",
        choice_options: createEmptyChoiceOptions(),
      };

  return {
    grade_id: getQuestionGradeId(question),
    ...familyFormValues(question),
    new_grade_name: "",
    unit_id: getQuestionUnitId(question),
    new_unit_name: "",
    type,
    difficulty: question.difficulty || "U",
    category: question.category || "",
    prompt_md: choiceParts.prompt_md,
    choice_options: choiceParts.choice_options,
    answer_md: question.answer_md || "",
    solution_md: question.solution_md || "",
    review_note: question.review_note || "",
    is_good_question: Boolean(question.is_good_question),
    status: question.status || "draft",
    visibility: question.visibility || "public",
    thinking: formatThinking(question.thinking),
    author_label: question.created_by_label || "",
    question_source: question.question_source || "",
    assets: (question.assets || []).map((asset) => ({
      role: asset.role || "prompt",
      url: asset.url || "",
      storage_key: asset.storage_key || "",
      alt_text: asset.alt_text || "",
      mime_type: asset.mime_type || "",
      source_language:
        asset.source_language || (asset.source_code ? "python" : ""),
      source_code: asset.source_code || "",
      marking_guide: asset.marking_guide || "",
      sort_order: asset.sort_order || 0,
      preview_status: asset.preview_status || "idle",
      preview_message: asset.preview_message || "",
      _preview_url: asset._preview_url || asset._previewUrl || "",
      _preview_source_code: asset._preview_source_code || "",
    })),
  };
};

const explicitQuestionStartPattern =
  /^\s*(?:#{1,6}\s*)?(?:[*_]{1,3})?\s*(?:(?:第\s*)?([0-9０-９]+|[零〇一二三四五六七八九十兩]+)\s*題|(?:問題|題目|Q(?:uestion)?)\s*([0-9０-９]+|[零〇一二三四五六七八九十兩]+))(?:\s*(?:題|[.．、:：)]))?\s*(?:[*_]{1,3})?\s*(.*)$/i;

const numberedQuestionStartPattern =
  /^\s*(?:#{1,6}\s*)?(?:[*_]{1,3})?\s*([0-9０-９]+|[零〇一二三四五六七八九十兩]+)\s*(?:[.．、)]\s+)(.*)$/;

const questionLikeStartPattern =
  /[？?]|下列|若|已知|求|計算|化簡|判斷|選出|何者|多少|設|如圖|試(?!題)/;

const bulkQuestionSeparatorPattern =
  /\n\s*(?:-{3,}|={3,}|_{3,}|[─━—]{3,})\s*\n/g;

const bulkQuestionSeparatorLinePattern =
  /^\s*(?:-{3,}|={3,}|_{3,}|[─━—]{3,})\s*$/m;

const normalizeBulkImportText = (text) =>
  String(text || "")
    .replace(/\r\n?/g, "\n")
    .trim();

const bulkOcrPageMarkerPattern = /^\s*---\s*Page\s+\d+\s*---\s*$/i;
const isBulkOcrPageIntro = (line) => {
  const value = String(line || "").trim();
  return !value || bulkQuestionSeparatorLinePattern.test(value) ||
    /^說明\s*[：:]/.test(value) ||
    /^[一二三四五六七八九十]+\s*[、.．]/.test(value) ||
    /(?:學年度|學期).*(?:考|試)|(?:期中|期末|段考|小考)/.test(value) ||
    /^(?:姓名|班級|座號|日期)\s*[：:]|^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/.test(value) ||
    /^頁碼\s*[：:]|^\d+\s*$/.test(value);
};

export const formatMathQuestionTags = (ids) =>
  (ids || [])
    .filter(Boolean)
    .map((id) => `<math-question uuid="${id}"></math-question>`)
    .join("\n\n");

export const parseAiSolutionText = (value) => {
  const text = String(value || "")
    .trim()
    .replace(/^```(?:markdown|md)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();
  const sectionPattern =
    /(?:^|\n)\s*(?:#{1,6}\s*)?(?:\*\*)?(題目|解題思路|詳解|解題過程|計算過程|過程|解題步驟|計算步驟|步驟|檢查|答案)(?:\*\*)?\s*[：:](?:\*\*)?\s*/g;
  const matches = [...text.matchAll(sectionPattern)];
  const sections = {};

  matches.forEach((match, index) => {
    const label = match[1];
    const start = (match.index || 0) + match[0].length;
    const end = matches[index + 1]?.index ?? text.length;
    sections[label] = text.slice(start, end).trim();
  });

  const solutionParts = [];
  if (sections.解題思路) {
    solutionParts.push(`**解題思路**\n\n${sections.解題思路}`);
  }
  const detailedSolution =
    sections.詳解 ||
    sections.解題過程 ||
    sections.計算過程 ||
    sections.過程 ||
    sections.解題步驟 ||
    sections.計算步驟 ||
    sections.步驟;
  if (detailedSolution) solutionParts.push(detailedSolution);
  if (sections.檢查) solutionParts.push(`**檢查**\n\n${sections.檢查}`);

  return {
    solution_md: solutionParts.join("\n\n").trim() || text,
    answer_md: sections.答案 || "",
  };
};

const chineseQuestionDigitMap = {
  零: 0,
  〇: 0,
  一: 1,
  二: 2,
  兩: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

const normalizeChineseQuestionNumber = (value) => {
  const text = String(value || "").trim();
  if (!/^[零〇一二三四五六七八九十兩]+$/.test(text)) return "";

  if (text === "十") return "10";
  if (text.includes("十")) {
    const [tensText, onesText = ""] = text.split("十");
    const tens = tensText ? chineseQuestionDigitMap[tensText] : 1;
    const ones = onesText ? chineseQuestionDigitMap[onesText] : 0;
    if (!Number.isFinite(tens) || !Number.isFinite(ones)) return "";
    return String(tens * 10 + ones);
  }

  const digit = chineseQuestionDigitMap[text];
  return Number.isFinite(digit) ? String(digit) : "";
};

const normalizeBulkQuestionNumber = (value, fallback) =>
  normalizeChineseQuestionNumber(value) ||
  String(value || fallback || "").replace(/[０-９]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0),
  );

const matchBulkQuestionStart = (value) => {
  const line = String(value || "");
  const explicitMatch = line.match(explicitQuestionStartPattern);
  if (explicitMatch) {
    return {
      number: explicitMatch[1] || explicitMatch[2],
      body: (explicitMatch[3] || "").trim(),
      explicit: true,
    };
  }

  const numberedMatch = line.match(numberedQuestionStartPattern);
  if (numberedMatch) {
    return {
      number: numberedMatch[1],
      body: (numberedMatch[2] || "").trim(),
      explicit: false,
    };
  }

  return null;
};

const joinBulkQuestionBodyLines = (firstBody, restLines) =>
  [firstBody, ...restLines].join("\n").trim();

const stripQuestionNumber = (value) => {
  const raw = String(value || "").trim();
  const [firstLine = "", ...restLines] = raw.split("\n");
  const match = matchBulkQuestionStart(firstLine);
  if (!match) return raw;
  return joinBulkQuestionBodyLines(match.body, restLines);
};

const createBulkQuestionBlock = (
  raw,
  fallbackNumber,
  { allowPlainText = false } = {},
) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return null;

  const [firstLine = "", ...restLines] = trimmed.split("\n");
  const startMatch = matchBulkQuestionStart(firstLine);
  const body = startMatch
    ? joinBulkQuestionBodyLines(startMatch.body, restLines)
    : trimmed;
  const looksLikeQuestion =
    Boolean(startMatch) ||
    /(?:題目|答案|答|詳解|解析|解法|過程|步驟)\s*[：:]/.test(body) ||
    questionLikeStartPattern.test(body);

  if (!body || (!allowPlainText && !looksLikeQuestion)) return null;

  return {
    raw: body,
    number: normalizeBulkQuestionNumber(startMatch?.number, fallbackNumber),
  };
};

const getBulkQuestionBlocksByMarkers = (normalized) => {
  if (!normalized) return [];

  const blocks = [];
  let current = [];
  let currentNumber = "";
  let pageMarkerIndex = -1;

  const pushCurrent = ({ dropPreamble = false } = {}) => {
    if (dropPreamble && !currentNumber) {
      current = [];
      return;
    }

    const bodyLines = current.filter((line) => !bulkOcrPageMarkerPattern.test(line));
    while (bodyLines.length &&
      (!bodyLines[bodyLines.length - 1].trim() ||
        bulkQuestionSeparatorLinePattern.test(bodyLines[bodyLines.length - 1]))) {
      bodyLines.pop();
    }
    const block = createBulkQuestionBlock(
      bodyLines.join("\n"),
      currentNumber || blocks.length + 1,
    );
    if (block) blocks.push(block);
    current = [];
    currentNumber = "";
    pageMarkerIndex = -1;
  };

  normalized.split("\n").forEach((line) => {
    if (bulkOcrPageMarkerPattern.test(line)) {
      pageMarkerIndex = current.length;
      current.push(line);
      return;
    }
    const match = matchBulkQuestionStart(line);
    const isLikelyNumberedQuestion =
      Boolean(match) &&
      questionLikeStartPattern.test(match.body || "") &&
      current.join("\n").trim().length > 20;

    if (match && (match.explicit || isLikelyNumberedQuestion)) {
      if (pageMarkerIndex >= 0 && current.slice(pageMarkerIndex + 1).every(isBulkOcrPageIntro)) {
        current = current.slice(0, pageMarkerIndex);
      }
      pushCurrent({ dropPreamble: match.explicit && (blocks.length === 0 || pageMarkerIndex >= 0) });
      currentNumber = normalizeBulkQuestionNumber(
        match.number,
        blocks.length + 1,
      );
      current = match.body ? [match.body] : [];
      return;
    }

    current.push(line);
  });
  pushCurrent();

  return blocks;
};

const getBulkQuestionBlocksBySeparators = (normalized) =>
  normalized
    .split(bulkQuestionSeparatorPattern)
    .map((chunk, index) =>
      createBulkQuestionBlock(chunk, index + 1, { allowPlainText: true }),
    )
    .filter(Boolean);

const getBulkQuestionBlocksByBlankLines = (normalized) =>
  normalized
    .split(/\n{2,}/g)
    .map((chunk, index) => createBulkQuestionBlock(chunk, index + 1))
    .filter(Boolean);

const getSingleBulkQuestionBlock = (normalized) => {
  const block = createBulkQuestionBlock(normalized, 1);
  return block ? [block] : [];
};

const getBulkQuestionBlocks = (text, splitMode = "auto") => {
  const normalized = normalizeBulkImportText(text);
  if (!normalized) return [];

  if (splitMode === "markers") {
    return getBulkQuestionBlocksByMarkers(normalized);
  }

  if (splitMode === "separators") {
    return getBulkQuestionBlocksBySeparators(normalized);
  }

  if (splitMode === "blank-lines") {
    return getBulkQuestionBlocksByBlankLines(normalized);
  }

  if (splitMode === "single") {
    return getSingleBulkQuestionBlock(normalized);
  }

  const markerBlocks = getBulkQuestionBlocksByMarkers(normalized);
  // OCR pages can contain several numbered questions between separators.
  if (markerBlocks.length > 1 &&
    normalized.split("\n").some((line) => bulkOcrPageMarkerPattern.test(line))) {
    return markerBlocks;
  }

  // A standalone separator is an explicit user instruction and should win over
  // heuristic marker detection. This also keeps plain declarative stems that do
  // not contain a question mark or one of the recognized question keywords.
  if (bulkQuestionSeparatorLinePattern.test(normalized)) {
    return getBulkQuestionBlocksBySeparators(normalized);
  }

  if (markerBlocks.length > 1) return markerBlocks;

  const separatorBlocks = getBulkQuestionBlocksBySeparators(normalized);
  if (separatorBlocks.length > 1) return separatorBlocks;

  const blankLineBlocks = getBulkQuestionBlocksByBlankLines(normalized);
  if (blankLineBlocks.length > 1) return blankLineBlocks;

  return markerBlocks.length
    ? markerBlocks
    : getSingleBulkQuestionBlock(normalized);
};

const isWordDocumentFile = (value) =>
  Boolean(
    value?.name?.toLowerCase().endsWith(".docx") ||
    value?.type ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );

const appendBulkWordText = (lines, value) => {
  const text = String(value || "").trim();
  if (text) lines.push(text);
};

const bulkWordDocumentToText = (document) => {
  const lines = [];
  appendBulkWordText(lines, document?.title);

  (document?.unassigned_content || []).forEach((block) => {
    appendBulkWordText(lines, block?.text);
  });

  (document?.sections || []).forEach((section) => {
    appendBulkWordText(lines, section?.title);
    (section?.instructions || []).forEach((instruction) => {
      appendBulkWordText(lines, instruction?.text || instruction);
    });

    (section?.questions || []).forEach((question, index) => {
      const number = question?.number || String(index + 1);
      lines.push(`第 ${number} 題`);

      (question?.shared_context || []).forEach((block) => {
        appendBulkWordText(lines, block?.text);
      });

      lines.push("題目：");
      appendBulkWordText(lines, question?.stem);

      (question?.options || []).forEach((option) => {
        const label = option?.label ? `(${option.label}) ` : "";
        appendBulkWordText(lines, `${label}${option?.text || ""}`);
      });

      if (question?.answer) {
        lines.push("答案：");
        appendBulkWordText(lines, question.answer);
      }

      if (question?.solution?.length) {
        lines.push("詳解：");
        question.solution.forEach((step) => appendBulkWordText(lines, step));
      }

      lines.push("");
    });
  });

  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const bulkSectionLabelPattern =
  /(?:^|\n)\s*(?:#{1,6}\s*)?(?:\*\*)?(題目|答案|答|詳解|解析|解法|解題過程|計算過程|過程|解題步驟|計算步驟|步驟)(?:\*\*)?\s*[：:](?:\*\*)?\s*/gi;

const bulkHandwritingLabelPattern =
  /(?:^|\n)[\t ]*(?:#{1,6}[\t ]*)?(?:\*\*)?(?:\[|【)(?:橘|橙|藍|紅)(?:色)?筆(?:未分配)?(?:\]|】)(?:\*\*)?[\t ]*[：:]?/g;

const getBulkSectionRole = (label) => {
  if (label === "題目") return "prompt";
  if (label === "答案" || label === "答") return "answer";
  return "solution";
};

const parseBulkQuestionSections = (value) => {
  const text = String(value || "").trim();
  const matches = [
    ...[...text.matchAll(bulkSectionLabelPattern)].map((match) => ({
      match,
      handwriting: false,
    })),
    ...[...text.matchAll(bulkHandwritingLabelPattern)].map((match) => ({
      match,
      handwriting: true,
    })),
  ].sort((a, b) => a.match.index - b.match.index);
  if (!matches.length) {
    return { prompt: text, answer: "", solution: "" };
  }

  const sections = { prompt: [], answer: [], solution: [] };
  const preamble = text.slice(0, matches[0].match.index).trim();
  if (preamble) sections.prompt.push(preamble);

  let role = "prompt";
  matches.forEach(({ match, handwriting }, index) => {
    // A pen label starts a solution unless an explicit answer label is active.
    // Keep pen labels so different handwritten methods remain distinguishable.
    role = handwriting
      ? role === "answer" ? "answer" : "solution"
      : getBulkSectionRole(match[1]);
    const start = (match.index || 0) + (handwriting ? 0 : match[0].length);
    const end = matches[index + 1]?.match.index ?? text.length;
    const content = text.slice(start, end).trim();
    if (content) sections[role].push(content);
  });

  return {
    prompt: sections.prompt.join("\n\n").trim(),
    answer: sections.answer.join("\n\n").trim(),
    solution: sections.solution.join("\n\n").trim(),
  };
};

const figureCodeBlockPattern =
  /(?:^|\n)\s*(?:(?:圖形|圖片)\s*)?程式碼(?:[（(]\s*([^)）\n]+?)\s*[）)])?\s*[：:]?\s*\n```(?:python|py)\s*\n([\s\S]*?)```/gi;

const getFigureCodeAssetRole = (label, fallbackRole = "prompt") => {
  const normalizedLabel = String(label || "")
    .trim()
    .toLowerCase();
  if (/題目|問題|prompt|question/.test(normalizedLabel)) return "prompt";
  if (/答案|答|answer|ans/.test(normalizedLabel)) return "answer";
  if (/詳解|解析|解法|solution|explanation/.test(normalizedLabel))
    return "solution";
  return fallbackRole;
};

const figureCodeAssetRoleLabels = {
  prompt: "題目",
  answer: "答案",
  solution: "詳解",
};

const extractFigureCodeAssets = (value, fallbackRole = "prompt") => {
  const originals = extractOriginalFigureAssets(String(value || ""), fallbackRole);
  const text = originals.text;
  const assets = [...originals.assets];
  let cleaned = "";
  let lastIndex = 0;
  let match;

  figureCodeBlockPattern.lastIndex = 0;
  while ((match = figureCodeBlockPattern.exec(text))) {
    const code = String(match[2] || "").trim();
    cleaned += text.slice(lastIndex, match.index);
    lastIndex = figureCodeBlockPattern.lastIndex;
    if (!code) continue;

    const role = getFigureCodeAssetRole(match[1], fallbackRole);
    assets.push({
      role,
      url: "",
      storage_key: "",
      alt_text: `圖形程式碼（${figureCodeAssetRoleLabels[role] || "題目"}）`,
      mime_type: "",
      source_language: "python",
      source_code: code,
      marking_guide: "",
      sort_order: assets.length,
    });
  }

  cleaned += text.slice(lastIndex);
  return {
    text: cleaned.replace(/\n{3,}/g, "\n\n").trim(),
    assets,
  };
};

const detectBulkQuestionFlags = (form) => {
  const joined = [
    form.prompt_md,
    form.answer_md,
    form.solution_md,
    ...(form.assets || []).map((asset) =>
      [
        asset.url,
        asset.storage_key,
        asset.source_code,
        asset.marking_guide,
      ].join("\n"),
    ),
  ].join("\n");
  const flags = [];

  if (/公式\/圖片待辨識|待辨識|圖片待辨識|公式待辨識|附圖待處理|頁待校正/i.test(joined)) {
    flags.push("公式/圖片待辨識");
  }
  if (
    /!\[[^\]]*\]\(|<img\b|\\includegraphics|assets\.sunnytseng\.com/i.test(
      joined,
    )
  ) {
    flags.push("含圖片線索");
  }
  if (/\\\(|\\\[|\$\$?|\\frac|\\sqrt|\\begin\{/.test(joined)) {
    flags.push("含公式");
  }

  return flags;
};

export const parseBulkImportText = (text, scope, splitMode = "auto") => {
  return getBulkQuestionBlocks(text, splitMode).map((blockItem, index) => {
    const block = stripQuestionNumber(blockItem.raw);
    const sections = parseBulkQuestionSections(block);
    const promptParts = extractFigureCodeAssets(sections.prompt, "prompt");
    const answerParts = extractFigureCodeAssets(sections.answer, "answer");
    const solutionParts = extractFigureCodeAssets(
      sections.solution,
      "solution",
    );
    const assets = [
      ...promptParts.assets,
      ...answerParts.assets,
      ...solutionParts.assets,
    ].map((asset, assetIndex) => ({ ...asset, sort_order: assetIndex }));
    const form = {
      ...emptyQuestionForm,
      grade_id: scope.grade_id,
      unit_id: scope.unit_id,
      type: scope.type,
      difficulty: scope.difficulty,
      category: scope.category || "",
      is_good_question: Boolean(scope.is_good_question),
      status: scope.status,
      visibility: scope.visibility,
      thinking: scope.thinking || "",
      author_label: scope.author_label,
      question_source: scope.question_source || "",
      prompt_md: promptParts.text,
      answer_md: answerParts.text,
      solution_md: solutionParts.text,
      assets,
    };

    return {
      localId: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      questionNumber: blockItem.number,
      form,
      selected: true,
      edited: false,
      importStatus: "idle",
      importMessage: "",
      createdId: "",
    };
  });
};

const ensureQuestionGrade = async ({ subject, authToken, form, grades, setGrades }) => {
  if (form.grade_id !== newGradeValue) return form.grade_id;

  const createdGrade = await createGrade({
    subject,
    authToken,
    data: {
      name: form.new_grade_name.trim(),
      order_index: grades.length,
      is_active: true,
    },
  });
  const createdGradeId = getRecordId(createdGrade);
  if (!createdGradeId) throw new Error("年級已新增，但後端未回傳年級 ID。");
  setGrades((current) => [...current, createdGrade]);
  return createdGradeId;
};

const ensureQuestionUnit = async ({
  subject,
  authToken,
  form,
  gradeId,
  unitsInGrade,
  setUnits,
}) => {
  if (form.unit_id !== newUnitValue && form.grade_id !== newGradeValue) {
    return form.unit_id;
  }

  const createdUnit = await createUnit({
    subject,
    authToken,
    data: {
      grade_id: gradeId,
      parent_id: null,
      name: form.new_unit_name.trim(),
      order_index: unitsInGrade.length,
      is_active: true,
    },
  });
  const createdUnitId = getRecordId(createdUnit);
  if (!createdUnitId) throw new Error("單元已新增，但後端未回傳單元 ID。");
  setUnits((current) => [...current, createdUnit]);
  return createdUnitId;
};

const getAssetSourceCode = (assets = []) =>
  assets
    .map((asset) => (asset.source_code || "").trim())
    .filter(Boolean)
    .join("\n\n");

const getPreviewableAssets = (assets = [], role = null) =>
  getPreviewableQuestionAssets(assets, role);

const getAssetRoleLabel = (role) =>
  assetRoles.find((item) => item.value === role)?.label || role || "圖片";

function createSvgAssetEditor({ key, asset, onChange, onClose }) {
  const updateCode = (sourceCode) => onChange({
    source_language: "svg", source_code: sourceCode, url: "", storage_key: "",
    mime_type: "", preview_status: "idle", preview_message: "",
    _preview_url: "", _preview_source_code: "",
  });
  return {
    key, title: `SVG 原始碼 · ${getAssetRoleLabel(asset.role)}`,
    value: asset.source_code || "", defaultVimMode: true, syntaxMode: "svg",
    onChange: updateCode, preview: true, previewType: "custom",
    previewTitle: "SVG 即時預覽", previewSource: asset.source_code || "",
    previewContent: <QuestionAssetEditorPreview asset={asset} inputMode="svg" />,
    onLeaderWrite: (code) => { updateCode(code); onClose(); return true; },
  };
}

function AssetImagePreviews({ assets, compact = false, limit = Infinity }) {
  return (
    <QuestionAssetPreviewStrip
      assets={assets}
      compact={compact}
      limit={limit}
      getRoleLabel={getAssetRoleLabel}
    />
  );
}

function QuestionSourceField({
  id,
  inputId,
  value,
  onChange,
  sourceOptions = [],
  fieldMarker,
}) {
  return (
    <SearchableSelect
      id={inputId || id}
      value={value}
      onChange={onChange}
      options={sourceOptions.map((source) => ({ value: source, label: source }))}
      allowCustom
      aria-label="題目來源"
      placeholder="輸入或選擇題目來源"
      maxLength={255}
      data-question-field={fieldMarker}
      emptyMessage="沒有符合的來源，可直接保留目前文字作為新來源"
    />
  );
}

const staffApiKeyStorageKey = "math_bank_staff_api_key";
const staffAuthorLabelStorageKey = "math_bank_staff_author_label";
const bulkImportDraftKind = "bulk_question_import";
const emptyDuplicateScanFilters = {
  grade_id: "",
  unit_id: "",
  question_source: "",
};

const getDuplicateGroups = (result) => {
  if (Array.isArray(result?.groups)) return result.groups;
  return (result?.results || []).map((pair) => ({
    ...pair,
    group_key: `${pair.question_a.id}-${pair.question_b.id}`,
    questions: [pair.question_a, pair.question_b],
    review_status: "pending",
  }));
};

const duplicateScanErrorMessage = (error) =>
  [502, 503, 504].includes(error?.status)
    ? `重複題目掃描暫時無法完成（${error.status}），可能是伺服器忙碌或處理逾時。請稍後重試。`
    : error.message || "重複題目掃描失敗";

const formatDraftUpdatedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-TW", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function QuestionBankAdminPage({ editRequest, onEditRequestHandled, routeActive = true } = {}) {
  return <SubjectWorkspace
    component={QuestionBankAdminWorkspace}
    workspaceProps={{ editRequest, onEditRequestHandled, routeActive }}
    subjectRequest={editRequest?.subject}
  />;
}

function QuestionBankAdminWorkspace({ subject, subjectLabel, isActive, routeActive, editRequest, onEditRequestHandled }) {
  const { createGrade, createDraftSession, createQuestion, createSource, createUnit, deleteDraftSession, deleteGrade, deleteQuestion, deleteQuestions, deleteSource, deleteUnit, startDuplicateQuestionScan, getDuplicateQuestionScan, getDuplicateQuestionScanResults, getDuplicateQuestionScanGroup, reviewDuplicateQuestions, getQuestion, listDraftSessions, listGrades, listQuestionFamilies, listQuestionSources, listSources, listUnits, updateDraftSession, searchQuestions, updateGrade, updateQuestion, updateQuestions, updateSource, updateUnit } = useMemo(() => Object.fromEntries(
    Object.entries(questionBankApi).map(([name, fn]) => [name, (options = {}) => fn({ ...options, subject })]),
  ), [subject]);
  const {
    authToken,
    previewMatplotlibCode,
    startAiProcessJob,
    getAiProcessJob,
    startAiSolutionFromImagesJob,
    getAiSolutionFromImagesJob,
  } = useAuth();
  const pageShellRef = useRef(null);
  const questionFormTopRef = useRef(null);
  const bulkWordInputRef = useRef(null);
  const handledBulkTransferRef = useRef("");

  const [activeTab, setActiveTab] = useState("question-create");
  const activeTabRef = useRef("question-create");
  const tabScrollPositionsRef = useRef({
    "question-create": { scrollTop: 0 },
  });
  const tabRestoreFramesRef = useRef([]);
  const hasMountedTabScrollRef = useRef(false);

  const isQuestionBankVisible = useCallback(() => {
    const shell = pageShellRef.current;
    return Boolean(shell && !shell.closest("[hidden]"));
  }, []);

  const getWindowScrollTop = useCallback(
    () => document.scrollingElement?.scrollTop ?? window.scrollY ?? 0,
    [],
  );

  const captureTabScrollPosition = useCallback(
    (tab) => {
      const shell = pageShellRef.current;
      const scrollTop = getWindowScrollTop();
      const anchors = shell
        ? Array.from(shell.querySelectorAll("[data-tab-scroll-anchor]"))
        : [];
      const anchor = selectViewportAnchor(anchors);

      tabScrollPositionsRef.current[tab] = {
        scrollTop,
        anchorKey: anchor?.element.dataset.tabScrollAnchor || "",
        anchorOffset: anchor?.rect.top ?? null,
      };
    },
    [getWindowScrollTop],
  );

  const changeActiveTab = useCallback(
    (nextTab, { resetScroll = false } = {}) => {
      const currentTab = activeTabRef.current;
      if (!nextTab || nextTab === currentTab) return;

      if (isQuestionBankVisible() && typeof window !== "undefined") {
        captureTabScrollPosition(currentTab);
      }
      if (resetScroll) {
        tabScrollPositionsRef.current[nextTab] = { scrollTop: 0 };
      }

      activeTabRef.current = nextTab;
      setActiveTab(nextTab);
    },
    [captureTabScrollPosition, isQuestionBankVisible],
  );

  useLayoutEffect(() => {
    activeTabRef.current = activeTab;
    if (!hasMountedTabScrollRef.current) {
      hasMountedTabScrollRef.current = true;
      return undefined;
    }
    if (!isQuestionBankVisible() || typeof window === "undefined") {
      return undefined;
    }

    tabRestoreFramesRef.current.forEach((frame) =>
      window.cancelAnimationFrame(frame),
    );
    tabRestoreFramesRef.current = [];

    const savedPosition = tabScrollPositionsRef.current[activeTab] || {
      scrollTop: 0,
    };
    let stopped = false;
    const restore = () => {
      if (stopped) return;
      let top = savedPosition.scrollTop || 0;
      if (savedPosition.anchorKey) {
        const anchor = Array.from(
          pageShellRef.current?.querySelectorAll("[data-tab-scroll-anchor]") ||
            [],
        ).find(
          (element) =>
            element.dataset.tabScrollAnchor === savedPosition.anchorKey,
        );
        if (anchor) {
          const currentTop = getWindowScrollTop();
          top = calculateAnchoredScrollTop({
            currentScrollTop: currentTop,
            currentAnchorTop: anchor.getBoundingClientRect().top,
            savedAnchorOffset: savedPosition.anchorOffset,
          });
        }
      }
      window.scrollTo({ top, left: 0, behavior: "auto" });
    };
    const stopRestoring = () => {
      stopped = true;
    };
    restore();

    if (typeof window.requestAnimationFrame === "function") {
      const firstFrame = window.requestAnimationFrame(() => {
        restore();
        const secondFrame = window.requestAnimationFrame(restore);
        tabRestoreFramesRef.current.push(secondFrame);
      });
      tabRestoreFramesRef.current.push(firstFrame);
    }

    const shell = pageShellRef.current;
    const observer =
      savedPosition.anchorKey && shell && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(restore)
        : null;
    observer?.observe(shell);
    shell?.addEventListener("load", restore, true);
    window.addEventListener("wheel", stopRestoring, { passive: true });
    window.addEventListener("touchstart", stopRestoring, { passive: true });
    window.addEventListener("pointerdown", stopRestoring, { passive: true });
    window.addEventListener("keydown", stopRestoring);
    const stopTimer = window.setTimeout(stopRestoring, 3000);

    return () => {
      stopped = true;
      tabRestoreFramesRef.current.forEach((frame) =>
        window.cancelAnimationFrame(frame),
      );
      tabRestoreFramesRef.current = [];
      window.clearTimeout(stopTimer);
      observer?.disconnect();
      shell?.removeEventListener("load", restore, true);
      window.removeEventListener("wheel", stopRestoring);
      window.removeEventListener("touchstart", stopRestoring);
      window.removeEventListener("pointerdown", stopRestoring);
      window.removeEventListener("keydown", stopRestoring);
    };
  }, [activeTab, getWindowScrollTop, isQuestionBankVisible]);

  const [grades, setGrades] = useState([]);
  const [questionFamilies, setQuestionFamilies] = useState([]);
  const [units, setUnits] = useState([]);
  const [sources, setSources] = useState([]);
  const [questionSources, setQuestionSources] = useState([]);
  const [questionIds, setQuestionIds] = useState([]);
  const [questionsById, setQuestionsById] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [questionSelectionMode, setQuestionSelectionMode] = useState(false);
  const selectAllQuestionsRef = useRef(null);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const questionPreviewTimerRef = useRef(null);

  useEffect(() => () => window.clearTimeout(questionPreviewTimerRef.current), []);

  const showQuestionPreview = async (question) => {
    setPreviewQuestion(question);
    if (!question.duplicate_preview) return;
    try {
      const fullQuestion = await getQuestion({ authToken, id: question.id });
      setPreviewQuestion((current) => current?.id === question.id ? fullQuestion : current);
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "無法載入完整題目" });
    }
  };

  const queueQuestionPreview = (event, question) => {
    window.clearTimeout(questionPreviewTimerRef.current);
    if (event.detail > 1) return;
    questionPreviewTimerRef.current = window.setTimeout(() => {
      showQuestionPreview(question);
    }, 350);
  };

  const cancelQuestionPreview = () => {
    window.clearTimeout(questionPreviewTimerRef.current);
    setPreviewQuestion(null);
  };

  const [gradeForm, setGradeForm] = useState(emptyGradeForm);
  const [unitForm, setUnitForm] = useState(emptyUnitForm);
  const [sourceForm, setSourceForm] = useState(emptySourceForm);
  const [questionForm, setQuestionForm] = useState(emptyQuestionForm);
  const [expandedMarkdownField, setExpandedMarkdownField] = useState(null);
  const [expandedAssetSourceIndex, setExpandedAssetSourceIndex] =
    useState(null);
  const [expandedBulkMarkdownField, setExpandedBulkMarkdownField] =
    useState(null);
  const [expandedBulkAssetSource, setExpandedBulkAssetSource] = useState(null);
  const [assetPreviewStatus, setAssetPreviewStatus] = useState("idle");
  const [assetPreviewMessage, setAssetPreviewMessage] = useState("");
  const assetPreviewLeaderSubmittedRef = useRef(false);
  const resetAssetPreviewLeaderSubmission = () => {
    assetPreviewLeaderSubmittedRef.current = false;
  };
  const shouldCloseAssetPreviewEditorOnLeaderWrite = () => {
    if (assetPreviewLeaderSubmittedRef.current) {
      assetPreviewLeaderSubmittedRef.current = false;
      return true;
    }
    assetPreviewLeaderSubmittedRef.current = true;
    return false;
  };
  const [duplicateQuestionWarning, setDuplicateQuestionWarning] =
    useState(null);
  const [duplicateScanFilters, setDuplicateScanFilters] = useState(
    emptyDuplicateScanFilters,
  );
  const [duplicateScanSearch, setDuplicateScanSearch] = useState("");
  const [duplicateScanResult, setDuplicateScanResult] = useState(null);
  const [duplicateScanLoading, setDuplicateScanLoading] = useState(false);
  const [duplicateReviewStatus, setDuplicateReviewStatus] = useState("pending");
  const [duplicateReviewSavingKey, setDuplicateReviewSavingKey] = useState(null);
  const duplicateScanVisibleRef = useRef(false);
  const [duplicateScanJob, setDuplicateScanJob] = useState(null);
  const [duplicateScanConnectionLost, setDuplicateScanConnectionLost] = useState(false);
  const [duplicateResultsLoading, setDuplicateResultsLoading] = useState(false);
  const [duplicateGroupLoading, setDuplicateGroupLoading] = useState(null);
  const duplicateResultsGenerationRef = useRef(0);
  const duplicateSearchRef = useRef("");
  const duplicateScanGenerationRef = useRef(0);
  const duplicateScanWaitRef = useRef(null);
  const [staffApiKey, setStaffApiKey] = useState(() => {
    try {
      return window.localStorage.getItem(staffApiKeyStorageKey) || "";
    } catch {
      return "";
    }
  });
  const [staffAuthorLabel, setStaffAuthorLabel] = useState(() => {
    try {
      return window.localStorage.getItem(staffAuthorLabelStorageKey) || "";
    } catch {
      return "";
    }
  });
  const [apiImportScope, setApiImportScope] = useState({
    grade_id: "",
    unit_id: "",
    visibility: "public",
    question_source: "",
    status: "draft",
  });
  const [bulkImportScope, setBulkImportScope] = useState(emptyBulkImportScope);
  const [bulkImportText, setBulkImportText] = useState("");
  const [bulkQuestions, setBulkQuestions] = useState([]);
  const [bulkEditingQuestion, setBulkEditingQuestion] = useState(null);
  const bulkEditingSessionRef = useRef(null);
  const bulkModalRef = useRef(null);
  const [bulkAllowDuplicate, setBulkAllowDuplicate] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [importFamilyAnalysis, setImportFamilyAnalysis] = useState({ batches: [], index: 0, open: false });
  const importAnalysisBatch = importFamilyAnalysis.batches[importFamilyAnalysis.index];
  const [bulkSolutionProgress, setBulkSolutionProgress] = useState({
    running: false,
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
  });
  const [bulkWordParsing, setBulkWordParsing] = useState(false);
  const [bulkWordDragOver, setBulkWordDragOver] = useState(false);
  const [bulkWordMode, setBulkWordMode] = useState("fast");
  const [bulkSplitMode, setBulkSplitMode] = useState("auto");
  const [bulkDraftSessions, setBulkDraftSessions] = useState([]);
  const [selectedBulkDraftId, setSelectedBulkDraftId] = useState("");
  const [bulkDraftLoading, setBulkDraftLoading] = useState(false);
  const [bulkDraftSaving, setBulkDraftSaving] = useState(false);
  const [bulkWordProgress, setBulkWordProgress] = useState({
    percent: 0,
    label: "",
    status: "idle",
    fileName: "",
    elapsedSeconds: 0,
    error: "",
  });

  const [editingGradeId, setEditingGradeId] = useState(null);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [bulkStatusAction, setBulkStatusAction] = useState("published");
  const [bulkVisibilityAction, setBulkVisibilityAction] = useState("public");

  const [loading, setLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [loadingMoreQuestions, setLoadingMoreQuestions] = useState(false);
  const [hasMoreQuestions, setHasMoreQuestions] = useState(false);
  const [questionTotal, setQuestionTotal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [questionAiStatus, setQuestionAiStatus] = useState("idle");
  const [notice, setNotice] = useState(null);
  const questionStateRef = useRef({
    signature: "",
    ids: [],
    byId: {},
    nextCursor: null,
    hasMore: false,
  });
  const questionSearchReturnRef = useRef(null);
  const skipNextQuestionSearchRefreshRef = useRef(false);
  const activeQuestionFilterSignatureRef = useRef("");
  const questionSentinelRef = useRef(null);
  const questionRequestKeyRef = useRef(0);
  const noticeClearTimerRef = useRef(null);
  const questionEditPreviewRequestRef = useRef(0);
  const handledEditRequestRef = useRef(null);
  const solutionScopeRef = useRef(0);
  const questionSolutionTaskRef = useRef(null);
  const bulkSolutionTasksRef = useRef(new Map());
  const solutionLiveRef = useRef(null);
  solutionLiveRef.current = { questionForm, bulkQuestions, editingQuestionId, active: isActive && routeActive };

  useEffect(() => {
    const tasks = bulkSolutionTasksRef.current;
    solutionScopeRef.current += 1;
    questionSolutionTaskRef.current = null;
    tasks.clear();
    setQuestionAiStatus("idle");
    setBulkQuestions((items) => items.map((item) => item.aiStatus === "loading" ? { ...item, aiStatus: "idle", aiMessage: "" } : item));
    setBulkSolutionProgress((progress) => progress.running ? { ...progress, running: false } : progress);
    return () => {
      solutionScopeRef.current += 1;
      questionSolutionTaskRef.current = null;
      tasks.clear();
    };
  }, [subject, authToken, isActive, routeActive]);

  useEffect(() => {
    const task = questionSolutionTaskRef.current;
    if (task && (task.editEpoch !== questionEditPreviewRequestRef.current || task.signature !== getSolutionInputSignature(questionForm))) {
      questionSolutionTaskRef.current = null;
      setQuestionAiStatus("idle");
    }
    const invalidIds = [];
    for (const [id, bulkTask] of bulkSolutionTasksRef.current) {
      const item = bulkQuestions.find((candidate) => candidate.localId === id);
      if (!item || bulkTask.signature !== getSolutionInputSignature(item.form)) {
        bulkSolutionTasksRef.current.delete(id);
        invalidIds.push(id);
      }
    }
    if (invalidIds.length) setBulkQuestions((items) => items.map((item) => invalidIds.includes(item.localId) ? { ...item, aiStatus: "idle", aiMessage: "題目已變更，請重新產生詳解。" } : item));
  }, [questionForm, editingQuestionId, bulkQuestions]);

  const questions = useMemo(
    () => questionIds.map((id) => questionsById[id]).filter(Boolean),
    [questionIds, questionsById],
  );

  const displayQuestions = useMemo(
    () =>
      questions.filter((question) =>
        questionMatchesLocalFilters(question, filters),
      ),
    [filters, questions],
  );

  const clearNoticeTimeout = useCallback(() => {
    if (noticeClearTimerRef.current) {
      window.clearTimeout(noticeClearTimerRef.current);
      noticeClearTimerRef.current = null;
    }
  }, []);

  const closeBulkQuestionEditor = useCallback(() => {
    const session = bulkEditingSessionRef.current;
    if (session?.status === "editing") session.status = "discarded";
    bulkEditingSessionRef.current = null;
    setBulkEditingQuestion(null);
    setExpandedBulkMarkdownField(null);
    setExpandedBulkAssetSource(null);
  }, []);

  const openBulkQuestionEditor = (question) => {
    closeBulkQuestionEditor();
    bulkEditingSessionRef.current = {
      question,
      changedFields: new Set(),
      status: "editing",
    };
    setBulkEditingQuestion(question);
  };

  const finishBulkQuestionEditor = useCallback(() => {
    const session = bulkEditingSessionRef.current;
    if (!session || session.status !== "editing") return;
    const { question, changedFields } = session;
    session.status = "saved";
    if (changedFields.size) {
      // Merge only manual edits so background AI results are not overwritten.
      const patch = Object.fromEntries(
        [...changedFields].map((field) => [field, question.form[field]]),
      );
      setBulkQuestions((current) => current.map((item) =>
        item.localId === question.localId ? {
          ...item,
          form: { ...item.form, ...patch },
          edited: true,
          importStatus: item.importStatus === "success" ? "idle" : item.importStatus,
          importMessage: item.importStatus === "success" ? "" : item.importMessage,
          createdId: item.importStatus === "success" ? "" : item.createdId,
        } : item,
      ));
    }
    closeBulkQuestionEditor();
  }, [closeBulkQuestionEditor]);

  useEffect(() => () => {
    const session = bulkEditingSessionRef.current;
    if (session) session.status = "discarded";
    bulkEditingSessionRef.current = null;
  }, []);

  const clearNotice = useCallback(() => {
    clearNoticeTimeout();
    setNotice(null);
  }, [clearNoticeTimeout]);

  const showNotice = useCallback(
    (nextNotice, { autoHide = false } = {}) => {
      clearNoticeTimeout();
      setNotice(nextNotice);

      if (autoHide) {
        noticeClearTimerRef.current = window.setTimeout(() => {
          setNotice((currentNotice) =>
            currentNotice?.tone === nextNotice.tone &&
              currentNotice?.text === nextNotice.text
              ? null
              : currentNotice,
          );
          noticeClearTimerRef.current = null;
        }, 3000);
      }
    },
    [clearNoticeTimeout],
  );

  const rememberQuestionSource = useCallback((value) => {
    const nextSource = normalizeQuestionSourceValue(value);
    if (!nextSource) return;

    setQuestionSources((current) =>
      normalizeQuestionSourceOptions([...current, nextSource]),
    );
  }, []);

  const getFirstMissingQuestionFieldKey = useCallback((form) => {
    if (!form.grade_id.trim()) return "grade";
    if (form.grade_id === newGradeValue && !form.new_grade_name.trim()) {
      return "new-grade";
    }
    if (!form.unit_id.trim()) return "unit";
    if (
      (form.unit_id === newUnitValue || form.grade_id === newGradeValue) &&
      !form.new_unit_name.trim()
    ) {
      return "new-unit";
    }
    if (!hasQuestionContent(form)) return "prompt";
    if (
      form.type === "choice" &&
      buildChoiceOptionLines(form.choice_options).length < 2
    ) {
      return "choices";
    }
    return "";
  }, []);

  const scrollToQuestionFormError = useCallback((fieldKey) => {
    if (typeof window === "undefined") return;

    const scrollTarget = questionFormTopRef.current || pageShellRef.current;
    const focusTarget = fieldKey
      ? pageShellRef.current?.querySelector(`[data-question-field="${fieldKey}"]`)
      : null;
    const run = () => {
      if (scrollTarget?.scrollIntoView) {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (typeof window.scrollTo === "function") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      window.setTimeout(() => {
        focusTarget?.focus?.({ preventScroll: true });
      }, 220);
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(run);
    } else {
      run();
    }
  }, []);

  useEffect(
    () => () => {
      clearNoticeTimeout();
    },
    [clearNoticeTimeout],
  );

  const loadData = useCallback(
    async ({ preserveNotice = false } = {}) => {
      setLoading(true);
      if (!preserveNotice) {
        clearNotice();
      }

      try {
        const [gradeData, unitData, sourceListData, sourceData, familyData] = await Promise.all([
          listGrades({ authToken }),
          listUnits({ authToken }),
          listSources({ authToken }),
          listQuestionSources({ authToken }).catch(() => []),
          listQuestionFamilies({ authToken }),
        ]);

        setGrades(gradeData || []);
        setQuestionFamilies(familyData || []);
        setUnits(unitData || []);
        setSources(sourceListData || []);
        setQuestionSources(normalizeQuestionSourceOptions(sourceData));
      } catch (error) {
        setNotice({ tone: "error", text: error.message || "題庫資料讀取失敗" });
      } finally {
        setLoading(false);
      }
    },
    [authToken, clearNotice, listGrades, listQuestionFamilies, listQuestionSources, listSources, listUnits],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshQuestionFamilies = useCallback(async () => {
    try { setQuestionFamilies(await listQuestionFamilies({ authToken })); }
    catch (error) { setNotice({ tone: "error", text: error.message }); }
  }, [authToken, listQuestionFamilies]);

  const loadBulkDraftSessions = useCallback(async () => {
    if (!authToken) return;
    setBulkDraftLoading(true);
    try {
      const drafts = await listDraftSessions({
        authToken,
        params: { kind: bulkImportDraftKind },
      });
      setBulkDraftSessions(drafts || []);
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "暫存讀取失敗" });
    } finally {
      setBulkDraftLoading(false);
    }
  }, [authToken, listDraftSessions]);

  useEffect(() => {
    if (activeTab === "bulk-import") {
      loadBulkDraftSessions();
    }
  }, [activeTab, loadBulkDraftSessions]);

  const activeQuestionFilters = useMemo(
    () => ({
      family_id: filters.family_id,
      search: normalizeSearchText(filters.search),
      grade_id: toQuestionFilterParam(filters.grade_id),
      unit_id: toQuestionFilterParam(filters.unit_id),
      type: filters.type,
      difficulty: filters.difficulty,
      category: filters.category,
      status: filters.status,
      visibility: filters.visibility,
      is_good_question: filters.is_good_question,
      answer_state: filters.answer_state,
      question_source: filters.question_source,
    }),
    [
      filters.answer_state,
      filters.family_id,
      filters.difficulty,
      filters.category,
      filters.grade_id,
      filters.is_good_question,
      filters.question_source,
      filters.search,
      filters.status,
      filters.type,
      filters.unit_id,
      filters.visibility,
    ],
  );

  const shouldLoadQuestionResults = useMemo(
    () => Boolean(filters.search.trim()) || Object.entries(filters).some(([key, value]) =>
      key !== "search" && (Array.isArray(value) ? value.length > 0 : Boolean(value) && value !== filterNoneValue),
    ),
    [filters],
  );

  const activeQuestionFilterSignature = useMemo(
    () =>
      JSON.stringify({
        auth: Boolean(authToken),
        filters: activeQuestionFilters,
      }),
    [activeQuestionFilters, authToken],
  );
  activeQuestionFilterSignatureRef.current = activeQuestionFilterSignature;

  const applyQuestionState = useCallback((nextState) => {
    questionStateRef.current = nextState;
    setQuestionIds(nextState.ids);
    setQuestionsById(nextState.byId);
    setHasMoreQuestions(nextState.hasMore);
    setQuestionTotal(nextState.total ?? null);
  }, []);

  useEffect(() => subscribeToAssetRelocations((moves) => {
    const rewrite = (value) => rewriteAssetReferences(value, moves);
    setQuestionForm(rewrite);
    setBulkQuestions(rewrite);
    const session = bulkEditingSessionRef.current;
    if (session?.status === "editing") {
      session.question = rewrite(session.question);
      setBulkEditingQuestion(session.question);
    }
    setBulkImportText(rewrite);
    setBulkDraftSessions(rewrite);
    setPreviewQuestion(rewrite);
    applyQuestionState({
      ...questionStateRef.current,
      byId: rewrite(questionStateRef.current.byId),
    });
  }), [applyQuestionState]);

  const handlePreviewAssetsRendered = useCallback(
    (sourceQuestion, nextAssets) => {
      const questionId = getRecordId(sourceQuestion);
      const currentState = questionStateRef.current;
      if (questionId && currentState.byId[questionId]) {
        applyQuestionState({
          ...currentState,
          byId: {
            ...currentState.byId,
            [questionId]: {
              ...currentState.byId[questionId],
              assets: nextAssets,
            },
          },
        });
      }

      if (sourceQuestion?._bulkLocalId) {
        setBulkQuestions((current) =>
          current.map((item) =>
            item.localId === sourceQuestion._bulkLocalId
              ? {
                ...item,
                form: { ...item.form, assets: nextAssets },
              }
              : item,
          ),
        );
      }
    },
    [applyQuestionState],
  );

  const mergeQuestionIntoCurrentResults = useCallback(
    (question) => {
      const questionId = getRecordId(question);
      const currentState = questionStateRef.current;

      if (!questionId || !currentState.ids.includes(questionId)) return;

      applyQuestionState({
        ...currentState,
        byId: {
          ...currentState.byId,
          [questionId]: {
            ...(currentState.byId[questionId] || {}),
            ...question,
          },
        },
      });
    },
    [applyQuestionState],
  );

  const rememberQuestionSearchPosition = useCallback((questionId) => {
    questionSearchReturnRef.current = {
      questionId,
      scrollY: window.scrollY,
    };
  }, []);

  const restoreQuestionSearchPosition = useCallback(() => {
    const returnState = questionSearchReturnRef.current;
    if (!returnState) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: returnState.scrollY,
          behavior: "auto",
        });
        questionSearchReturnRef.current = null;
      });
    });
  }, []);

  const clearQuestionState = useCallback(() => {
    questionRequestKeyRef.current += 1;
    applyQuestionState({
      signature: "",
      ids: [],
      byId: {},
      nextCursor: null,
      hasMore: false,
    });
    setSelectedQuestionIds([]);
    setQuestionSelectionMode(false);
    setQuestionLoading(false);
    setLoadingMoreQuestions(false);
  }, [applyQuestionState]);

  const visibleQuestionIds = useMemo(
    () =>
      displayQuestions.map((question) => getRecordId(question)).filter(Boolean),
    [displayQuestions],
  );
  const selectedQuestionIdSet = useMemo(
    () => new Set(selectedQuestionIds),
    [selectedQuestionIds],
  );
  const allVisibleQuestionsSelected =
    visibleQuestionIds.length > 0 &&
    visibleQuestionIds.every((id) => selectedQuestionIdSet.has(id));
  const someVisibleQuestionsSelected = visibleQuestionIds.some((id) =>
    selectedQuestionIdSet.has(id),
  );
  const selectedVisibleQuestionIds = useMemo(
    () => selectedQuestionIds.filter((id) => visibleQuestionIds.includes(id)),
    [selectedQuestionIds, visibleQuestionIds],
  );

  useEffect(() => {
    setSelectedQuestionIds([]);
    setQuestionSelectionMode(false);
  }, [activeTab, authToken, filters, isActive, routeActive]);

  useLayoutEffect(() => {
    if (selectAllQuestionsRef.current) {
      selectAllQuestionsRef.current.indeterminate =
        someVisibleQuestionsSelected && !allVisibleQuestionsSelected;
    }
  }, [allVisibleQuestionsSelected, questionSelectionMode, someVisibleQuestionsSelected]);

  const loadQuestionResults = useCallback(
    async ({ reset = true, preserveNotice = false } = {}) => {
      if (!shouldLoadQuestionResults) {
        clearQuestionState();
        return;
      }

      const signature = activeQuestionFilterSignature;
      const currentState = questionStateRef.current;
      questionRequestKeyRef.current += 1;
      const requestKey = questionRequestKeyRef.current;

      if (
        !reset &&
        (currentState.signature !== signature ||
          !currentState.hasMore ||
          !currentState.nextCursor)
      ) {
        return;
      }

      if (reset) {
        applyQuestionState({
          signature,
          ids: [],
          byId: {},
          nextCursor: null,
          hasMore: false,
        });
        setSelectedQuestionIds([]);
        setQuestionSelectionMode(false);
        setLoadingMoreQuestions(false);
        setQuestionLoading(true);
      } else {
        setLoadingMoreQuestions(true);
      }
      if (!preserveNotice) {
        clearNotice();
      }

      try {
        const uuidSearch = isUuidSearch(activeQuestionFilters.search);
        let questionData;

        if (uuidSearch) {
          let question = null;

          try {
            question = await getQuestion({
              authToken,
              id: activeQuestionFilters.search,
            });
          } catch (error) {
            if (error.status !== 404) {
              throw error;
            }
          }

          questionData = question
            ? { results: [question], next_cursor: null, has_more: false, total: 1 }
            : { results: [], next_cursor: null, has_more: false, total: 0 };
        } else {
          questionData = await searchQuestions({
            authToken,
            params: {
              ...activeQuestionFilters,
              limit: questionPageSize,
              include_details: 1,
              ...(reset ? { include_total: 1 } : {}),
              ...(reset ? {} : { cursor: currentState.nextCursor }),
            },
          });
        }

        if (activeQuestionFilterSignatureRef.current !== signature) {
          return;
        }

        const pageQuestions = questionData.results || [];

        if (activeQuestionFilterSignatureRef.current !== signature) {
          return;
        }

        const baseState = reset
          ? { ids: [], byId: {} }
          : questionStateRef.current;
        const nextById = { ...baseState.byId };
        const nextIds = reset ? [] : [...baseState.ids];
        const nextIdSet = new Set(nextIds);

        pageQuestions.forEach((question) => {
          const questionId = getRecordId(question);
          if (!questionId) return;

          nextById[questionId] = {
            ...(nextById[questionId] || {}),
            ...question,
          };

          if (!nextIdSet.has(questionId)) {
            nextIds.push(questionId);
            nextIdSet.add(questionId);
          }
        });

        applyQuestionState({
          signature,
          ids: nextIds,
          byId: nextById,
          nextCursor: questionData.next_cursor || null,
          hasMore: Boolean(questionData.has_more),
          total: questionData.total ?? (reset ? null : currentState.total),
        });
      } catch (error) {
        if (activeQuestionFilterSignatureRef.current === signature) {
          setNotice({ tone: "error", text: error.message || "題目搜尋失敗" });
        }
      } finally {
        if (questionRequestKeyRef.current === requestKey) {
          if (reset) {
            setQuestionLoading(false);
          } else {
            setLoadingMoreQuestions(false);
          }
        }
      }
    },
    [
      getQuestion,
      searchQuestions,
      activeQuestionFilterSignature,
      activeQuestionFilters,
      applyQuestionState,
      authToken,
      clearNotice,
      clearQuestionState,
      shouldLoadQuestionResults,
    ],
  );

  useEffect(() => {
    if (activeTab !== "question-search") return undefined;

    if (!shouldLoadQuestionResults) {
      clearQuestionState();
      return undefined;
    }

    if (skipNextQuestionSearchRefreshRef.current) {
      skipNextQuestionSearchRefreshRef.current = false;
      if (
        questionStateRef.current.signature === activeQuestionFilterSignature
      ) {
        return undefined;
      }
    }

    const timer = window.setTimeout(() => {
      loadQuestionResults({ reset: true });
    }, 320);

    return () => window.clearTimeout(timer);
  }, [
    activeQuestionFilterSignature,
    activeTab,
    clearQuestionState,
    loadQuestionResults,
    shouldLoadQuestionResults,
  ]);

  useEffect(() => {
    const sentinel = questionSentinelRef.current;

    if (
      activeTab !== "question-search" ||
      !sentinel ||
      !shouldLoadQuestionResults ||
      !hasMoreQuestions ||
      questionLoading ||
      loadingMoreQuestions
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadQuestionResults({ reset: false });
        }
      },
      { rootMargin: "220px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    activeTab,
    hasMoreQuestions,
    loadQuestionResults,
    loadingMoreQuestions,
    questionLoading,
    shouldLoadQuestionResults,
  ]);

  const filteredUnitsForQuestion = useMemo(
    () =>
      units.filter(
        (unit) =>
          !questionForm.grade_id ||
          getUnitGradeId(unit) === questionForm.grade_id,
      ),
    [questionForm.grade_id, units],
  );

  const filteredUnitsForUnitForm = useMemo(
    () =>
      units.filter(
        (unit) =>
          !unitForm.grade_id || getUnitGradeId(unit) === unitForm.grade_id,
      ),
    [unitForm.grade_id, units],
  );

  const filteredUnitsForFilter = useMemo(() => {
    if (filters.grade_id === filterNoneValue) return [];
    if (filters.grade_id === filterAllValue) return units;

    return units.filter((unit) => filterMatches(filters.grade_id, getUnitGradeId(unit)));
  }, [filters.grade_id, units]);

  const filteredUnitsForApiImport = useMemo(
    () =>
      units.filter(
        (unit) =>
          apiImportScope.grade_id &&
          getUnitGradeId(unit) === apiImportScope.grade_id,
      ),
    [apiImportScope.grade_id, units],
  );

  const filteredUnitsForBulkImport = useMemo(
    () =>
      units.filter(
        (unit) =>
          bulkImportScope.grade_id &&
          getUnitGradeId(unit) === bulkImportScope.grade_id,
      ),
    [bulkImportScope.grade_id, units],
  );

  const filteredGradesForGradeForm = useMemo(() => {
    const keyword = normalizeSearchText(gradeForm.name);
    if (!keyword) return grades;

    return grades.filter((grade) =>
      normalizeSearchText(`${grade.name} ${getRecordId(grade)}`).includes(keyword),
    );
  }, [gradeForm.name, grades]);

  const filteredUnitsForUnitList = useMemo(() => {
    const keyword = normalizeSearchText(unitForm.name);

    return units.filter((unit) => {
      const matchesGrade =
        !unitForm.grade_id || getUnitGradeId(unit) === unitForm.grade_id;
      const matchesName =
        !keyword ||
        normalizeSearchText(`${unit.name} ${getRecordId(unit)}`).includes(keyword);

      return matchesGrade && matchesName;
    });
  }, [unitForm.grade_id, unitForm.name, units]);

  const filteredSourcesForSourceForm = useMemo(() => {
    const keyword = normalizeSearchText(sourceForm.name);
    if (!keyword) return sources;

    return sources.filter((source) =>
      normalizeSearchText(`${source.name} ${getRecordId(source)}`).includes(keyword),
    );
  }, [sourceForm.name, sources]);

  const setSuccess = useCallback(
    (text) => {
      showNotice({ tone: "success", text }, { autoHide: true });
    },
    [showNotice],
  );

  const copyTextToClipboard = async (
    text,
    successText,
    errorText,
    { showNotice = true } = {},
  ) => {
    if (!text) return false;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        if (showNotice) setSuccess(successText);
        return true;
      }
    } catch {
      // Some browsers reject clipboard writes after an asynchronous save.
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    let copied = false;
    try {
      copied = document.execCommand?.("copy") === true;
    } catch {
      copied = false;
    } finally {
      document.body.removeChild(textarea);
    }

    if (copied && showNotice) setSuccess(successText);
    else if (!copied && errorText && showNotice) {
      setNotice({ tone: "error", text: errorText });
    }
    return copied;
  };

  const beginQuestionIdClipboardWrite = () => {
    if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
      return null;
    }

    let resolveQuestionId;
    const questionIdPromise = new Promise((resolve) => {
      resolveQuestionId = resolve;
    });
    const clipboardData = questionIdPromise.then((id) => {
      if (!id) throw new Error("Question creation did not return a UUID");
      return new Blob([id], { type: "text/plain" });
    });
    clipboardData.catch(() => {});

    try {
      const writeResult = navigator.clipboard.write([
        new ClipboardItem({ "text/plain": clipboardData }),
      ]).then(() => true, () => false);
      return {
        complete: (id) => {
          resolveQuestionId(id);
          return writeResult;
        },
      };
    } catch {
      resolveQuestionId(null);
      return null;
    }
  };

  const copyApiImportCommand = (text, successText, errorText) => {
    if (!apiImportScope.grade_id || !apiImportScope.unit_id) {
      setNotice({
        tone: "error",
        text: "請先選擇入題年級與單元，系統會自動帶入 UUID。",
      });
      return;
    }
    copyTextToClipboard(text, successText, errorText);
  };

  const copyQuestionId = async (
    id,
    successText = "題目 UUID 已複製",
    errorText = "UUID 複製失敗",
  ) => copyTextToClipboard(id, successText, errorText);

  const copyRecordId = async (id, label) =>
    copyTextToClipboard(id, `${label} UUID 已複製`, "UUID 複製失敗");

  const copyMarkdownText = async (text) => {
    copyTextToClipboard(text, "Markdown 已複製", "Markdown 複製失敗");
  };

  const copyAssetSourceCode = (sourceCode) => {
    copyTextToClipboard(
      sourceCode,
      "Figure Code copied",
      "Failed to copy Figure Code",
    );
  };

  const updateStaffApiKey = (value) => {
    setStaffApiKey(value);
    try {
      if (value) {
        window.localStorage.setItem(staffApiKeyStorageKey, value);
      } else {
        window.localStorage.removeItem(staffApiKeyStorageKey);
      }
    } catch {
      // localStorage may be unavailable in private browsing modes.
    }
  };

  const updateStaffAuthorLabel = (value) => {
    setStaffAuthorLabel(value);
    try {
      if (value) {
        window.localStorage.setItem(staffAuthorLabelStorageKey, value);
      } else {
        window.localStorage.removeItem(staffAuthorLabelStorageKey);
      }
    } catch {
      // localStorage may be unavailable in private browsing modes.
    }
  };

  const buildBulkDraftPayload = () => ({
    scope: bulkImportScope,
    text: bulkImportText,
    questions: bulkQuestions,
    allowDuplicate: bulkAllowDuplicate,
    wordMode: bulkWordMode,
    splitMode: bulkSplitMode,
  });

  const getBulkDraftTitle = () => {
    const firstQuestion = bulkQuestions.find((item) =>
      item.form?.prompt_md?.trim(),
    );
    const preview = String(
      firstQuestion?.form?.prompt_md || bulkImportText || "",
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 28);
    return preview
      ? `大量入題暫存：${preview}`
      : `大量入題暫存 ${formatDraftUpdatedAt(new Date().toISOString())}`;
  };

  const saveBulkDraftSession = async ({ showShortcutToast = false } = {}) => {
    if (!bulkImportText.trim() && !bulkQuestions.length) {
      setNotice({ tone: "error", text: "目前沒有可暫存的內容。" });
      return false;
    }
    setBulkDraftSaving(true);
    try {
      const currentTitle = bulkDraftSessions.find(
        (draft) => draft.id === selectedBulkDraftId,
      )?.title || getBulkDraftTitle();
      const result = await promptDraftName({
        title: currentTitle,
        workspaceLabel: "大量入題",
        isExisting: Boolean(selectedBulkDraftId),
      });
      if (!result.isConfirmed) return false;
      const payload = {
        kind: bulkImportDraftKind,
        title: String(result.value).trim(),
        payload: buildBulkDraftPayload(),
      };
      const savedDraft = selectedBulkDraftId
        ? await updateDraftSession({
          authToken,
          id: selectedBulkDraftId,
          data: payload,
        })
        : await createDraftSession({ authToken, data: payload });

      setSelectedBulkDraftId(savedDraft.id);
      await loadBulkDraftSessions();
      setSuccess("大量入題已暫存。");
      if (showShortcutToast) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "大量入題已暫存",
          showConfirmButton: false,
          timer: 1400,
          timerProgressBar: true,
        });
      }
      return true;
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "大量入題暫存失敗" });
      return false;
    } finally {
      setBulkDraftSaving(false);
    }
  };

  useEffect(() => {
    if (!routeActive || !isActive || activeTab !== "bulk-import") return undefined;

    const handleBulkDraftShortcut = (event) => {
      const isSaveShortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        event.key.toLowerCase() === "s";
      if (!isSaveShortcut) return;

      event.preventDefault();
      if (
        event.repeat ||
        bulkDraftSaving ||
        bulkImporting ||
        bulkWordParsing
      ) {
        return;
      }
      saveBulkDraftSession({ showShortcutToast: true });
    };

    window.addEventListener("keydown", handleBulkDraftShortcut);
    return () => window.removeEventListener("keydown", handleBulkDraftShortcut);
  });

  const loadBulkDraftSession = (draftId = selectedBulkDraftId) => {
    const draft = bulkDraftSessions.find((item) => item.id === draftId);
    if (!draft) {
      setNotice({ tone: "error", text: "請先選擇一筆暫存。" });
      return;
    }
    const payload = draft.payload || {};
    setBulkImportScope({
      ...emptyBulkImportScope,
      ...(payload.scope || {}),
    });
    setBulkImportText(String(payload.text || ""));
    setBulkQuestions(Array.isArray(payload.questions) ? payload.questions : []);
    setBulkAllowDuplicate(Boolean(payload.allowDuplicate));
    setBulkWordMode(payload.wordMode || "fast");
    setBulkSplitMode(payload.splitMode || "auto");
    setSelectedBulkDraftId(draft.id);
    closeBulkQuestionEditor();
    setSuccess(`已載入暫存：${draft.title}`);
  };

  const deleteBulkDraftSession = async () => {
    const draft = bulkDraftSessions.find(
      (item) => item.id === selectedBulkDraftId,
    );
    if (!draft) {
      setNotice({ tone: "error", text: "請先選擇一筆暫存。" });
      return;
    }

    const result = await Swal.fire({
      title: `刪除暫存「${draft.title}」？`,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#b3261e",
      cancelButtonColor: "#6b7280",
    });
    if (!result.isConfirmed) return;

    setBulkDraftSaving(true);
    try {
      await deleteDraftSession({ authToken, id: draft.id });
      setSelectedBulkDraftId("");
      await loadBulkDraftSessions();
      setSuccess("暫存已刪除。");
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "暫存刪除失敗" });
    } finally {
      setBulkDraftSaving(false);
    }
  };

  const updateBulkImportScope = (key, value) => {
    setBulkImportScope((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "grade_id" ? { unit_id: "" } : {}),
    }));
  };

  const parseBulkQuestions = () => {
    const nextQuestions = parseBulkImportText(
      bulkImportText,
      bulkImportScope,
      bulkSplitMode,
    );
    if (!nextQuestions.length) {
      setNotice({ tone: "error", text: "請先貼上要切割的題目文字。" });
      return;
    }
    setBulkQuestions(nextQuestions);
    closeBulkQuestionEditor();
    setSuccess(`已切出 ${nextQuestions.length} 題，請先預覽再入題。`);
  };

  const loadBulkQuestionsFromText = useCallback(
    (
      text,
      successPrefix = "已切出",
      scope = bulkImportScope,
      splitMode = bulkSplitMode,
    ) => {
      const nextQuestions = parseBulkImportText(text, scope, splitMode);
      if (!nextQuestions.length) {
        setBulkImportText(text);
        showNotice(
          {
            tone: "error",
            text: "解析完成，但沒有切出可用題目。請在題目之間加入獨立一行 --- 後重新切割。",
          },
          { autoHide: true },
        );
        return [];
      }
      setBulkImportText(text);
      setBulkQuestions(nextQuestions);
      closeBulkQuestionEditor();
      setSuccess(
        `${successPrefix} ${nextQuestions.length} 題，請先預覽再入題。`,
      );
      return nextQuestions;
    },
    [bulkImportScope, bulkSplitMode, setSuccess, showNotice, closeBulkQuestionEditor],
  );

  const receiveDocumentBulkImportTransfer = useCallback(
    (payload) => {
      if (!isActive || payload?.target !== "math-bank-bulk-import") return false;

      const incomingText = String(payload.text || "").trim();
      if (!incomingText) return false;
      const transferIdentity = `${payload.createdAt || ""}:${payload.sourceMode || ""}:${incomingText}`;
      if (handledBulkTransferRef.current === transferIdentity) return false;
      handledBulkTransferRef.current = transferIdentity;

      const incomingScope =
        payload.scope && typeof payload.scope === "object"
          ? payload.scope
          : null;
      const nextScope = incomingScope
        ? {
          ...bulkImportScope,
          ...incomingScope,
        }
        : bulkImportScope;

      if (incomingScope) {
        setBulkImportScope(nextScope);
      }

      const nextSplitMode = payload.splitMode || bulkSplitMode;
      if (payload.splitMode) {
        setBulkSplitMode(nextSplitMode);
      }

      const nextQuestions = loadBulkQuestionsFromText(
        incomingText,
        payload.sourceTitle ? `${payload.sourceTitle}已切出` : "文件解析已切出",
        nextScope,
        nextSplitMode,
      );

      changeActiveTab("bulk-import", { resetScroll: true });
      setSelectedBulkDraftId("");
      closeBulkQuestionEditor();
      clearDocumentBulkImportTransfer();
      if (payload.autoSaveDraft && ["local_ocr", "local_ocr_latex"].includes(payload.sourceMode) && nextQuestions.length) {
        if (!authToken) {
          setNotice({ tone: "error", text: "已切題，但尚未登入，無法自動暫存。登入後可按「儲存暫存」。" });
          return true;
        }
        const titlePreview = String(nextQuestions[0]?.form?.prompt_md || incomingText)
          .replace(/\s+/g, " ").trim().slice(0, 28);
        setBulkDraftSaving(true);
        createDraftSession({
          authToken,
          data: {
            kind: bulkImportDraftKind,
            title: `${payload.sourceTitle || "後端 OCR"}暫存：${titlePreview}`,
            payload: {
              scope: nextScope,
              text: incomingText,
              questions: nextQuestions,
              allowDuplicate: false,
              wordMode: bulkWordMode,
              splitMode: nextSplitMode,
            },
          },
        }).then(async (savedDraft) => {
          setSelectedBulkDraftId(savedDraft.id);
          await loadBulkDraftSessions();
          setSuccess(`已切出並暫存 ${nextQuestions.length} 題，請逐題檢查後再入題。`);
        }).catch((error) => {
          setNotice({ tone: "error", text: `已切題，但自動暫存失敗：${error.message || "請按「儲存暫存」重試。"}` });
        }).finally(() => setBulkDraftSaving(false));
      }
      return nextQuestions.length > 0;
    },
    [
      isActive,
      bulkImportScope,
      bulkSplitMode,
      bulkWordMode,
      authToken,
      changeActiveTab,
      closeBulkQuestionEditor,
      createDraftSession,
      loadBulkQuestionsFromText,
      loadBulkDraftSessions,
      setSuccess,
    ],
  );

  useEffect(() => {
    receiveDocumentBulkImportTransfer(readDocumentBulkImportTransfer());

    const handleTransfer = (event) => {
      receiveDocumentBulkImportTransfer(
        event.detail || readDocumentBulkImportTransfer(),
      );
    };

    window.addEventListener(
      DOCUMENT_BULK_IMPORT_TRANSFER_EVENT,
      handleTransfer,
    );
    return () =>
      window.removeEventListener(
        DOCUMENT_BULK_IMPORT_TRANSFER_EVENT,
        handleTransfer,
      );
  }, [receiveDocumentBulkImportTransfer]);

  const handleBulkWordFile = async (file) => {
    if (!file) return;
    if (!isWordDocumentFile(file)) {
      setNotice({ tone: "error", text: "請選擇 .docx Word 檔。" });
      return;
    }

    setBulkWordParsing(true);
    setBulkWordProgress({
      percent: 8,
      label: "準備讀取 Word 檔案",
      status: "loading",
      fileName: file.name,
      elapsedSeconds: 0,
      error: "",
    });
    clearNotice();
    let progressTimer = null;
    let elapsedTimer = null;
    const startedAt = Date.now();
    try {
      elapsedTimer = window.setInterval(() => {
        setBulkWordProgress((current) => ({
          ...current,
          elapsedSeconds: Math.max(
            1,
            Math.round((Date.now() - startedAt) / 1000),
          ),
        }));
      }, 1000);
      window.setTimeout(() => {
        setBulkWordProgress((current) =>
          current.percent < 18
            ? { ...current, percent: 18, label: "上傳 Word 檔案" }
            : current,
        );
      }, 120);
      progressTimer = window.setInterval(() => {
        setBulkWordProgress((current) => {
          if (current.percent >= 88) return current;
          const nextPercent = Math.min(88, current.percent + 4);
          return {
            ...current,
            percent: nextPercent,
            label:
              nextPercent < 42
                ? "上傳 Word 檔案"
                : nextPercent < 72
                  ? "解析文字、表格與公式線索"
                  : "整理題目結構",
          };
        });
      }, 650);

      const result = await parseWordDocument({
        file,
        includeAssets: true,
        mode: bulkWordMode,
        authToken,
      });
      if (!result.success || !result.document) {
        throw new Error(result.error || "Word 文件解析失敗");
      }
      if (progressTimer) {
        window.clearInterval(progressTimer);
        progressTimer = null;
      }
      if (elapsedTimer) {
        window.clearInterval(elapsedTimer);
        elapsedTimer = null;
      }
      const elapsedSeconds = Math.max(
        1,
        Math.round((Date.now() - startedAt) / 1000),
      );
      setBulkWordProgress((current) => ({
        ...current,
        percent: 92,
        label: "轉成題目文字",
        elapsedSeconds,
      }));
      const nextText = bulkWordDocumentToText(result.document);
      setBulkWordProgress((current) => ({
        ...current,
        percent: 97,
        label: "建立預覽清單",
        elapsedSeconds,
      }));
      loadBulkQuestionsFromText(nextText, `Word「${file.name}」已切出`);
      setBulkWordProgress((current) => ({
        ...current,
        percent: 100,
        label: "解析完成",
        status: "success",
        elapsedSeconds,
        error: "",
      }));
    } catch (error) {
      if (progressTimer) {
        window.clearInterval(progressTimer);
        progressTimer = null;
      }
      if (elapsedTimer) {
        window.clearInterval(elapsedTimer);
        elapsedTimer = null;
      }
      setBulkWordProgress((current) => ({
        ...current,
        percent: 100,
        label: "解析失敗",
        status: "error",
        elapsedSeconds: Math.max(
          1,
          Math.round((Date.now() - startedAt) / 1000),
        ),
        error: error.message || "Word 文件解析失敗",
      }));
      setNotice({
        tone: "error",
        text: error.message || "Word 文件解析失敗",
      });
    } finally {
      window.setTimeout(() => {
        setBulkWordParsing(false);
        setBulkWordProgress({
          percent: 0,
          label: "",
          status: "idle",
          fileName: "",
          elapsedSeconds: 0,
          error: "",
        });
      }, 420);
      if (bulkWordInputRef.current) {
        bulkWordInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    const handlePaste = (event) => {
      if (
        !isVisiblePasteRoot(pageShellRef.current) ||
        activeTab !== "bulk-import" ||
        bulkWordParsing ||
        bulkImporting
      ) {
        return;
      }

      const wordFile = getClipboardFiles(event.clipboardData).find(isWordDocumentFile);
      if (!wordFile) return;

      event.preventDefault();
      setBulkWordDragOver(false);
      handleBulkWordFile(wordFile);
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  });

  const updateBulkQuestion = (localId, updater) => {
    setBulkQuestions((current) =>
      current.map((item) =>
        item.localId === localId
          ? {
            ...item,
            ...(typeof updater === "function" ? updater(item) : updater),
          }
          : item,
      ),
    );
  };

  const updateBulkEditingQuestion = (localId, updater) => {
    const session = bulkEditingSessionRef.current;
    if (session?.status !== "editing" || session.question.localId !== localId) return;
    const previous = session.question;
    const next = { ...previous, ...updater(previous) };
    Object.keys(next.form).forEach((field) => {
      if (next.form[field] !== previous.form[field]) session.changedFields.add(field);
    });
    session.question = next;
    setBulkEditingQuestion(next);
  };

  const updateBulkQuestionForm = (localId, patch) => {
    updateBulkEditingQuestion(localId, (item) => ({
      form: {
        ...item.form,
        ...patch,
      },
      edited: true,
      importStatus:
        item.importStatus === "success" ? "idle" : item.importStatus,
      importMessage: item.importStatus === "success" ? "" : item.importMessage,
      createdId: item.importStatus === "success" ? "" : item.createdId,
    }));
  };

  const updateBulkQuestionAsset = (localId, assetIndex, patch) => {
    updateBulkEditingQuestion(localId, (item) => ({
      form: {
        ...item.form,
        assets: item.form.assets.map((asset, index) =>
          index === assetIndex ? { ...asset, ...patch } : asset,
        ),
      },
      edited: true,
      importStatus:
        item.importStatus === "success" ? "idle" : item.importStatus,
      importMessage: item.importStatus === "success" ? "" : item.importMessage,
      createdId: item.importStatus === "success" ? "" : item.createdId,
    }));
  };

  const addBulkQuestionAsset = (localId) => {
    updateBulkEditingQuestion(localId, (item) => ({
      form: {
        ...item.form,
        assets: [
          ...item.form.assets,
          {
            role: "prompt",
            url: "",
            storage_key: "",
            alt_text: "",
            mime_type: "",
            source_language: "python",
            source_code: "",
            marking_guide: "",
            sort_order: item.form.assets.length,
          },
        ],
      },
      edited: true,
      importStatus:
        item.importStatus === "success" ? "idle" : item.importStatus,
      importMessage: item.importStatus === "success" ? "" : item.importMessage,
      createdId: item.importStatus === "success" ? "" : item.createdId,
    }));
  };

  const removeBulkQuestionAsset = (localId, assetIndex) => {
    updateBulkEditingQuestion(localId, (item) => ({
      form: {
        ...item.form,
        assets: item.form.assets.filter((_, index) => index !== assetIndex),
      },
      edited: true,
      importStatus:
        item.importStatus === "success" ? "idle" : item.importStatus,
      importMessage: item.importStatus === "success" ? "" : item.importMessage,
      createdId: item.importStatus === "success" ? "" : item.createdId,
    }));
  };

  const toggleBulkQuestion = (localId) => {
    updateBulkQuestion(localId, (item) => ({ selected: !item.selected }));
  };

  const setAllBulkQuestionsSelected = (selected) => {
    setBulkQuestions((current) =>
      current.map((item) => ({
        ...item,
        selected,
      })),
    );
  };

  const removeBulkQuestion = (localId) => {
    setBulkQuestions((current) =>
      current.filter((item) => item.localId !== localId),
    );
    if (bulkEditingSessionRef.current?.question.localId === localId) {
      closeBulkQuestionEditor();
    }
  };

  const insertBlankBulkQuestionBelow = (localId) => {
    if (bulkImporting) return;
    const blankLocalId = `${Date.now()}-blank-${Math.random().toString(16).slice(2)}`;
    setBulkQuestions((current) => {
      const index = current.findIndex((item) => item.localId === localId);
      if (index < 0) return current;
      const sourceForm = current[index].form;
      const scope = Object.fromEntries(
        Object.keys(emptyBulkImportScope).map((key) => [
          key,
          sourceForm[key] ?? bulkImportScope[key],
        ]),
      );
      const blankQuestion = {
        localId: blankLocalId,
        form: {
          ...emptyQuestionForm,
          ...scope,
          new_grade_name: sourceForm.new_grade_name || "",
          new_unit_name: sourceForm.new_unit_name || "",
          choice_options: createEmptyChoiceOptions(),
          assets: [],
        },
        selected: true,
        edited: false,
        importStatus: "idle",
        importMessage: "",
        createdId: "",
      };
      return [
        ...current.slice(0, index + 1),
        blankQuestion,
        ...current.slice(index + 1),
      ].map((item, questionIndex) => ({
        ...item,
        questionNumber: String(questionIndex + 1),
      }));
    });
  };

  const applyBulkScopeToQuestions = () => {
    setBulkQuestions((current) =>
      current.map((item) => ({
        ...item,
        form: {
          ...item.form,
          grade_id: bulkImportScope.grade_id,
          unit_id: bulkImportScope.unit_id,
          type: bulkImportScope.type,
          difficulty: bulkImportScope.difficulty,
          is_good_question: Boolean(bulkImportScope.is_good_question),
          status: bulkImportScope.status,
          visibility: bulkImportScope.visibility,
          thinking: bulkImportScope.thinking,
          author_label: bulkImportScope.author_label,
          question_source: bulkImportScope.question_source,
        },
        edited: true,
        importStatus:
          item.importStatus === "success" ? "idle" : item.importStatus,
        importMessage:
          item.importStatus === "success" ? "" : item.importMessage,
        createdId: item.importStatus === "success" ? "" : item.createdId,
      })),
    );
    setSuccess("已套用批次設定到預覽清單。");
  };

  const copyBulkQuestionTags = async () => {
    const ids = bulkQuestions.map((item) => item.createdId).filter(Boolean);
    const tags = formatMathQuestionTags(ids);
    const copied = await copyTextToClipboard(
      tags,
      `已複製 ${ids.length} 題 math-question 標籤`,
      "題目標籤複製失敗",
      { showNotice: false },
    );
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: copied ? "success" : "error",
      title: copied ? `已複製 ${ids.length} 題標籤` : "題目標籤複製失敗",
      showConfirmButton: false,
      timer: 1400,
      timerProgressBar: true,
    });
  };

  const runBulkQuestionSolutionApi = async (item, { quiet = false } = {}) => {
    item = solutionLiveRef.current.bulkQuestions.find((candidate) => candidate.localId === item.localId);
    if (!item || bulkSolutionTasksRef.current.has(item.localId) || !solutionLiveRef.current.active) return false;
    const prompt = getQuestionPrompt(item.form).trim();
    const task = { scope: solutionScopeRef.current, signature: getSolutionInputSignature(item.form) };
    bulkSolutionTasksRef.current.set(item.localId, task);
    const isCurrent = () => {
      const current = solutionLiveRef.current.bulkQuestions.find((candidate) => candidate.localId === item.localId);
      return solutionLiveRef.current.active && task.scope === solutionScopeRef.current &&
        bulkSolutionTasksRef.current.get(item.localId) === task && current && getSolutionInputSignature(current.form) === task.signature;
    };
    try {
      const promptAssets = collectQuestionPromptImages({ prompt, assets: item.form.assets });
      if (!prompt && !promptAssets.length) throw new Error("請先輸入題目內容或附上題幹圖片再打 API。");
      const imageJob = promptAssets.length > 0;
      const startJob = imageJob ? startAiSolutionFromImagesJob : startAiProcessJob;
      const getJob = imageJob ? getAiSolutionFromImagesJob : getAiProcessJob;
      if (typeof startJob !== "function" || typeof getJob !== "function") throw new Error(imageJob ? "目前無法使用背景圖片詳解工作，請稍後再試。" : "目前無法使用背景詳解工作。");
      updateBulkQuestion(item.localId, { aiStatus: "loading", aiMessage: imageJob ? "正在連同題幹圖片產生詳解..." : "正在產生詳解..." });
      const answer = String(item.form.answer_md || "").trim();
      const source = [
        `題目：\n${prompt || "請依附圖題目產生答案與詳解。"}`,
        answer ? `答案：\n${answer}` : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      const startResult = imageJob ? await startJob([], { text: source, promptAssets }) : await startJob(source, "solution");
      if (!isCurrent()) return false;
      if (!startResult?.success || !startResult.job?.id) {
        throw new Error(startResult?.error || "詳解工作建立失敗。");
      }
      const completedJob = await waitForQuestionSolutionJob({
        initialJob: startResult.job,
        getAiProcessJob: getJob,
        isCurrent,
        onProgress: (job) => {
          const total = Number(job?.total || 1);
          const current = Number(job?.current || 0);
          updateBulkQuestion(item.localId, {
            aiStatus: "loading",
            aiMessage: `背景詳解處理中 ${current}/${total}`,
          });
        },
      });
      if (!completedJob || !isCurrent()) return false;
      const parsed = parseAiSolutionText(completedJob.text);
      bulkSolutionTasksRef.current.delete(item.localId);
      updateBulkQuestion(item.localId, (current) => ({
        form: {
          ...current.form,
          solution_md: parsed.solution_md,
          answer_md: current.form.answer_md || parsed.answer_md,
        },
        edited: true,
        aiStatus: "success",
        aiMessage: "背景詳解已完成",
        importStatus: ["success", "duplicate"].includes(current.importStatus)
          ? "idle"
          : current.importStatus,
        importMessage: ["success", "duplicate"].includes(current.importStatus)
          ? ""
          : current.importMessage,
        createdId: ["success", "duplicate"].includes(current.importStatus)
          ? ""
          : current.createdId,
      }));
      if (!quiet) {
        setSuccess(`第 ${item.questionNumber || "這"} 題詳解已產生。`);
      }
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      bulkSolutionTasksRef.current.delete(item.localId);
      const message = error.message || "詳解 API 執行失敗。";
      updateBulkQuestion(item.localId, {
        aiStatus: "error",
        aiMessage: message,
      });
      if (!quiet) setNotice({ tone: "error", text: message });
      return false;
    }
  };

  const runEveryBulkQuestionSolutionApi = async () => {
    if (!bulkQuestions.length || bulkSolutionProgress.running) return;
    const scopeId = solutionScopeRef.current;
    const isCurrent = () => scopeId === solutionScopeRef.current && solutionLiveRef.current.active;
    const questions = [...bulkQuestions];
    let success = 0;
    let failed = 0;
    setNotice(null);
    setBulkSolutionProgress({
      running: true,
      current: 0,
      total: questions.length,
      success: 0,
      failed: 0,
    });

    for (let index = 0; index < questions.length; index += 1) {
      if (!isCurrent()) return;
      setBulkSolutionProgress({
        running: true,
        current: index + 1,
        total: questions.length,
        success,
        failed,
      });
      const completed = await runBulkQuestionSolutionApi(questions[index], {
        quiet: true,
      });
      if (!isCurrent()) return;
      if (completed) success += 1;
      else failed += 1;
      setBulkSolutionProgress({
        running: true,
        current: index + 1,
        total: questions.length,
        success,
        failed,
      });
    }

    setBulkSolutionProgress({
      running: false,
      current: questions.length,
      total: questions.length,
      success,
      failed,
    });
    setNotice({
      tone: failed ? "error" : "success",
      text: `每題詳解完成：成功 ${success}，失敗 ${failed}`,
    });
  };

  const importSelectedBulkQuestions = async () => {
    const selectedItems = bulkQuestions.filter((item) => item.selected);
    if (!selectedItems.length) {
      setNotice({ tone: "error", text: "請先勾選要入題的項目。" });
      return;
    }

    const invalidItem = selectedItems.find(
      (item) => getQuestionMissingFields(item.form).length,
    );
    if (invalidItem) {
      openBulkQuestionEditor(invalidItem);
      setNotice({
        tone: "error",
        text: `請先補齊：${getQuestionMissingFields(invalidItem.form).join("、")}`,
      });
      return;
    }

    const result = await Swal.fire({
      title: `大量入題 ${selectedItems.length} 題？`,
      icon: "question",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "開始入題",
      cancelButtonText: "取消",
      confirmButtonColor: "#202124",
      cancelButtonColor: "#6b7280",
    });
    if (!result.isConfirmed) return;

    setBulkImporting(true);
    setSaving(true);
    let successCount = 0;
    let duplicateCount = 0;
    let failCount = 0;
    const createdQuestionIds = [];

    try {
      for (const item of selectedItems) {
        updateBulkQuestion(item.localId, {
          importStatus: "loading",
          importMessage: "入題中...",
          createdId: "",
        });

        try {
          const payload = {
            ...toQuestionPayload(item.form),
            ...(bulkAllowDuplicate ? { duplicate_policy: "allow" } : {}),
          };
          const createdQuestion = await createQuestion({
            authToken,
            data: payload,
          });
          rememberQuestionSource(payload.question_source);
          const createdId = createdQuestion?.id || createdQuestion?.uuid || "";
          if (createdId) createdQuestionIds.push(createdId);
          successCount += 1;
          updateBulkQuestion(item.localId, {
            selected: false,
            importStatus: "success",
            importMessage: "已入題",
            createdId,
          });
        } catch (error) {
          const duplicateError = getDuplicateError(error);
          if (duplicateError && !bulkAllowDuplicate) {
            duplicateCount += 1;
            updateBulkQuestion(item.localId, {
              importStatus: "duplicate",
              importMessage: duplicateError.message,
              createdId: duplicateError.duplicateQuestionId,
            });
          } else {
            failCount += 1;
            updateBulkQuestion(item.localId, {
              importStatus: "error",
              importMessage: error.message || "入題失敗",
              createdId: "",
            });
          }
        }
      }

      setSuccess(
        `大量入題完成：成功 ${successCount}，重複 ${duplicateCount}，失敗 ${failCount}`,
      );
      if (successCount)
        await loadQuestionResults({ reset: true, preserveNotice: true });
    } finally {
      setBulkImporting(false);
      setSaving(false);
      if (createdQuestionIds.length) {
        const ids = [...new Set(createdQuestionIds)];
        const batches = [];
        for (let index = 0; index < ids.length; index += 100) batches.push({ key: ids[index], requestId: crypto.randomUUID(), questionIds: ids.slice(index, index + 100), started: false, status: "idle", analysisId: null });
        setImportFamilyAnalysis((current) => ({
          batches: [...current.batches, ...batches],
          index: !current.batches.length || current.batches[current.index]?.status === "applied" ? current.batches.length : current.index,
          open: true,
        }));
      }
    }
  };

  const resetGradeForm = () => {
    setEditingGradeId(null);
    setGradeForm(emptyGradeForm);
  };

  const resetUnitForm = () => {
    setEditingUnitId(null);
    setUnitForm(emptyUnitForm);
  };

  const resetSourceForm = () => {
    setEditingSourceId(null);
    setSourceForm(emptySourceForm);
  };

  const resetQuestionForm = (preservedTaxonomy = null) => {
    questionEditPreviewRequestRef.current += 1;
    setEditingQuestionId(null);
    setQuestionForm({
      ...emptyQuestionForm,
      ...(preservedTaxonomy || {}),
    });
    setDuplicateQuestionWarning(null);
    setExpandedMarkdownField(null);
    setExpandedAssetSourceIndex(null);
    setAssetPreviewStatus("idle");
    setAssetPreviewMessage("");
    setQuestionAiStatus("idle");
    resetAssetPreviewLeaderSubmission();
  };

  const cancelQuestionForm = () => {
    resetQuestionForm();
    skipNextQuestionSearchRefreshRef.current =
      shouldLoadQuestionResults &&
      questionStateRef.current.signature === activeQuestionFilterSignature;
    changeActiveTab("question-search");
    restoreQuestionSearchPosition();
  };

  const runQuestionSolutionApi = async () => {
    if (questionSolutionTaskRef.current || !solutionLiveRef.current.active) return;
    const prompt = getQuestionPrompt(questionForm).trim();
    const task = { scope: solutionScopeRef.current, signature: getSolutionInputSignature(questionForm), editEpoch: questionEditPreviewRequestRef.current };
    questionSolutionTaskRef.current = task;
    const isCurrent = () => solutionLiveRef.current.active && task.scope === solutionScopeRef.current &&
      questionSolutionTaskRef.current === task && task.editEpoch === questionEditPreviewRequestRef.current &&
      task.signature === getSolutionInputSignature(solutionLiveRef.current.questionForm);
    try {
      const promptAssets = collectQuestionPromptImages({ prompt, assets: questionForm.assets });
      if (!prompt && !promptAssets.length) throw new Error("請先輸入題目內容或附上題幹圖片再產生詳解。");
      const imageJob = promptAssets.length > 0;
      const startJob = imageJob ? startAiSolutionFromImagesJob : startAiProcessJob;
      const getJob = imageJob ? getAiSolutionFromImagesJob : getAiProcessJob;
      if (typeof startJob !== "function" || typeof getJob !== "function") throw new Error(imageJob ? "目前無法使用背景圖片詳解工作，請稍後再試。" : "目前無法使用背景 AI 詳解。");
      setQuestionAiStatus("loading");
      const answer = String(questionForm.answer_md || "").trim();
      const source = [
        `題目：\n${prompt || "請依附圖題目產生答案與詳解。"}`,
        answer ? `答案：\n${answer}` : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      const startResult = imageJob ? await startJob([], { text: source, promptAssets }) : await startJob(source, "solution");
      if (!isCurrent()) return;
      if (!startResult?.success || !startResult.job?.id) {
        throw new Error(startResult?.error || "AI 詳解工作建立失敗。");
      }
      const completedJob = await waitForQuestionSolutionJob({
        initialJob: startResult.job,
        getAiProcessJob: getJob,
        isCurrent,
      });
      if (!completedJob || !isCurrent()) return;
      const parsed = parseAiSolutionText(completedJob.text);
      questionSolutionTaskRef.current = null;
      setQuestionForm((current) => ({
        ...current,
        solution_md: parsed.solution_md,
        answer_md: current.answer_md || parsed.answer_md,
      }));
      setQuestionAiStatus("success");
      setSuccess("AI 詳解已產生，請確認內容後再儲存題目。");
    } catch (error) {
      if (!isCurrent()) return;
      questionSolutionTaskRef.current = null;
      setQuestionAiStatus("error");
      setNotice({
        tone: "error",
        text: error.message || "AI 詳解產生失敗。",
      });
    }
  };

  const renderAssetSourcePreview = async (assetIndex, sourceCode) => {
    const code = String(sourceCode || "").trim();
    if (!code) {
      setAssetPreviewStatus("error");
      setAssetPreviewMessage("請先輸入 Python matplotlib 程式碼。");
      return false;
    }

    setAssetPreviewStatus("loading");
    setAssetPreviewMessage("正在送後端產生圖片...");
    updateQuestionAsset(assetIndex, {
      source_language: "python",
      source_code: code,
      url: "",
      mime_type: "",
      preview_status: "loading",
      preview_message: "後端圖片產生中",
      _preview_url: "",
      _preview_source_code: code,
    });
    const result = await getOrRenderMatplotlibPreview(
      code,
      previewMatplotlibCode,
    );

    if (result.success && result.image) {
      updateQuestionAsset(assetIndex, {
        source_language: "python",
        source_code: code,
        url: "",
        mime_type: "",
        preview_status: "success",
        preview_message: "",
        _preview_url: result.image,
        _preview_source_code: code,
      });
      setAssetPreviewStatus("success");
      setAssetPreviewMessage(
        "後端圖片預覽已產生；再按一次 Space + W 關閉編輯視窗。儲存時只會送 Python 程式碼，不會送預覽 URL。",
      );
      return true;
    }

    updateQuestionAsset(assetIndex, {
      preview_status: "error",
      preview_message:
        result.error || "後端圖片產生失敗，請檢查 Python 程式碼。",
      _preview_url: "",
      _preview_source_code: code,
    });
    setAssetPreviewStatus("error");
    setAssetPreviewMessage(result.error || "後端圖片產生失敗，請檢查程式碼。");
    return false;
  };

  const renderBulkAssetSourcePreview = async (bulkAssetSource, sourceCode) => {
    const code = String(sourceCode || "").trim();
    const session = bulkEditingSessionRef.current;
    if (session?.status !== "editing" ||
      session.question.localId !== bulkAssetSource?.localId) return false;
    if (!code) {
      setAssetPreviewStatus("error");
      setAssetPreviewMessage("請先輸入 Python matplotlib 程式碼。");
      return false;
    }

    setAssetPreviewStatus("loading");
    setAssetPreviewMessage("正在送後端產生圖片...");
    updateBulkQuestionAsset(
      bulkAssetSource.localId,
      bulkAssetSource.assetIndex,
      {
        source_language: "python",
        source_code: code,
        url: "",
        mime_type: "",
        preview_status: "loading",
        preview_message: "後端圖片產生中",
        _preview_url: "",
        _preview_source_code: code,
      },
    );
    const result = await getOrRenderMatplotlibPreview(
      code,
      previewMatplotlibCode,
    );

    // A late response must not restore discarded edits or overwrite newer code.
    const isPendingAsset = (asset) => asset?.preview_status === "loading" &&
      asset._preview_source_code === code && String(asset.source_code || "").trim() === code;
    if (session.status === "discarded" ||
      !isPendingAsset(session.question.form.assets[bulkAssetSource.assetIndex])) return false;
    const applyPreviewResult = (patch) => {
      if (session.status === "saved") {
        setBulkQuestions((current) => current.map((item) => {
          if (item.localId !== bulkAssetSource.localId ||
            !isPendingAsset(item.form.assets[bulkAssetSource.assetIndex])) return item;
          return {
            ...item,
            form: {
              ...item.form,
              assets: item.form.assets.map((asset, index) =>
                index === bulkAssetSource.assetIndex ? { ...asset, ...patch } : asset,
              ),
            },
          };
        }));
      } else if (bulkEditingSessionRef.current === session) {
        updateBulkQuestionAsset(bulkAssetSource.localId, bulkAssetSource.assetIndex, patch);
      }
    };
    const isStillEditing = bulkEditingSessionRef.current === session;

    if (result.success && result.image) {
      applyPreviewResult({
        source_language: "python",
        source_code: code,
        url: "",
        mime_type: "",
        preview_status: "success",
        preview_message: "",
        _preview_url: result.image,
        _preview_source_code: code,
      });
      if (isStillEditing) {
        setAssetPreviewStatus("success");
        setAssetPreviewMessage(
          "後端圖片預覽已產生；再按一次 Space + W 關閉編輯視窗。儲存時只會送 Python 程式碼，不會送預覽 URL。",
        );
      }
      return true;
    }

    applyPreviewResult({
      preview_status: "error",
      preview_message:
        result.error || "後端圖片產生失敗，請檢查 Python 程式碼。",
      _preview_url: "",
      _preview_source_code: code,
    });
    if (isStillEditing) {
      setAssetPreviewStatus("error");
      setAssetPreviewMessage(result.error || "後端圖片產生失敗，請檢查程式碼。");
    }
    return false;
  };

  const openQuestionEditor = (question) => {
    const questionId = getRecordId(question);
    const form = getQuestionForm(question);
    const requestId = questionEditPreviewRequestRef.current + 1;
    questionEditPreviewRequestRef.current = requestId;

    const pythonAssets = form.assets
      .map((asset, index) => ({
        index,
        sourceCode: String(asset.source_code || "").trim(),
        sourceLanguage: String(asset.source_language || "")
          .trim()
          .toLowerCase(),
        cachedImage:
          getCachedMatplotlibPreview(asset.source_code) ||
          (String(asset._preview_source_code || "").trim() ===
            String(asset.source_code || "").trim()
            ? String(asset._preview_url || "").trim()
            : ""),
      }))
      .filter((asset) => asset.sourceLanguage === "python" && asset.sourceCode);

    const formWithPreviewState = pythonAssets.length
      ? {
        ...form,
        assets: form.assets.map((asset, index) => {
          const pythonAsset = pythonAssets.find(
            (candidate) => candidate.index === index,
          );
          if (!pythonAsset) return asset;
          return pythonAsset.cachedImage
            ? {
                ...asset,
                preview_status: "success",
                preview_message: "",
                _preview_url: pythonAsset.cachedImage,
                _preview_source_code: pythonAsset.sourceCode,
              }
            : {
              ...asset,
              preview_status: "loading",
              preview_message: "後端圖片產生中",
            };
        }),
      }
      : form;

    rememberQuestionSearchPosition(questionId);
    setEditingQuestionId(questionId);
    setQuestionForm(formWithPreviewState);
    changeActiveTab("question-create");

    const pendingPythonAssets = pythonAssets.filter((asset) => !asset.cachedImage);
    if (!pendingPythonAssets.length) return;

    (async () => {
      let failedCount = 0;

      for (const asset of pendingPythonAssets) {
        const result = await getOrRenderMatplotlibPreview(
          asset.sourceCode,
          previewMatplotlibCode,
        );
        if (questionEditPreviewRequestRef.current !== requestId) return;

        if (result.success && result.image) {
          setQuestionForm((current) => ({
            ...current,
            assets: current.assets.map((currentAsset, currentIndex) =>
              currentIndex === asset.index &&
                String(currentAsset.source_code || "").trim() === asset.sourceCode
                ? {
                  ...currentAsset,
                  source_language: "python",
                  url: getPersistableAssetUrl(currentAsset),
                  mime_type: getPersistableAssetUrl(currentAsset)
                    ? currentAsset.mime_type
                    : "",
                  preview_status: "success",
                  preview_message: "",
                  _preview_url: result.image,
                  _preview_source_code: asset.sourceCode,
                }
                : currentAsset,
            ),
          }));
        } else {
          failedCount += 1;
          setQuestionForm((current) => ({
            ...current,
            assets: current.assets.map((currentAsset, currentIndex) =>
              currentIndex === asset.index &&
                String(currentAsset.source_code || "").trim() === asset.sourceCode
                ? {
                  ...currentAsset,
                  preview_status: "error",
                  preview_message:
                    result.error ||
                    "後端圖片產生失敗，請檢查 Python 程式碼。",
                  _preview_url: "",
                  _preview_source_code: asset.sourceCode,
                }
                : currentAsset,
            ),
          }));
        }
      }

      if (questionEditPreviewRequestRef.current !== requestId) return;
      if (failedCount) {
        showNotice({
          tone: "error",
          text: `有 ${failedCount} 張 Python 圖產生失敗，編輯內容仍已載入。`,
        });
      }
    })();
  };

  const openQuestionEditorRef = useRef(openQuestionEditor);
  openQuestionEditorRef.current = openQuestionEditor;
  useEffect(() => {
    if (!editRequest?.id || editRequest.subject !== subject || !isActive ||
        handledEditRequestRef.current === editRequest.token) return;
    handledEditRequestRef.current = editRequest.token;
    let cancelled = false;
    getQuestion({ authToken, id: editRequest.id })
      .then((question) => {
        if (!cancelled) openQuestionEditorRef.current(question);
      })
      .catch((error) => {
        if (!cancelled) setNotice({ tone: "error", text: error.message || "無法載入題目編輯資料" });
      })
      .finally(() => onEditRequestHandled?.(editRequest.token));
    return () => { cancelled = true; };
  }, [authToken, editRequest, getQuestion, isActive, onEditRequestHandled, subject]);

  const openMarkdownEditor = (field) => {
    setExpandedBulkMarkdownField(null);
    setExpandedBulkAssetSource(null);
    setExpandedAssetSourceIndex(null);
    setExpandedMarkdownField(field);
  };

  const openBulkMarkdownEditor = (localId, field) => {
    setExpandedMarkdownField(null);
    setExpandedAssetSourceIndex(null);
    setExpandedBulkAssetSource(null);
    setExpandedBulkMarkdownField({ localId, field });
  };

  const openBulkAssetSourceEditor = (localId, assetIndex, hasUrl) => {
    setExpandedMarkdownField(null);
    setExpandedAssetSourceIndex(null);
    setExpandedBulkMarkdownField(null);
    setExpandedBulkAssetSource({ localId, assetIndex });
    setAssetPreviewStatus("idle");
    resetAssetPreviewLeaderSubmission();
    setAssetPreviewMessage(
      hasUrl
        ? "目前顯示後端圖片；修改後會先顯示 SVG 動態預覽。"
        : "SVG 動態預覽會即時顯示，按 Space + W 送後端產生圖片。",
    );
  };

  const markdownEditorConfig = useMemo(() => {
    const editorFields = {
      prompt_md: {
        key: "question-prompt-markdown",
        title: "題目 Markdown / KaTeX",
        previewFallback: "尚未輸入題目",
      },
      answer_md: {
        key: "question-answer-markdown",
        title: "答案 Markdown / KaTeX",
        previewFallback: "尚未輸入答案",
      },
      solution_md: {
        key: "question-solution-markdown",
        title: "詳解 Markdown / KaTeX",
        previewFallback: "尚未輸入詳解",
      },
    };
    const editorField = editorFields[expandedMarkdownField];

    if (!editorField) return null;

    return {
      ...editorField,
      defaultVimMode: true,
      preview: true,
      value: questionForm[expandedMarkdownField],
      onChange: (value) =>
        setQuestionForm((prev) => ({
          ...prev,
          [expandedMarkdownField]: value,
        })),
    };
  }, [expandedMarkdownField, questionForm]);

  const assetEditorConfig = (() => {
    if (expandedAssetSourceIndex === null) return null;
    const asset = questionForm.assets[expandedAssetSourceIndex];
    if (!asset) return null;
    if (asset.source_language === "svg") return createSvgAssetEditor({
      key: `question-asset-svg-${expandedAssetSourceIndex}`, asset,
      onChange: (patch) => updateQuestionAsset(expandedAssetSourceIndex, patch),
      onClose: () => setExpandedAssetSourceIndex(null),
    });
    const sourceCode = asset.source_code || "";
    const previewImageUrl = getAssetDisplayImageUrl(asset);

    return {
      key: `question-asset-python-${expandedAssetSourceIndex}`,
      title: `Figure Code · ${getAssetRoleLabel(asset.role)}`,
      value: sourceCode,
      onChange: (value) => {
        setAssetPreviewStatus("idle");
        resetAssetPreviewLeaderSubmission();
        setAssetPreviewMessage(
          "SVG 動態預覽已更新，按 Space + W 送後端產生圖片。",
        );
        updateQuestionAsset(expandedAssetSourceIndex, {
          source_language: "python",
          source_code: value,
          url: "",
          mime_type: "",
          preview_status: "idle",
          preview_message: "",
          _preview_url: "",
          _preview_source_code: "",
        });
      },
      defaultVimMode: true,
      syntaxMode: "python",
      preview: true,
      previewType: "custom",
      previewTitle: "SVG 動態預覽",
      previewSource: sourceCode,
      previewContent: (
        <AssetCodePreviewPane>
          {assetPreviewMessage && (
            <AssetCodePreviewStatus $status={assetPreviewStatus}>
              {assetPreviewStatus === "loading" && <SpinningIcon />}
              <span>{assetPreviewMessage}</span>
            </AssetCodePreviewStatus>
          )}
          <AssetCodePreviewFrame>
            <MatplotlibQuickPreview
              badgeAction="Space + W 送後端"
              badgeTitle="SVG 動態"
              code={sourceCode}
              emptyTitle="SVG 動態預覽尚無可畫內容"
              image={previewImageUrl}
              pending={!previewImageUrl}
              loading={false}
              unsupportedLabel="送後端產生圖片"
              emptyMessage="支援 plot、scatter、text、Circle、Rectangle、Polygon、PathPatch 與 hand_line 手繪 helper。"
            />
          </AssetCodePreviewFrame>
        </AssetCodePreviewPane>
      ),
      onLeaderWrite: (currentCode) => {
        if (shouldCloseAssetPreviewEditorOnLeaderWrite()) {
          setExpandedAssetSourceIndex(null);
          return true;
        }
        renderAssetSourcePreview(expandedAssetSourceIndex, currentCode);
        return true;
      },
    };
  })();

  const bulkMarkdownEditorConfig = (() => {
    if (!expandedBulkMarkdownField) return null;
    const bulkQuestion = bulkEditingQuestion?.localId === expandedBulkMarkdownField.localId
      ? bulkEditingQuestion : null;
    if (!bulkQuestion) return null;

    const editorFields = {
      prompt_md: {
        key: `bulk-question-prompt-${bulkQuestion.localId}`,
        title:
          bulkQuestion.form.type === "choice"
            ? "題幹 Markdown / KaTeX"
            : "題目 Markdown / KaTeX",
        previewFallback: "尚未輸入題目",
      },
      answer_md: {
        key: `bulk-question-answer-${bulkQuestion.localId}`,
        title: "答案 Markdown / KaTeX",
        previewFallback: "尚未輸入答案",
      },
      solution_md: {
        key: `bulk-question-solution-${bulkQuestion.localId}`,
        title: "詳解 Markdown / KaTeX",
        previewFallback: "尚未輸入詳解",
      },
    };
    const editorField = editorFields[expandedBulkMarkdownField.field];
    if (!editorField) return null;

    return {
      ...editorField,
      defaultVimMode: true,
      preview: true,
      value: bulkQuestion.form[expandedBulkMarkdownField.field],
      onChange: (value) =>
        updateBulkQuestionForm(bulkQuestion.localId, {
          [expandedBulkMarkdownField.field]: value,
        }),
    };
  })();

  const bulkAssetEditorConfig = (() => {
    if (!expandedBulkAssetSource) return null;
    const bulkQuestion = bulkEditingQuestion?.localId === expandedBulkAssetSource.localId
      ? bulkEditingQuestion : null;
    const asset =
      bulkQuestion?.form.assets?.[expandedBulkAssetSource.assetIndex];
    if (!bulkQuestion || !asset) return null;
    if (asset.source_language === "svg") return createSvgAssetEditor({
      key: `bulk-asset-svg-${bulkQuestion.localId}-${expandedBulkAssetSource.assetIndex}`, asset,
      onChange: (patch) => updateBulkQuestionAsset(bulkQuestion.localId, expandedBulkAssetSource.assetIndex, patch),
      onClose: () => setExpandedBulkAssetSource(null),
    });
    const sourceCode = asset.source_code || "";
    const previewImageUrl = getAssetDisplayImageUrl(asset);

    return {
      key: `bulk-question-asset-python-${bulkQuestion.localId}-${expandedBulkAssetSource.assetIndex}`,
      title: `Figure Code · ${getAssetRoleLabel(asset.role)}`,
      value: sourceCode,
      onChange: (value) => {
        setAssetPreviewStatus("idle");
        resetAssetPreviewLeaderSubmission();
        setAssetPreviewMessage(
          "SVG 動態預覽已更新，按 Space + W 送後端產生圖片。",
        );
        updateBulkQuestionAsset(
          bulkQuestion.localId,
          expandedBulkAssetSource.assetIndex,
          {
            source_language: "python",
            source_code: value,
            url: "",
            mime_type: "",
            preview_status: "idle",
            preview_message: "",
            _preview_url: "",
            _preview_source_code: "",
          },
        );
      },
      defaultVimMode: true,
      syntaxMode: "python",
      preview: true,
      previewType: "custom",
      previewTitle: "SVG 動態預覽",
      previewSource: sourceCode,
      previewContent: (
        <AssetCodePreviewPane>
          {assetPreviewMessage && (
            <AssetCodePreviewStatus $status={assetPreviewStatus}>
              {assetPreviewStatus === "loading" && <SpinningIcon />}
              <span>{assetPreviewMessage}</span>
            </AssetCodePreviewStatus>
          )}
          <AssetCodePreviewFrame>
            <MatplotlibQuickPreview
              badgeAction="Space + W 送後端"
              badgeTitle="SVG 動態"
              code={sourceCode}
              emptyTitle="SVG 動態預覽尚無可畫內容"
              image={previewImageUrl}
              pending={!previewImageUrl}
              loading={false}
              unsupportedLabel="送後端產生圖片"
              emptyMessage="支援 plot、scatter、text、Circle、Rectangle、Polygon、PathPatch 與 hand_line 手繪 helper。"
            />
          </AssetCodePreviewFrame>
        </AssetCodePreviewPane>
      ),
      onLeaderWrite: (currentCode) => {
        if (shouldCloseAssetPreviewEditorOnLeaderWrite()) {
          setExpandedBulkAssetSource(null);
          return true;
        }
        renderBulkAssetSourcePreview(expandedBulkAssetSource, currentCode);
        return true;
      },
    };
  })();

  const expandedEditorConfig =
    bulkAssetEditorConfig ||
    assetEditorConfig ||
    bulkMarkdownEditorConfig ||
    markdownEditorConfig;

  const questionDuplicateSignature = useMemo(
    () => getQuestionDuplicateSignature(questionForm),
    [questionForm],
  );
  const apiImportGradeId = apiImportScope.grade_id || "請先選擇年級_UUID";
  const apiImportUnitId = apiImportScope.unit_id || "請先選擇單元_UUID";
  const apiImportVisibility = apiImportScope.visibility || "public";
  const apiImportQuestionSource = normalizeQuestionSourceValue(
    apiImportScope.question_source,
  );
  const apiImportStatus = apiImportScope.status || "draft";
  const apiImportAuthorLabel = staffAuthorLabel.trim();
  const apiImportJwtToken = authToken || "請先登入取得_JWT";

  useEffect(() => {
    if (
      duplicateQuestionWarning &&
      duplicateQuestionWarning.signature !== questionDuplicateSignature
    ) {
      setDuplicateQuestionWarning(null);
    }
  }, [duplicateQuestionWarning, questionDuplicateSignature]);

  const macSimpleImportCommand = useMemo(() => {
    const apiKey = staffApiKey.trim();
    return [
      `API_KEY="${apiKey}"`,
      `JWT_TOKEN="${apiImportJwtToken}"`,
      `AUTHOR_LABEL="${apiImportAuthorLabel}"`,
      `QUESTION_SOURCE="${apiImportQuestionSource}"`,
      'FILE="./math-bank-questions.json"',
      'JWT_API_URL="https://sunnytseng.com/api/math-bank/questions/"',
      'STAFF_API_URL="https://sunnytseng.com/api/math-bank/staff/questions/"',
      `GRADE_ID="${apiImportGradeId}"`,
      `UNIT_ID="${apiImportUnitId}"`,
      `VISIBILITY="${apiImportVisibility}"`,
      `STATUS="${apiImportStatus}"`,
      "FORCE_DUPLICATES=0",
      "",
      'case "$GRADE_ID:$UNIT_ID" in *請先選擇*) echo "請先在 UI 選擇年級與單元後再複製指令"; exit 1;; esac',
      'if [ -z "$API_KEY" ] && [ "$JWT_TOKEN" = "請先登入取得_JWT" ]; then',
      '  echo "請先登入，或填 Staff API Key 後再複製指令"; exit 1',
      "fi",
      'command -v jq >/dev/null || { echo "缺少 jq，請先安裝 jq"; exit 1; }',
      '[ -f "$FILE" ] || { echo "找不到檔案：$FILE"; exit 1; }',
      "jq -c '.questions[]' \"$FILE\" | while read -r body; do",
      '  body=$(printf \'%s\' "$body" | jq -c --arg grade "$GRADE_ID" --arg unit "$UNIT_ID" --arg visibility "$VISIBILITY" --arg source "$QUESTION_SOURCE" --arg status "$STATUS" \'. + {grade_id: $grade, unit_id: $unit, visibility: $visibility, question_source: $source, status: $status}\')',
      '  if [ -n "$AUTHOR_LABEL" ]; then',
      "    body=$(printf '%s' \"$body\" | jq -c --arg author \"$AUTHOR_LABEL\" '. + {author_label: $author}')",
      "  fi",
      '  if [ "$FORCE_DUPLICATES" = "1" ]; then',
      "    body=$(printf '%s' \"$body\" | jq -c '. + {duplicate_policy: \"allow\"}')",
      "  fi",
      "",
      '  if [ -n "$API_KEY" ]; then',
      "    response=$(curl -sS -w '\\n%{http_code}' -X POST \"$STAFF_API_URL\" \\",
      '      -H "Content-Type: application/json" \\',
      '      -H "X-API-KEY: $API_KEY" \\',
      '      -d "$body")',
      "  else",
      "    response=$(curl -sS -w '\\n%{http_code}' -X POST \"$JWT_API_URL\" \\",
      '      -H "Content-Type: application/json" \\',
      '      -H "Authorization: Bearer $JWT_TOKEN" \\',
      '      -d "$body")',
      "  fi",
      '  http_code=$(printf "%s" "$response" | tail -n 1)',
      '  response_body=$(printf "%s" "$response" | sed \'$d\')',
      "",
      '  if [ "$http_code" -ge 200 ] && [ "$http_code" -lt 300 ]; then',
      '    id=$(printf "%s" "$response_body" | jq -r ".id // empty" 2>/dev/null)',
      '    echo "OK $id"',
      "  else",
      "    duplicate_id=$(printf '%s' \"$response_body\" | jq -r '.duplicate_question_id[0] // .duplicate_question_id // empty' 2>/dev/null)",
      '    if [ -n "$duplicate_id" ]; then',
      '      echo "DUPLICATE $duplicate_id"',
      "    else",
      '      echo "FAIL HTTP $http_code"',
      '      printf "%s\\n" "$response_body"',
      "    fi",
      "  fi",
      "done",
    ].join("\n");
  }, [
    apiImportAuthorLabel,
    apiImportGradeId,
    apiImportJwtToken,
    apiImportQuestionSource,
    apiImportStatus,
    apiImportUnitId,
    apiImportVisibility,
    staffApiKey,
  ]);

  const macTimelineImportCommand = useMemo(() => {
    const apiKey = staffApiKey.trim();
    return [
      `API_KEY="${apiKey}"`,
      `JWT_TOKEN="${apiImportJwtToken}"`,
      `AUTHOR_LABEL="${apiImportAuthorLabel}"`,
      `QUESTION_SOURCE="${apiImportQuestionSource}"`,
      'FILE="./math-bank-questions.json"',
      'JWT_API_URL="https://sunnytseng.com/api/math-bank/questions/"',
      'STAFF_API_URL="https://sunnytseng.com/api/math-bank/staff/questions/"',
      `GRADE_ID="${apiImportGradeId}"`,
      `UNIT_ID="${apiImportUnitId}"`,
      `VISIBILITY="${apiImportVisibility}"`,
      `STATUS="${apiImportStatus}"`,
      "FORCE_DUPLICATES=0",
      "",
      'case "$GRADE_ID:$UNIT_ID" in *請先選擇*) echo "請先在 UI 選擇年級與單元後再複製指令"; exit 1;; esac',
      'if [ -z "$API_KEY" ] && [ "$JWT_TOKEN" = "請先登入取得_JWT" ]; then',
      '  echo "請先登入，或填 Staff API Key 後再複製指令"; exit 1',
      "fi",
      'command -v jq >/dev/null || { echo "缺少 jq，請先安裝 jq"; exit 1; }',
      '[ -f "$FILE" ] || { echo "找不到檔案：$FILE"; exit 1; }',
      "",
      'TOTAL=$(jq ".questions | length" "$FILE")',
      "OK=0",
      "SKIP=0",
      "FAIL=0",
      "START=$(date +%s)",
      'echo "開始匯入：$TOTAL 題"',
      "",
      "i=0",
      "while read -r body; do",
      "  i=$((i + 1))",
      '  body=$(printf \'%s\' "$body" | jq -c --arg grade "$GRADE_ID" --arg unit "$UNIT_ID" --arg visibility "$VISIBILITY" --arg source "$QUESTION_SOURCE" --arg status "$STATUS" \'. + {grade_id: $grade, unit_id: $unit, visibility: $visibility, question_source: $source, status: $status}\')',
      '  if [ -n "$AUTHOR_LABEL" ]; then',
      "    body=$(printf '%s' \"$body\" | jq -c --arg author \"$AUTHOR_LABEL\" '. + {author_label: $author}')",
      "  fi",
      '  if [ "$FORCE_DUPLICATES" = "1" ]; then',
      "    body=$(printf '%s' \"$body\" | jq -c '. + {duplicate_policy: \"allow\"}')",
      "  fi",
      '  title=$(printf \'%s\' "$body" | jq -r \'.prompt_md // "" | gsub("\\\\n"; " ") | .[0:36]\')',
      '  printf "[%s/%s] %s ... " "$i" "$TOTAL" "$title"',
      "",
      '  if [ -n "$API_KEY" ]; then',
      "    response=$(curl -sS -w '\\n%{http_code}' -X POST \"$STAFF_API_URL\" \\",
      '      -H "Content-Type: application/json" \\',
      '      -H "X-API-KEY: $API_KEY" \\',
      '      -d "$body")',
      "  else",
      "    response=$(curl -sS -w '\\n%{http_code}' -X POST \"$JWT_API_URL\" \\",
      '      -H "Content-Type: application/json" \\',
      '      -H "Authorization: Bearer $JWT_TOKEN" \\',
      '      -d "$body")',
      "  fi",
      '  http_code=$(printf "%s" "$response" | tail -n 1)',
      '  response_body=$(printf "%s" "$response" | sed \'$d\')',
      "",
      '  if [ "$http_code" -ge 200 ] && [ "$http_code" -lt 300 ]; then',
      '    id=$(printf "%s" "$response_body" | jq -r ".id // empty" 2>/dev/null)',
      '    echo "OK $id"',
      "    OK=$((OK + 1))",
      "  else",
      "    duplicate_id=$(printf '%s' \"$response_body\" | jq -r '.duplicate_question_id[0] // .duplicate_question_id // empty' 2>/dev/null)",
      '    if [ -n "$duplicate_id" ]; then',
      '      echo "DUPLICATE $duplicate_id"',
      "      SKIP=$((SKIP + 1))",
      "    else",
      '      echo "FAIL HTTP $http_code"',
      '      printf "%s\\n" "$response_body"',
      "      FAIL=$((FAIL + 1))",
      "    fi",
      "  fi",
      "done < <(jq -c '.questions[]' \"$FILE\")",
      "",
      "END=$(date +%s)",
      'echo "完成：成功 $OK / 重複略過 $SKIP / 失敗 $FAIL / 耗時 $((END - START)) 秒"',
    ].join("\n");
  }, [
    apiImportAuthorLabel,
    apiImportGradeId,
    apiImportJwtToken,
    apiImportQuestionSource,
    apiImportStatus,
    apiImportUnitId,
    apiImportVisibility,
    staffApiKey,
  ]);

  const windowsSimpleImportCommand = useMemo(() => {
    const apiKey = staffApiKey.trim();
    return [
      `$ApiKey = "${apiKey}"`,
      `$JwtToken = "${apiImportJwtToken}"`,
      `$AuthorLabel = "${apiImportAuthorLabel}"`,
      `$QuestionSource = "${apiImportQuestionSource}"`,
      '$File = ".\\math-bank-questions.json"',
      '$JwtApiUrl = "https://sunnytseng.com/api/math-bank/questions/"',
      '$StaffApiUrl = "https://sunnytseng.com/api/math-bank/staff/questions/"',
      `$GradeId = "${apiImportGradeId}"`,
      `$UnitId = "${apiImportUnitId}"`,
      `$Visibility = "${apiImportVisibility}"`,
      `$Status = "${apiImportStatus}"`,
      "$ForceDuplicates = $false",
      "",
      'if ($GradeId -like "請先選擇*" -or $UnitId -like "請先選擇*") { throw "請先在 UI 選擇年級與單元後再複製指令" }',
      'if (!$ApiKey -and $JwtToken -eq "請先登入取得_JWT") { throw "請先登入，或填 Staff API Key 後再複製指令" }',
      'if (!(Test-Path $File)) { throw "找不到檔案：$File" }',
      "$Data = Get-Content $File -Raw | ConvertFrom-Json",
      "foreach ($Question in $Data.questions) {",
      '  $Question | Add-Member -NotePropertyName "grade_id" -NotePropertyValue $GradeId -Force',
      '  $Question | Add-Member -NotePropertyName "unit_id" -NotePropertyValue $UnitId -Force',
      '  $Question | Add-Member -NotePropertyName "visibility" -NotePropertyValue $Visibility -Force',
      '  $Question | Add-Member -NotePropertyName "question_source" -NotePropertyValue $QuestionSource -Force',
      '  $Question | Add-Member -NotePropertyName "status" -NotePropertyValue $Status -Force',
      '  if ($AuthorLabel) { $Question | Add-Member -NotePropertyName "author_label" -NotePropertyValue $AuthorLabel -Force }',
      '  if ($ForceDuplicates) { $Question | Add-Member -NotePropertyName "duplicate_policy" -NotePropertyValue "allow" -Force }',
      "  $Body = $Question | ConvertTo-Json -Depth 20 -Compress",
      "  try {",
      '    $Headers = @{ "Content-Type" = "application/json" }',
      "    $Uri = $JwtApiUrl",
      "    if ($ApiKey) {",
      '      $Headers["X-API-KEY"] = $ApiKey',
      "      $Uri = $StaffApiUrl",
      "    } else {",
      '      $Headers["Authorization"] = "Bearer $JwtToken"',
      "    }",
      "    $Result = Invoke-RestMethod `",
      "      -Method Post `",
      "      -Uri $Uri `",
      "      -Headers $Headers `",
      "      -Body $Body",
      '    Write-Host "OK $($Result.id)"',
      "  } catch {",
      "    $ErrorJson = $_.ErrorDetails.Message | ConvertFrom-Json -ErrorAction SilentlyContinue",
      "    $DuplicateId = @($ErrorJson.duplicate_question_id)[0]",
      "    if ($DuplicateId) {",
      '      Write-Host "DUPLICATE $DuplicateId"',
      "    } else {",
      '      Write-Host "FAIL $($_.Exception.Message)"',
      "    }",
      "  }",
      "}",
    ].join("\n");
  }, [
    apiImportAuthorLabel,
    apiImportGradeId,
    apiImportJwtToken,
    apiImportQuestionSource,
    apiImportStatus,
    apiImportUnitId,
    apiImportVisibility,
    staffApiKey,
  ]);

  const windowsTimelineImportCommand = useMemo(() => {
    const apiKey = staffApiKey.trim();
    return [
      `$ApiKey = "${apiKey}"`,
      `$JwtToken = "${apiImportJwtToken}"`,
      `$AuthorLabel = "${apiImportAuthorLabel}"`,
      `$QuestionSource = "${apiImportQuestionSource}"`,
      '$File = ".\\math-bank-questions.json"',
      '$JwtApiUrl = "https://sunnytseng.com/api/math-bank/questions/"',
      '$StaffApiUrl = "https://sunnytseng.com/api/math-bank/staff/questions/"',
      `$GradeId = "${apiImportGradeId}"`,
      `$UnitId = "${apiImportUnitId}"`,
      `$Visibility = "${apiImportVisibility}"`,
      `$Status = "${apiImportStatus}"`,
      "$ForceDuplicates = $false",
      "",
      'if ($GradeId -like "請先選擇*" -or $UnitId -like "請先選擇*") { throw "請先在 UI 選擇年級與單元後再複製指令" }',
      'if (!$ApiKey -and $JwtToken -eq "請先登入取得_JWT") { throw "請先登入，或填 Staff API Key 後再複製指令" }',
      'if (!(Test-Path $File)) { throw "找不到檔案：$File" }',
      "$Data = Get-Content $File -Raw | ConvertFrom-Json",
      "$Questions = @($Data.questions)",
      "$Total = $Questions.Count",
      "$Ok = 0",
      "$Skip = 0",
      "$Fail = 0",
      "$Index = 0",
      "$Start = Get-Date",
      'Write-Host "開始匯入：$Total 題"',
      "",
      "foreach ($Question in $Questions) {",
      "  $Index++",
      '  $Question | Add-Member -NotePropertyName "grade_id" -NotePropertyValue $GradeId -Force',
      '  $Question | Add-Member -NotePropertyName "unit_id" -NotePropertyValue $UnitId -Force',
      '  $Question | Add-Member -NotePropertyName "visibility" -NotePropertyValue $Visibility -Force',
      '  $Question | Add-Member -NotePropertyName "question_source" -NotePropertyValue $QuestionSource -Force',
      '  $Question | Add-Member -NotePropertyName "status" -NotePropertyValue $Status -Force',
      '  if ($AuthorLabel) { $Question | Add-Member -NotePropertyName "author_label" -NotePropertyValue $AuthorLabel -Force }',
      '  if ($ForceDuplicates) { $Question | Add-Member -NotePropertyName "duplicate_policy" -NotePropertyValue "allow" -Force }',
      '  $Title = (($Question.prompt_md -replace "\\s+", " ").Trim())',
      "  if ($Title.Length -gt 36) { $Title = $Title.Substring(0, 36) }",
      '  Write-Host "[$Index/$Total] $Title ... " -NoNewline',
      "  $Body = $Question | ConvertTo-Json -Depth 20 -Compress",
      "  try {",
      '    $Headers = @{ "Content-Type" = "application/json" }',
      "    $Uri = $JwtApiUrl",
      "    if ($ApiKey) {",
      '      $Headers["X-API-KEY"] = $ApiKey',
      "      $Uri = $StaffApiUrl",
      "    } else {",
      '      $Headers["Authorization"] = "Bearer $JwtToken"',
      "    }",
      "    $Result = Invoke-RestMethod `",
      "      -Method Post `",
      "      -Uri $Uri `",
      "      -Headers $Headers `",
      "      -Body $Body",
      '    Write-Host "OK $($Result.id)"',
      "    $Ok++",
      "  } catch {",
      "    $ErrorJson = $_.ErrorDetails.Message | ConvertFrom-Json -ErrorAction SilentlyContinue",
      "    $DuplicateId = @($ErrorJson.duplicate_question_id)[0]",
      "    if ($DuplicateId) {",
      '      Write-Host "DUPLICATE $DuplicateId"',
      "      $Skip++",
      "    } else {",
      '      Write-Host "FAIL"',
      "      Write-Host $_.Exception.Message",
      "      $Fail++",
      "    }",
      "  }",
      "}",
      "",
      "$Elapsed = [int]((Get-Date) - $Start).TotalSeconds",
      'Write-Host "完成：成功 $Ok / 重複略過 $Skip / 失敗 $Fail / 耗時 $Elapsed 秒"',
    ].join("\n");
  }, [
    apiImportAuthorLabel,
    apiImportGradeId,
    apiImportJwtToken,
    apiImportQuestionSource,
    apiImportStatus,
    apiImportUnitId,
    apiImportVisibility,
    staffApiKey,
  ]);

  const handleSaveGrade = async (event) => {
    event.preventDefault();
    if (!gradeForm.name.trim()) {
      setNotice({ tone: "error", text: "請輸入年級名稱" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: gradeForm.name.trim(),
        order_index: toInt(gradeForm.order_index, 0),
        is_active: Boolean(gradeForm.is_active),
      };

      if (editingGradeId) {
        await updateGrade({ authToken, id: editingGradeId, data: payload });
        setSuccess("年級已更新");
      } else {
        await createGrade({ authToken, data: payload });
        setSuccess("年級已新增");
      }

      resetGradeForm();
      await loadData({ preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "年級儲存失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUnit = async (event) => {
    event.preventDefault();
    if (!unitForm.grade_id || !unitForm.name.trim()) {
      setNotice({ tone: "error", text: "請選擇年級並輸入單元名稱" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        grade_id: unitForm.grade_id,
        parent_id: unitForm.parent_id || null,
        name: unitForm.name.trim(),
        order_index: toInt(unitForm.order_index, 0),
        is_active: Boolean(unitForm.is_active),
      };

      if (editingUnitId) {
        await updateUnit({ authToken, id: editingUnitId, data: payload });
        setSuccess("單元已更新");
      } else {
        await createUnit({ authToken, data: payload });
        setSuccess("單元已新增");
      }

      resetUnitForm();
      await loadData({ preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "單元儲存失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSource = async (event) => {
    event.preventDefault();
    if (!sourceForm.name.trim()) {
      setNotice({ tone: "error", text: "請輸入來源名稱" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: sourceForm.name.trim(),
        order_index: toInt(sourceForm.order_index, 0),
        is_active: Boolean(sourceForm.is_active),
      };

      if (editingSourceId) {
        await updateSource({ authToken, id: editingSourceId, data: payload });
        setSuccess("來源已更新");
      } else {
        await createSource({ authToken, data: payload });
        setSuccess("來源已新增");
      }

      resetSourceForm();
      await loadData({ preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "來源儲存失敗" });
    } finally {
      setSaving(false);
    }
  };

  const saveQuestion = async ({ allowDuplicate = false } = {}) => {
    const missingFields = getQuestionMissingFields(questionForm);

    if (missingFields.length) {
      setNotice({
        tone: "error",
        text: `請補齊：${missingFields.join("、")}`,
      });
      scrollToQuestionFormError(getFirstMissingQuestionFieldKey(questionForm));
      return;
    }

    const isEditingQuestion = Boolean(editingQuestionId);
    const questionIdClipboardWrite = isEditingQuestion
      ? null
      : beginQuestionIdClipboardWrite();
    let resolvedQuestionForm = questionForm;

    setSaving(true);
    try {
      const gradeId = await ensureQuestionGrade({
        subject,
        authToken,
        form: questionForm,
        grades,
        setGrades,
      });
      const unitsInGrade = units.filter(
        (unit) => getUnitGradeId(unit) === gradeId,
      );
      const unitId = await ensureQuestionUnit({
        subject,
        authToken,
        form: questionForm,
        gradeId,
        unitsInGrade,
        setUnits,
      });
      resolvedQuestionForm = {
        ...questionForm,
        grade_id: gradeId,
        new_grade_name: "",
        unit_id: unitId,
        new_unit_name: "",
      };
      const payload = {
        ...toQuestionPayload(resolvedQuestionForm),
        ...(allowDuplicate ? { duplicate_policy: "allow" } : {}),
      };
      rememberQuestionSource(payload.question_source);

      if (isEditingQuestion) {
        await updateQuestion({
          authToken,
          id: editingQuestionId,
          data: payload,
        });
        const latestQuestion = await getQuestion({
          authToken,
          id: editingQuestionId,
        });
        mergeQuestionIntoCurrentResults(latestQuestion);
        setSuccess("題目已更新");
      } else {
        const createdQuestion = await createQuestion({
          authToken,
          data: payload,
        });
        const createdQuestionId = createdQuestion?.id || createdQuestion?.uuid;

        if (createdQuestionId) {
          const copiedEarly = await questionIdClipboardWrite?.complete(createdQuestionId);
          const copied = copiedEarly || await copyQuestionId(
            createdQuestionId,
            "題目已新增，UUID 已複製",
            null,
          );
          if (copiedEarly) setSuccess("題目已新增，UUID 已複製");
          if (!copied) {
            showNotice({
              tone: "success",
              text: `題目已新增。UUID：${createdQuestionId}`,
              copyId: createdQuestionId,
            });
          }
        } else {
          questionIdClipboardWrite?.complete(null);
          setSuccess("題目已新增");
        }
      }

      setDuplicateQuestionWarning(null);
      resetQuestionForm(
        isEditingQuestion
          ? null
          : {
            grade_id: resolvedQuestionForm.grade_id,
            unit_id: resolvedQuestionForm.unit_id,
            question_source: payload.question_source,
          },
      );
      if (isEditingQuestion) {
        skipNextQuestionSearchRefreshRef.current = true;
        changeActiveTab("question-search");
        restoreQuestionSearchPosition();
      } else {
        questionFormTopRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        await loadQuestionResults({ reset: true, preserveNotice: true });
      }
    } catch (error) {
      questionIdClipboardWrite?.complete(null);
      const duplicateError = getDuplicateError(error);
      if (duplicateError && !allowDuplicate) {
        setQuestionForm(resolvedQuestionForm);
        setDuplicateQuestionWarning({
          ...duplicateError,
          signature: getQuestionDuplicateSignature(resolvedQuestionForm),
        });
        setNotice(null);
        return;
      }

      setNotice({ tone: "error", text: error.message || "題目儲存失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuestion = async (event) => {
    event.preventDefault();
    await saveQuestion();
  };

  const confirmDelete = async (title) => {
    const result = await Swal.fire({
      title,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#c0392b",
      cancelButtonColor: "#6b7280",
    });

    return result.isConfirmed;
  };

  const handleDeleteGrade = async (grade) => {
    if (!(await confirmDelete(`刪除年級「${grade.name}」？`))) return;

    setSaving(true);
    try {
      await deleteGrade({ authToken, id: getRecordId(grade) });
      resetGradeForm();
      setSuccess("年級已刪除");
      await loadData();
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "年級刪除失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnit = async (unit) => {
    if (!(await confirmDelete(`刪除單元「${unit.name}」？`))) return;

    setSaving(true);
    try {
      await deleteUnit({ authToken, id: getRecordId(unit) });
      resetUnitForm();
      setSuccess("單元已刪除");
      await loadData();
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "單元刪除失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async (source) => {
    if (!(await confirmDelete(`刪除來源「${source.name}」？`))) return;

    setSaving(true);
    try {
      await deleteSource({ authToken, id: getRecordId(source) });
      resetSourceForm();
      setSuccess("來源已從列表刪除，既有題目的來源文字不受影響");
      await loadData({ preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "來源刪除失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (question, { refreshDuplicates = false } = {}) => {
    if (!(await confirmDelete("刪除這道題目？"))) return;

    setSaving(true);
    try {
      await deleteQuestion({ authToken, id: getRecordId(question) });
      resetQuestionForm();
      setSelectedQuestionIds((current) =>
        current.filter((id) => id !== getRecordId(question)),
      );
      setSuccess("題目已刪除");
      if (refreshDuplicates) {
        cancelQuestionPreview();
        setPreviewQuestion(null);
        setDuplicateScanResult(null);
        await scanDuplicateQuestions();
      }
      await loadQuestionResults({ reset: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "題目刪除失敗" });
    } finally {
      setSaving(false);
    }
  };

  const toggleQuestionSelection = (id) => {
    setSelectedQuestionIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  const toggleVisibleQuestionSelection = () => {
    setSelectedQuestionIds((current) => {
      if (allVisibleQuestionsSelected) {
        return current.filter((id) => !visibleQuestionIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleQuestionIds]));
    });
  };

  const handleBulkDeleteQuestions = async () => {
    const ids = selectedVisibleQuestionIds;

    if (ids.length === 0) {
      setNotice({ tone: "error", text: "請先勾選要刪除的題目" });
      return;
    }

    if (!(await confirmDelete(`刪除已勾選的 ${ids.length} 道題目？`))) return;

    setSaving(true);
    try {
      const result = await deleteQuestions({ authToken, ids });
      setSelectedQuestionIds([]);
      resetQuestionForm();
      setSuccess(`已刪除 ${result?.deleted_count ?? ids.length} 道題目`);
      await loadQuestionResults({ reset: true, preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "批次刪除失敗" });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpdateQuestions = async (patch, label) => {
    const ids = selectedVisibleQuestionIds;

    if (ids.length === 0) {
      setNotice({ tone: "error", text: "請先勾選要更新的題目" });
      return;
    }

    const result = await Swal.fire({
      title: `將已勾選的 ${ids.length} 道題目改為${label}？`,
      icon: "question",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "套用",
      cancelButtonText: "取消",
      confirmButtonColor: "#202124",
      cancelButtonColor: "#6b7280",
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      const updateResult = await updateQuestions({
        authToken,
        ids,
        data: patch,
      });
      const updatedCount = updateResult?.updated_count ?? ids.length;
      setSuccess(`已更新 ${updatedCount} / ${ids.length} 道題目`);
      await loadQuestionResults({ reset: true, preserveNotice: true });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "批次更新失敗" });
    } finally {
      setSaving(false);
    }
  };

  const updateQuestionAsset = (index, patch) => {
    setQuestionForm((prev) => ({
      ...prev,
      assets: prev.assets.map((asset, assetIndex) =>
        assetIndex === index ? { ...asset, ...patch } : asset,
      ),
    }));
  };

  const addQuestionAsset = () => {
    setQuestionForm((prev) => ({
      ...prev,
      assets: [
        ...prev.assets,
        {
          role: "prompt",
          url: "",
          storage_key: "",
          alt_text: "",
          mime_type: "",
          source_language: "python",
          source_code: "",
          marking_guide: "",
          sort_order: prev.assets.length,
        },
      ],
    }));
  };

  const removeQuestionAsset = (index) => {
    setQuestionForm((prev) => ({
      ...prev,
      assets: prev.assets.filter((_, assetIndex) => assetIndex !== index),
    }));
  };

  const selectGradeForQuestion = (gradeId) => {
    setQuestionForm((prev) => ({
      ...prev,
      grade_id: gradeId,
      new_grade_name: gradeId === newGradeValue ? prev.new_grade_name : "",
      unit_id: units.some(
        (unit) =>
          getRecordId(unit) === prev.unit_id &&
          getUnitGradeId(unit) === gradeId,
      )
        ? prev.unit_id
        : gradeId === newGradeValue
          ? newUnitValue
          : "",
      new_unit_name: gradeId === newGradeValue ? prev.new_unit_name : "",
    }));
  };

  const selectGradeForFilter = (gradeId) => {
    setFilters((prev) => ({
      ...prev, grade_id: gradeId,
      unit_id: reconcileUnitFilter(prev.unit_id, gradeId, units, getUnitGradeId),
    }));
  };

  const selectGradeForDuplicateScan = (gradeId) => {
    updateDuplicateScanFilters((current) => ({
      ...current, grade_id: gradeId,
      unit_id: reconcileUnitFilter(current.unit_id, gradeId, units, getUnitGradeId, ""),
    }));
  };

  const cancelDuplicateScanPolling = useCallback(() => {
    duplicateScanGenerationRef.current += 1;
    duplicateResultsGenerationRef.current += 1;
    const waiting = duplicateScanWaitRef.current;
    if (waiting) {
      window.clearTimeout(waiting.timer);
      waiting.resolve();
      duplicateScanWaitRef.current = null;
    }
  }, []);

  const updateDuplicateScanFilters = (update) => {
    cancelDuplicateScanPolling();
    setDuplicateScanFilters(update);
    setDuplicateScanResult(null);
    setDuplicateScanJob(null);
    setDuplicateResultsLoading(false);
    setDuplicateGroupLoading(null);
    setDuplicateScanConnectionLost(false);
  };

  useEffect(() => {
    cancelDuplicateScanPolling();
    setDuplicateScanLoading(false);
    setDuplicateScanJob(null);
    setDuplicateResultsLoading(false);
    setDuplicateGroupLoading(null);
    return cancelDuplicateScanPolling;
  }, [authToken, cancelDuplicateScanPolling, isActive, routeActive, subject]);

  const loadDuplicateResults = useCallback(async (id, reviewStatus, page = 1, search = "") => {
    const generation = ++duplicateResultsGenerationRef.current;
    setDuplicateResultsLoading(true);
    try {
      const result = await getDuplicateQuestionScanResults({
        authToken, id, params: { review_status: reviewStatus, page, page_size: 20, search },
      });
      if (generation !== duplicateResultsGenerationRef.current) return false;
      setDuplicateReviewStatus(reviewStatus);
      setDuplicateScanResult(result);
      setNotice((current) => current?.tone === "error" ? null : current);
      return true;
    } catch (error) {
      if (generation === duplicateResultsGenerationRef.current) {
        setNotice({ tone: "error", text: error.message || "掃描結果載入失敗，請重試載入結果；不需要重新掃描。" });
      }
      return false;
    } finally {
      if (generation === duplicateResultsGenerationRef.current) setDuplicateResultsLoading(false);
    }
  }, [authToken, getDuplicateQuestionScanResults]);

  const changeDuplicateReviewStatus = (reviewStatus) => {
    if (duplicateScanResult?.schema_version === 2) {
      loadDuplicateResults(duplicateScanResult.scan_job_id, reviewStatus, 1, duplicateScanSearch);
    } else {
      scanDuplicateQuestions(reviewStatus);
    }
  };

  const loadDuplicateGroup = async (group) => {
    const generation = duplicateScanGenerationRef.current;
    setDuplicateGroupLoading(group.group_key);
    try {
      const details = await getDuplicateQuestionScanGroup({
        authToken, id: duplicateScanResult.scan_job_id, groupKey: group.group_key,
        params: { page: (group.questions_page || 0) + 1, page_size: 20 },
      });
      if (generation !== duplicateScanGenerationRef.current) return;
      setDuplicateScanResult((current) => ({ ...current, groups: current.groups.map((candidate) => {
        if (candidate.group_key !== group.group_key) return candidate;
        const questions = group.questions_page ? [...candidate.questions, ...details.questions] : details.questions;
        return { ...candidate, ...details, questions };
      }) }));
    } catch (error) {
      if (generation === duplicateScanGenerationRef.current) setNotice({ tone: "error", text: error.message || "群組題目載入失敗" });
    } finally {
      if (generation === duplicateScanGenerationRef.current) setDuplicateGroupLoading(null);
    }
  };

  useEffect(() => {
    if (!isActive || !routeActive || duplicateScanResult?.schema_version !== 2 || duplicateScanLoading) return undefined;
    if (duplicateSearchRef.current === duplicateScanSearch) return undefined;
    duplicateSearchRef.current = duplicateScanSearch;
    const timer = window.setTimeout(() => {
      loadDuplicateResults(duplicateScanResult.scan_job_id, duplicateReviewStatus, 1, duplicateScanSearch);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [duplicateScanSearch, duplicateReviewStatus, duplicateScanResult?.scan_job_id,
      duplicateScanResult?.schema_version, duplicateScanLoading, isActive, routeActive, loadDuplicateResults]);

  const scanDuplicateQuestions = useCallback(async (reviewStatus = duplicateReviewStatus) => {
    cancelDuplicateScanPolling();
    duplicateSearchRef.current = duplicateScanSearch;
    const generation = duplicateScanGenerationRef.current;
    const startedAt = Date.now();
    const isCurrent = () => generation === duplicateScanGenerationRef.current;
    setDuplicateScanLoading(true);
    setDuplicateResultsLoading(false);
    setDuplicateScanConnectionLost(false);
    setDuplicateScanJob({ status: "queued", phase: "queued", progress: 0, elapsed_seconds: 0 });
    setNotice(null);
    let requestStage = "建立掃描工作";
    try {
      let job = await startDuplicateQuestionScan({
        authToken,
        data: {
          ...duplicateScanFilters,
          limit: 500,
          review_status: reviewStatus,
        },
      });
      let connectionFailures = 0;
      while (isCurrent()) {
        setDuplicateScanJob({ ...job, elapsed_seconds: Math.floor((Date.now() - startedAt) / 1000) });
        if (job.status === "succeeded") {
          if (!job.result) throw new Error("掃描工作沒有回傳結果，請重新掃描。");
          if (job.results_paginated) {
            setDuplicateScanResult({ ...job.result, groups: [], results: [] });
            return await loadDuplicateResults(job.id, reviewStatus, 1, duplicateScanSearch);
          }
          setDuplicateReviewStatus(reviewStatus);
          setDuplicateScanResult(job.result);
          return true;
        }
        if (job.status === "failed") throw new Error(job.error || "重複題目掃描失敗，請重新掃描。");
        if (!job.id) throw new Error("無法取得掃描工作編號，請重新掃描。");
        const pollDelay = DUPLICATE_SCAN_POLL_INTERVAL_MS * Math.min(8, 2 ** connectionFailures);
        await new Promise((resolve) => {
          const timer = window.setTimeout(() => {
            duplicateScanWaitRef.current = null;
            resolve();
          }, pollDelay);
          duplicateScanWaitRef.current = { timer, resolve };
        });
        if (!isCurrent()) return false;
        try {
          requestStage = "查詢掃描進度";
          const updatedJob = await getDuplicateQuestionScan({ authToken, id: job.id });
          if (!isCurrent()) return false;
          job = updatedJob;
          connectionFailures = 0;
          setDuplicateScanConnectionLost(false);
        } catch (error) {
          if (!isCurrent()) return false;
          connectionFailures += 1;
          if (connectionFailures >= 10 || (error.status && ![502, 503, 504].includes(error.status))) throw error;
          setDuplicateScanConnectionLost(true);
        }
      }
      return false;
    } catch (error) {
      if (isCurrent()) {
        setDuplicateScanJob((current) => ({ ...current, status: "failed" }));
        setNotice({ tone: "error", text: [502, 503, 504].includes(error.status)
          ? `${requestStage}：${duplicateScanErrorMessage(error)}` : duplicateScanErrorMessage(error) });
      }
      return false;
    } finally {
      if (isCurrent()) setDuplicateScanLoading(false);
    }
  }, [authToken, cancelDuplicateScanPolling, duplicateReviewStatus, duplicateScanFilters, duplicateScanSearch, getDuplicateQuestionScan, startDuplicateQuestionScan, loadDuplicateResults]);

  useEffect(() => {
    const visible = isActive && routeActive && activeTab === "duplicate-scan";
    const becameVisible = visible && !duplicateScanVisibleRef.current;
    duplicateScanVisibleRef.current = visible;
    if (becameVisible && duplicateScanResult && !duplicateReviewSavingKey && !duplicateScanLoading) {
      scanDuplicateQuestions();
    }
  }, [activeTab, duplicateReviewSavingKey, duplicateScanLoading, duplicateScanResult, isActive, routeActive, scanDuplicateQuestions]);

  const saveDuplicateReview = async (group, reviewStatus) => {
    setDuplicateReviewSavingKey(group.group_key);
    setNotice(null);
    try {
      await reviewDuplicateQuestions({
        authToken,
        data: {
          ...(duplicateScanResult?.schema_version === 2
            ? { group_key: group.group_key }
            : { question_ids: group.questions.map((question) => question.id) }),
          signature: group.signature,
          review_status: reviewStatus,
          ...(duplicateScanResult?.scan_job_id ? { scan_job_id: duplicateScanResult.scan_job_id } : {}),
          ...(duplicateScanResult?.scan_scope ? { scan_scope: duplicateScanResult.scan_scope } : {}),
        },
      });
      setDuplicateScanResult((current) => {
        if (!current) return current;
        const handledDelta = reviewStatus === "handled" ? 1 : -1;
        return {
          ...current,
          groups: getDuplicateGroups(current).filter(
            (candidate) => candidate.group_key !== group.group_key,
          ),
          pending_count: Math.max(0, (current.pending_count || 0) - handledDelta),
          handled_count: Math.max(0, (current.handled_count || 0) + handledDelta),
        };
      });
      setNotice({
        tone: "success",
        text: reviewStatus === "handled"
          ? "整組題目已移至已處理紀錄，可從紀錄還原。"
          : "整組題目已回到待處理。",
      });
      if (duplicateScanResult?.schema_version === 2) {
        await loadDuplicateResults(duplicateScanResult.scan_job_id, duplicateReviewStatus, duplicateScanResult.page, duplicateScanSearch);
      }
    } catch (error) {
      if (error.status === 409) {
        setDuplicateScanResult(null);
        const refreshed = await scanDuplicateQuestions();
        if (refreshed) setNotice({
          tone: "error",
          text: "題庫已有新增或變更，已重新掃描。請檢查最新整組題目後再處理。",
        });
      } else {
        setNotice({
          tone: "error",
          text: error.message || "處理紀錄儲存失敗，請稍後再試。",
        });
      }
    } finally {
      setDuplicateReviewSavingKey(null);
    }
  };

  const updateQuestionFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isSavingQuestion = saving && activeTab === "question-create";
  const duplicateScanUnits = units.filter(
    (unit) =>
      filterMatches(duplicateScanFilters.grade_id, getUnitGradeId(unit)),
  );
  const hasDuplicateScanScope = Object.values(duplicateScanFilters).some((value) => {
    const selected = filterParam(value);
    return Array.isArray(selected) ? selected.length > 0 : Boolean(selected);
  });
  const visibleDuplicateGroups = getDuplicateGroups(duplicateScanResult).filter(
    (group) => {
      if ((group.review_status || "pending") !== duplicateReviewStatus) return false;
      if (duplicateScanResult?.schema_version === 2) return true;
      const keyword = normalizeSearchText(duplicateScanSearch);
      if (!keyword) return true;
      return group.questions.some((question) =>
        normalizeSearchText(
          [
            question.id,
            question.prompt_md,
            question.answer_md,
            question.solution_md,
            question.question_source,
            question.grade?.name,
            question.unit?.name,
          ].join(" "),
        ).includes(keyword),
      );
    },
  );
  const questionSaveLabel = editingQuestionId
    ? isSavingQuestion
      ? "更新中..."
      : "更新題目"
    : isSavingQuestion
      ? "新增中..."
      : "新增題目";
  const activeBulkEditingId = bulkEditingQuestion?.localId || "";
  const isExpandedEditorOpen = Boolean(expandedEditorConfig);

  useEffect(() => {
    if (activeBulkEditingId) bulkModalRef.current?.focus();
  }, [activeBulkEditingId]);

  useEffect(() => {
    if (!routeActive || !isActive || !activeBulkEditingId) return undefined;

    const closeOnEnter = (event) => {
      if (
        event.key !== "Enter" ||
        event.defaultPrevented ||
        event.isComposing ||
        event.keyCode === 229 ||
        isExpandedEditorOpen ||
        event.shiftKey || event.ctrlKey || event.altKey || event.metaKey ||
        (event.target instanceof Element && event.target.closest(
          "select, button, [contenteditable], [role='combobox']",
        ))
      ) return;
      event.preventDefault();
      event.stopPropagation();
      finishBulkQuestionEditor();
    };

    window.addEventListener("keydown", closeOnEnter, true);
    return () => window.removeEventListener("keydown", closeOnEnter, true);
  }, [activeBulkEditingId, isExpandedEditorOpen, finishBulkQuestionEditor, routeActive, isActive]);
  const selectedBulkCount = bulkQuestions.filter(
    (item) => item.selected,
  ).length;
  const bulkMissingCount = bulkQuestions.filter(
    (item) => getQuestionMissingFields(item.form).length,
  ).length;
  const bulkEditedCount = bulkQuestions.filter((item) => item.edited).length;
  const bulkCreatedQuestionCount = bulkQuestions.filter(
    (item) => item.createdId,
  ).length;
  const bulkReadyCount = bulkQuestions.filter(
    (item) =>
      item.selected &&
      item.importStatus !== "success" &&
      getQuestionMissingFields(item.form).length === 0,
  ).length;
  const bulkFlagSummary = bulkQuestions.reduce(
    (summary, item) => {
      const flags = detectBulkQuestionFlags(item.form);
      return {
        needsReview:
          summary.needsReview + (flags.includes("公式/圖片待辨識") ? 1 : 0),
        imageHints: summary.imageHints + (flags.includes("含圖片線索") ? 1 : 0),
        formulas: summary.formulas + (flags.includes("含公式") ? 1 : 0),
      };
    },
    { needsReview: 0, imageHints: 0, formulas: 0 },
  );
  const bulkStatusCounts = bulkQuestions.reduce(
    (counts, item) => ({
      ...counts,
      [item.importStatus]: (counts[item.importStatus] || 0) + 1,
    }),
    {},
  );

  return (
    <PageShell ref={pageShellRef}>
      <Header>
        <TitleGroup>
          <Eyebrow>Question Bank · {subjectLabel}</Eyebrow>
          <PageTitle>題庫後台</PageTitle>
        </TitleGroup>
        <HeaderActions>
          <CountText>
            {shouldLoadQuestionResults
              ? questionTotal != null
                ? `共 ${questionTotal.toLocaleString()} 題 · 已載入 ${displayQuestions.length.toLocaleString()} 題`
                : `${displayQuestions.length}${hasMoreQuestions ? "+" : ""} 題`
              : "尚未篩選"}
          </CountText>
          <IconTextButton
            type="button"
            onClick={() => {
              resetQuestionForm();
              loadData();
              if (shouldLoadQuestionResults) {
                loadQuestionResults({ reset: true });
              } else {
                clearQuestionState();
              }
            }}
            disabled={
              loading || questionLoading || loadingMoreQuestions || saving
            }
          >
            <FaSyncAlt />
            重新整理
          </IconTextButton>
        </HeaderActions>
      </Header>

      {importAnalysisBatch && !importFamilyAnalysis.open && <IconTextButton type="button" onClick={() => setImportFamilyAnalysis((current) => ({ ...current, open: true }))}><FaLayerGroup />繼續入題後題族檢查</IconTextButton>}
      {importAnalysisBatch && importFamilyAnalysis.open && isActive && routeActive && <BulkImportPanel aria-label="入題後題族檢查">
        <PanelHeader><div><PanelTitle>入題後題族檢查 · 第 {importFamilyAnalysis.index + 1} / {importFamilyAnalysis.batches.length} 批</PanelTitle><small>本批 {importAnalysisBatch.questionIds.length} 題；每批最多 100 題，依序分析。題目已入庫，分類建議需另行確認。</small></div>
          {importFamilyAnalysis.index + 1 < importFamilyAnalysis.batches.length && <IconTextButton type="button" disabled={["starting", "queued", "running"].includes(importAnalysisBatch.status)} onClick={() => setImportFamilyAnalysis((current) => ({ ...current, index: current.index + 1 }))}>下一批分析<FaChevronRight /></IconTextButton>}
        </PanelHeader>
        <QuestionFamilyAnalysis key={importAnalysisBatch.key} subject={subject} authToken={authToken} families={questionFamilies} sources={questionSources} initialQuestionIds={importAnalysisBatch.questionIds} initialAnalysisId={importAnalysisBatch.analysisId} initialRequestId={importAnalysisBatch.requestId} autoStart={!importAnalysisBatch.started || (importAnalysisBatch.status === "starting" && !importAnalysisBatch.analysisId)}
          onStatusChange={(status, analysis) => setImportFamilyAnalysis((current) => ({ ...current, batches: current.batches.map((item) => item.key === importAnalysisBatch.key ? { ...item, status, started: true, analysisId: analysis?.id || item.analysisId } : item) }))}
          onChanged={async () => { await refreshQuestionFamilies(); await loadQuestionResults({ reset: true, preserveNotice: true }); }}
          onClose={() => setImportFamilyAnalysis((current) => ({ ...current, open: false }))} />
      </BulkImportPanel>}

      <TabBar>
        <TabButton
          type="button"
          $active={activeTab === "question-create"}
          onClick={() => changeActiveTab("question-create")}
        >
          <FaPlus />
          新增題目
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "question-search"}
          onClick={() => changeActiveTab("question-search")}
        >
          <FaListUl />
          查詢題目
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "grades"}
          onClick={() => changeActiveTab("grades")}
        >
          <FaLayerGroup />
          年級
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "units"}
          onClick={() => changeActiveTab("units")}
        >
          <FaLayerGroup />
          單元
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "sources"}
          onClick={() => changeActiveTab("sources")}
        >
          <FaLayerGroup />
          來源
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "families"}
          onClick={() => changeActiveTab("families")}
        >
          <FaLayerGroup />
          題族
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "json"}
          onClick={() => changeActiveTab("json")}
        >
          <FaFileCode />
          API 串接
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "duplicate-scan"}
          onClick={() => changeActiveTab("duplicate-scan")}
        >
          <FaFlag />
          重複題目檢查
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === "bulk-import"}
          onClick={() => changeActiveTab("bulk-import")}
        >
          <FaLayerGroup />
          大量入題
        </TabButton>
      </TabBar>

      {notice && (
        <Notice $tone={notice.tone}>
          {notice.text}
          {notice.copyId && (
            <IconTextButton
              type="button"
              onClick={async () => {
                const copied = await copyQuestionId(
                  notice.copyId,
                  "題目 UUID 已複製",
                  null,
                );
                if (!copied) {
                  showNotice({
                    tone: "success",
                    text: `請手動選取 UUID：${notice.copyId}`,
                    copyId: notice.copyId,
                  });
                }
              }}
            >
              <FaCopy />
              複製 UUID
            </IconTextButton>
          )}
        </Notice>
      )}

      {loading ? (
        <LoadingContainer>
          <LoadingText>Loading...</LoadingText>
        </LoadingContainer>
      ) : (
        <>
          {activeTab === "question-create" && (
            <QuestionWorkspace>
              <SplitLayout>
                <EditorPanel onSubmit={handleSaveQuestion}>
                  <PanelHeader ref={questionFormTopRef}>
                    <PanelTitleGroup>
                      <PanelTitle>
                        {editingQuestionId ? "編輯題目" : "新增題目"}
                      </PanelTitle>
                      {editingQuestionId && (
                        <InlineIdButton
                          type="button"
                          title="複製題目 UUID"
                          onClick={() => copyQuestionId(editingQuestionId)}
                        >
                          <FaCopy />
                          {editingQuestionId}
                        </InlineIdButton>
                      )}
                    </PanelTitleGroup>
                    {editingQuestionId && (
                      <IconOnlyButton
                        type="button"
                        title="取消編輯"
                        onClick={cancelQuestionForm}
                        disabled={saving}
                      >
                        <FaTimes />
                      </IconOnlyButton>
                    )}
                  </PanelHeader>
                  {duplicateQuestionWarning && (
                    <DuplicateWarningPanel>
                      <DuplicateWarningText>
                        <strong>偵測到可能重複題目</strong>
                        <span>{duplicateQuestionWarning.message}</span>
                        <QuestionIdLine>
                          既有題目 UUID：
                          {duplicateQuestionWarning.duplicateQuestionId}
                          <SmallIconButton
                            type="button"
                            title="複製重複題 UUID"
                            onClick={() =>
                              copyQuestionId(
                                duplicateQuestionWarning.duplicateQuestionId,
                                "重複題 UUID 已複製",
                                "重複題 UUID 複製失敗",
                              )
                            }
                          >
                            <FaCopy />
                          </SmallIconButton>
                        </QuestionIdLine>
                      </DuplicateWarningText>
                      <DuplicateActionRow>
                        <IconTextButton
                          type="button"
                          onClick={() => setDuplicateQuestionWarning(null)}
                        >
                          <FaTimes />
                          取消
                        </IconTextButton>
                        <IconTextButton
                          type="button"
                          $primary
                          disabled={saving}
                          onClick={() => saveQuestion({ allowDuplicate: true })}
                        >
                          <FaSave />
                          仍然{editingQuestionId ? "更新" : "新增"}
                        </IconTextButton>
                      </DuplicateActionRow>
                    </DuplicateWarningPanel>
                  )}

                  <FieldGrid>
                    <Field>
                      <label>年級</label>
                      <select
                        value={questionForm.grade_id}
                        data-question-field="grade"
                        onChange={(event) =>
                          selectGradeForQuestion(event.target.value)
                        }
                      >
                        <option value="">選擇年級</option>
                        {grades.map((grade) => (
                          <option
                            key={getRecordId(grade)}
                            value={getRecordId(grade)}
                          >
                            {grade.name}
                          </option>
                        ))}
                        <option value={newGradeValue}>+ 文字新增</option>
                      </select>
                      {questionForm.grade_id === newGradeValue && (
                        <input
                          value={questionForm.new_grade_name}
                          placeholder="輸入新年級名稱"
                          data-question-field="new-grade"
                          onChange={(event) =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              new_grade_name: event.target.value,
                            }))
                          }
                        />
                      )}
                    </Field>
                    <Field>
                      <label>單元</label>
                      <SearchableSelect
                        value={questionForm.unit_id}
                        disabled={!questionForm.grade_id}
                        data-question-field="unit"
                        aria-label="題目單元"
                        onChange={(value) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            unit_id: value,
                            new_unit_name:
                              value === newUnitValue
                                ? prev.new_unit_name
                                : "",
                          }))
                        }
                        options={[
                          { value: "", label: "選擇單元" },
                          ...filteredUnitsForQuestion.map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                          ...(questionForm.grade_id ? [{ value: newUnitValue, label: "+ 文字新增" }] : []),
                        ]}
                      />
                      {(questionForm.unit_id === newUnitValue ||
                        questionForm.grade_id === newGradeValue) && (
                          <input
                            value={questionForm.new_unit_name}
                            placeholder="輸入新單元名稱"
                            data-question-field="new-unit"
                            onChange={(event) =>
                              setQuestionForm((prev) => ({
                                ...prev,
                                new_unit_name: event.target.value,
                              }))
                            }
                          />
                        )}
                    </Field>
                    {subjectCategories[subject] && (
                      <Field as="div">
                        <QuestionCategorySelect
                          subject={subject}
                          value={questionForm.category}
                          label="題目分類"
                          onChange={(category) => setQuestionForm((prev) => ({ ...prev, category }))}
                        />
                      </Field>
                    )}
                    <Field>
                      <label>題型</label>
                      <select
                        value={questionForm.type}
                        onChange={(event) => {
                          const nextType = event.target.value;
                          setQuestionForm((prev) => {
                            if (nextType === "choice") {
                              const choiceParts = splitPromptChoiceOptions(
                                prev.prompt_md,
                              );
                              return {
                                ...prev,
                                type: nextType,
                                prompt_md: choiceParts.prompt_md,
                                choice_options: choiceParts.choice_options,
                              };
                            }

                            return {
                              ...prev,
                              type: nextType,
                              prompt_md:
                                prev.type === "choice"
                                  ? getQuestionPrompt(prev)
                                  : prev.prompt_md,
                              choice_options: createEmptyChoiceOptions(),
                            };
                          });
                        }}
                      >
                        {questionTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field>
                      <label>難度</label>
                      <select
                        value={questionForm.difficulty}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            difficulty: event.target.value,
                          }))
                        }
                      >
                        {questionDifficulties.map((difficulty) => (
                          <option
                            key={difficulty.value}
                            value={difficulty.value}
                          >
                            {difficulty.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field>
                      <label>好題</label>
                      <ToggleLabel>
                        <input
                          type="checkbox"
                          checked={questionForm.is_good_question}
                          onChange={(event) =>
                            setQuestionForm((prev) => ({
                              ...prev,
                              is_good_question: event.target.checked,
                            }))
                          }
                        />
                        好題星星
                      </ToggleLabel>
                    </Field>
                    <Field>
                      <label>狀態</label>
                      <select
                        value={questionForm.status}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            status: event.target.value,
                          }))
                        }
                      >
                        {questionStatuses.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field>
                      <label>可見性</label>
                      <select
                        value={questionForm.visibility}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            visibility: event.target.value,
                          }))
                        }
                      >
                        {questionVisibilities.map((visibility) => (
                          <option
                            key={visibility.value}
                            value={visibility.value}
                          >
                            {visibility.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field>
                      <label>思維/思路</label>
                      <input
                        value={questionForm.thinking}
                        placeholder="用逗號分隔，例如：移項, 化簡"
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            thinking: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field>
                      <label>作者</label>
                      <input
                        value={questionForm.author_label}
                        placeholder="留空則使用登入者名稱"
                        maxLength={150}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            author_label: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field>
                      <label>題目來源</label>
                      <QuestionSourceField
                        id="question-source-select"
                        inputId="question-source-input"
                        value={questionForm.question_source}
                        sourceOptions={questionSources}
                        onChange={(value) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            question_source: value,
                          }))
                        }
                      />
                    </Field>
                    <QuestionFamilyFields families={questionFamilies} form={questionForm} onChange={(values) => setQuestionForm((current) => ({ ...current, ...values }))} />
                    <Field>
                      <label htmlFor="question-review-note">草稿疑義／待處理</label>
                      <textarea id="question-review-note" value={questionForm.review_note || ""}
                        placeholder="例如：第 (2) 小題公式待核對；原答案與計算不符"
                        onChange={(event) => setQuestionForm((prev) => ({ ...prev, review_note: event.target.value }))} />
                    </Field>
                  </FieldGrid>

                  <Field as="div">
                    <FieldHeader>
                      <label>
                        {questionForm.type === "choice"
                          ? "題幹 Markdown / KaTeX"
                          : "題目 Markdown / KaTeX"}
                      </label>
                      <InlineToolButton
                        type="button"
                        onClick={() => openMarkdownEditor("prompt_md")}
                      >
                        <FaExpand />
                        Vim 編輯
                      </InlineToolButton>
                    </FieldHeader>
                    <textarea
                      rows="5"
                      data-question-field="prompt"
                      style={getMarkdownTextareaStyle(
                        questionForm.prompt_md,
                        5,
                      )}
                      value={questionForm.prompt_md}
                      onChange={(event) =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          prompt_md: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  {questionForm.type === "choice" && (
                    <Field as="div">
                      <FieldHeader>
                        <label>選項</label>
                      </FieldHeader>
                      <ChoiceOptionGrid>
                        {(
                          questionForm.choice_options ||
                          createEmptyChoiceOptions()
                        ).map((option, optionIndex) => (
                          <ChoiceOptionRow key={option.label}>
                            <strong>({option.label})</strong>
                            <textarea
                              rows="2"
                              data-question-field={
                                optionIndex === 0 ? "choices" : undefined
                              }
                              value={option.text}
                              placeholder={`輸入 ${option.label} 選項，可用 Markdown / KaTeX`}
                              onChange={(event) =>
                                setQuestionForm((prev) => {
                                  const options = [
                                    ...(prev.choice_options ||
                                      createEmptyChoiceOptions()),
                                  ];
                                  options[optionIndex] = {
                                    ...options[optionIndex],
                                    text: event.target.value,
                                  };
                                  return {
                                    ...prev,
                                    choice_options: options,
                                  };
                                })
                              }
                            />
                          </ChoiceOptionRow>
                        ))}
                      </ChoiceOptionGrid>
                    </Field>
                  )}
                  <TwoColumnText>
                    <Field as="div">
                      <FieldHeader>
                        <label>答案 Markdown / KaTeX</label>
                        <InlineToolButton
                          type="button"
                          onClick={() => openMarkdownEditor("answer_md")}
                        >
                          <FaExpand />
                          Vim 編輯
                        </InlineToolButton>
                      </FieldHeader>
                      <textarea
                        rows="4"
                        style={getMarkdownTextareaStyle(
                          questionForm.answer_md,
                          4,
                        )}
                        value={questionForm.answer_md}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            answer_md: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field as="div">
                      <FieldHeader>
                        <label>詳解 Markdown / KaTeX</label>
                        <RowActions>
                          <InlineToolButton
                            type="button"
                            onClick={runQuestionSolutionApi}
                            disabled={
                              saving || questionAiStatus === "loading"
                            }
                          >
                            {questionAiStatus === "loading" ? (
                              <SpinningIcon />
                            ) : (
                              <FaMagic />
                            )}
                            {questionAiStatus === "loading"
                              ? "產生中"
                              : "AI 寫詳解"}
                          </InlineToolButton>
                          <InlineToolButton
                            type="button"
                            onClick={() => openMarkdownEditor("solution_md")}
                          >
                            <FaExpand />
                            Vim 編輯
                          </InlineToolButton>
                        </RowActions>
                      </FieldHeader>
                      <textarea
                        rows="4"
                        style={getMarkdownTextareaStyle(
                          questionForm.solution_md,
                          4,
                        )}
                        value={questionForm.solution_md}
                        onChange={(event) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            solution_md: event.target.value,
                          }))
                        }
                      />
                    </Field>
                  </TwoColumnText>

                  <QuestionPreviewPanel>
                    <PreviewHeader>
                      <PanelTitle>預覽</PanelTitle>
                    </PreviewHeader>
                    <PreviewGrid>
                      <PreviewBlock>
                        <PreviewLabel>題目</PreviewLabel>
                        <PreviewContent
                          $empty={
                            !getQuestionPrompt(questionForm) &&
                            !getPreviewableAssets(questionForm.assets, "prompt")
                              .length
                          }
                        >
                          <MathText
                            source={
                              getQuestionPrompt(questionForm) ||
                              (hasPromptAsset(questionForm)
                                ? ""
                                : "尚未輸入題目")
                            }
                            fallback=""
                          />
                          <AssetImagePreviews
                            assets={getPreviewableAssets(
                              questionForm.assets,
                              "prompt",
                            )}
                          />
                        </PreviewContent>
                      </PreviewBlock>
                      <PreviewBlock $tone="answer" $empty={!hasAnswerContent(questionForm)}>
                        <PreviewLabel>答案</PreviewLabel>
                        <PreviewContent
                          $empty={
                            !questionForm.answer_md.trim() &&
                            !getPreviewableAssets(questionForm.assets, "answer")
                              .length
                          }
                        >
                          <MathText
                            source={
                              questionForm.answer_md.trim() ||
                              (getPreviewableAssets(
                                questionForm.assets,
                                "answer",
                              ).length
                                ? ""
                                : "尚未輸入答案")
                            }
                          />
                          <AssetImagePreviews
                            assets={getPreviewableAssets(
                              questionForm.assets,
                              "answer",
                            )}
                          />
                        </PreviewContent>
                      </PreviewBlock>
                      <PreviewBlock>
                        <PreviewLabel>詳解</PreviewLabel>
                        <PreviewContent
                          $empty={
                            !questionForm.solution_md.trim() &&
                            !getPreviewableAssets(
                              questionForm.assets,
                              "solution",
                            ).length
                          }
                        >
                          <MathText
                            source={
                              questionForm.solution_md.trim() ||
                              (getPreviewableAssets(
                                questionForm.assets,
                                "solution",
                              ).length
                                ? ""
                                : "尚未輸入詳解")
                            }
                          />
                          <AssetImagePreviews
                            assets={getPreviewableAssets(
                              questionForm.assets,
                              "solution",
                            )}
                          />
                        </PreviewContent>
                      </PreviewBlock>
                    </PreviewGrid>
                  </QuestionPreviewPanel>

                  <AssetSection>
                    <AssetHeader>
                      <PanelTitle>
                        <FaImage />
                        圖片素材
                      </PanelTitle>
                      <IconTextButton type="button" onClick={addQuestionAsset}>
                        <FaPlus />
                        新增圖片
                      </IconTextButton>
                    </AssetHeader>

                    {questionForm.assets.length === 0 ? (
                      <EmptyLine>無圖片</EmptyLine>
                    ) : (
                      <AssetList>
                        {questionForm.assets.map((asset, index) => (
                          <AssetRow key={index}>
                            <QuestionAssetInputMode asset={asset}>
                              {(inputMode, modeControl) => (
                                <>
                                  <AssetMetadata>
                                    <strong>
                                      <FaImage /> 圖片 {index + 1}
                                    </strong>
                                    {modeControl}
                                    <Field>
                                      <label>排序</label>
                                      <input
                                        type="number"
                                        value={asset.sort_order}
                                        onChange={(event) =>
                                          updateQuestionAsset(index, {
                                            sort_order: event.target.value,
                                          })
                                        }
                                      />
                                    </Field>
                                    <IconOnlyButton
                                      type="button"
                                      title="移除圖片"
                                      onClick={() => removeQuestionAsset(index)}
                                    >
                                      <FaTrash />
                                    </IconOnlyButton>
                                  </AssetMetadata>
                                  <QuestionAssetRoleButtons
                                    value={asset.role}
                                    onChange={(role) =>
                                      updateQuestionAsset(index, { role })
                                    }
                                  />
                                  <AssetWorkspace>
                                    {inputMode !== "url" ? (
                                      <AssetSourcePanel>
                                        <AssetCodeToolbar>
                                          {inputMode === "code" && (
                                            <Field>
                                              <label>來源語言</label>
                                              <select
                                                value={asset.source_language}
                                                onChange={(event) =>
                                                  updateQuestionAsset(index, {
                                                    source_language:
                                                      event.target.value,
                                                  })
                                                }
                                              >
                                                {assetLanguages.map(
                                                  (language) => (
                                                    <option
                                                      key={language.value}
                                                      value={language.value}
                                                    >
                                                      {language.label}
                                                    </option>
                                                  ),
                                                )}
                                              </select>
                                            </Field>
                                          )}
                                          <div>
                                            <IconTextButton
                                              type="button"
                                              title={
                                                asset.source_language === "svg"
                                                  ? "用 Vim 編輯 SVG 圖"
                                                  : "用 Vim 編輯 Python 圖"
                                              }
                                              onClick={() => {
                                                updateQuestionAsset(index, {
                                                  source_language:
                                                    asset.source_language ||
                                                    "python",
                                                });
                                                setExpandedMarkdownField(null);
                                                setExpandedAssetSourceIndex(
                                                  index,
                                                );
                                                setAssetPreviewStatus("idle");
                                                resetAssetPreviewLeaderSubmission();
                                                setAssetPreviewMessage(
                                                  asset.url
                                                    ? "目前顯示後端圖片；修改後會先顯示 SVG 動態預覽。"
                                                    : "SVG 動態預覽會即時顯示，按 Space + W 送後端產生圖片。",
                                                );
                                              }}
                                            >
                                              <FaExpand /> Vim 編輯
                                            </IconTextButton>
                                            <IconTextButton
                                              type="button"
                                              title="Copy Figure Code"
                                              disabled={
                                                !asset.source_code?.trim()
                                              }
                                              onClick={() =>
                                                copyAssetSourceCode(
                                                  asset.source_code,
                                                )
                                              }
                                            >
                                              <FaCopy /> 複製程式碼
                                            </IconTextButton>
                                          </div>
                                        </AssetCodeToolbar>
                                        <Field>
                                          <label>
                                            {asset.source_language === "svg"
                                              ? "SVG 原始碼"
                                              : "Figure Code"}
                                          </label>
                                          <textarea
                                            rows="9"
                                            value={asset.source_code}
                                            placeholder={
                                              asset.source_language === "svg"
                                                ? "貼上 SVG 原始碼，右側即時預覽"
                                                : "輸入 Python 繪圖程式碼，右側即時預覽"
                                            }
                                            onChange={(event) => {
                                              const nextCode =
                                                event.target.value;
                                              updateQuestionAsset(index, {
                                                source_language:
                                                  asset.source_language ||
                                                  "python",
                                                source_code: nextCode,
                                                ...(nextCode.trim()
                                                  ? {
                                                      url: "",
                                                      storage_key: "",
                                                      mime_type: "",
                                                      preview_status: "idle",
                                                      preview_message: "",
                                                      _preview_url: "",
                                                      _preview_source_code: "",
                                                    }
                                                  : {}),
                                              });
                                            }}
                                          />
                                        </Field>
                                      </AssetSourcePanel>
                                    ) : (
                                      <AssetSourcePanel>
                                        <Field>
                                          <label>圖片 URL</label>
                                          <input
                                            value={asset.url}
                                            placeholder="https://assets.sunnytseng.com/..."
                                            onChange={(event) => {
                                              const nextUrl =
                                                event.target.value;
                                              updateQuestionAsset(index, {
                                                url: nextUrl,
                                                ...(nextUrl.trim()
                                                  ? {
                                                      source_language: "",
                                                      source_code: "",
                                                      mime_type: "",
                                                      preview_status: "idle",
                                                      preview_message: "",
                                                      _preview_url: "",
                                                      _preview_source_code: "",
                                                    }
                                                  : {}),
                                              });
                                            }}
                                          />
                                        </Field>
                                        <p>
                                          貼上圖片連結，即可在右側確認圖片。
                                        </p>
                                      </AssetSourcePanel>
                                    )}
                                    <QuestionAssetEditorPreview
                                      asset={asset}
                                      inputMode={inputMode}
                                    />
                                  </AssetWorkspace>
                                  <AssetDetails>
                                    <Field>
                                      <label>圖片描述</label>
                                      <input
                                        value={asset.alt_text}
                                        placeholder="圖片描述，可空白"
                                        onChange={(event) =>
                                          updateQuestionAsset(index, {
                                            alt_text: event.target.value,
                                          })
                                        }
                                      />
                                    </Field>{" "}
                                    <Field>
                                      <label>判讀說明</label>
                                      <textarea
                                        rows="3"
                                        value={asset.marking_guide}
                                        placeholder="給審核或人工判讀看的說明"
                                        onChange={(event) =>
                                          updateQuestionAsset(index, {
                                            marking_guide: event.target.value,
                                          })
                                        }
                                      />
                                    </Field>
                                  </AssetDetails>
                                </>
                              )}
                            </QuestionAssetInputMode>
                          </AssetRow>
                        ))}
                      </AssetList>
                    )}
                  </AssetSection>

                  <FormActions>
                    <IconTextButton type="submit" $primary disabled={saving}>
                      {isSavingQuestion ? <SpinningIcon /> : <FaSave />}
                      {questionSaveLabel}
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={cancelQuestionForm}
                      disabled={saving}
                    >
                      <FaTimes />
                      取消
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={resetQuestionForm}
                      disabled={saving}
                    >
                      <FaTimes />
                      清空
                    </IconTextButton>
                  </FormActions>
                </EditorPanel>
              </SplitLayout>
            </QuestionWorkspace>
          )}

          {activeTab === "question-search" && (
            <QuestionWorkspace>
              <SplitLayout>
                <QuestionLookupSection>
                  <QuestionLookupHeader>
                    <PanelTitle>
                      <FaSearch />
                      查詢題目
                    </PanelTitle>
                    <SectionCount>
                      {shouldLoadQuestionResults
                        ? questionTotal != null
                          ? `共 ${questionTotal.toLocaleString()} 題 · 已載入 ${displayQuestions.length.toLocaleString()} 題`
                          : `${displayQuestions.length}${hasMoreQuestions ? "+" : ""} 題`
                        : "尚未篩選"}
                    </SectionCount>
                  </QuestionLookupHeader>

                  <QuestionCategorySelect subject={subject} value={filters.category} filter label="查詢分類" onChange={(value) => updateQuestionFilter("category", value)} />
                  <FilterBar>
                    <SearchBox>
                      <FaSearch />
                      <input
                        value={filters.search}
                        placeholder="搜尋 UUID / 題目 / 詳解 / 思維 / 來源"
                        onChange={(event) =>
                          updateQuestionFilter("search", event.target.value)
                        }
                      />
                    </SearchBox>
                    <QuestionFilterSelect
                      aria-label="查詢年級"
                      value={filters.grade_id}
                      onChange={(event) =>
                        selectGradeForFilter(event.target.value)
                      }
                    >
                      <option value={filterNoneValue}>無</option>
                      <option value={filterAllValue}>全部年級</option>
                      {grades.map((grade) => (
                        <option
                          key={getRecordId(grade)}
                          value={getRecordId(grade)}
                        >
                          {grade.name}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <SearchableSelect
                      allowMultiple placeholderValues={["", "__none__"]} placeholder="單元"
                      value={filters.unit_id}
                      aria-label="查詢單元"
                      onChange={(value) => updateQuestionFilter("unit_id", value)}
                      options={[
                        { value: filterNoneValue, label: "無" },
                        { value: filterAllValue, label: "全部單元" },
                        ...filteredUnitsForFilter.map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                      ]}
                    />
                    <QuestionFilterSelect
                      aria-label="查詢題型"
                      value={filters.type}
                      onChange={(event) =>
                        updateQuestionFilter("type", event.target.value)
                      }
                    >
                      <option value="">全部題型</option>
                      {questionTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <QuestionFilterSelect
                      aria-label="查詢難度"
                      value={filters.difficulty}
                      onChange={(event) =>
                        updateQuestionFilter("difficulty", event.target.value)
                      }
                    >
                      <option value="">全部難度</option>
                      {questionDifficulties.map((difficulty) => (
                        <option key={difficulty.value} value={difficulty.value}>
                          {difficulty.label}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <QuestionFilterSelect
                      aria-label="查詢狀態"
                      value={filters.status}
                      onChange={(event) =>
                        updateQuestionFilter("status", event.target.value)
                      }
                    >
                      <option value="">全部狀態</option>
                      {questionStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <QuestionFilterSelect
                      aria-label="查詢可見性"
                      value={filters.visibility}
                      onChange={(event) =>
                        updateQuestionFilter("visibility", event.target.value)
                      }
                    >
                      <option value="">全部可見性</option>
                      {questionVisibilities.map((visibility) => (
                        <option key={visibility.value} value={visibility.value}>
                          {visibility.label}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <QuestionFilterSelect
                      aria-label="查詢好題"
                      value={filters.is_good_question}
                      onChange={(event) =>
                        updateQuestionFilter(
                          "is_good_question",
                          event.target.value,
                        )
                      }
                    >
                      {questionGoodStarFilters.map((filter) => (
                        <option
                          key={filter.value || "all-good-star"}
                          value={filter.value}
                        >
                          {filter.label}
                        </option>
                      ))}
                    </QuestionFilterSelect>
                    <QuestionFilterSelect className="family-filter" aria-label="查詢題族" value={filters.family_id} onChange={(event) => updateQuestionFilter("family_id", event.target.value)}><option value="">全部題族</option><option value="unassigned">未歸題族</option>{questionFamilies.map((family) => <option key={family.id} value={family.id}>{family.name}</option>)}</QuestionFilterSelect>
                    <QuestionFilterSelect className="answer-state-filter" aria-label="查詢解答狀態" value={filters.answer_state} onChange={(event) => updateQuestionFilter("answer_state", event.target.value)}>
                      {questionAnswerStates.map((state) => <option key={state.value} value={state.value}>{state.label}</option>)}
                    </QuestionFilterSelect>
                    <SearchableSelect
                      allowMultiple placeholderValues={["", "__none__"]} placeholder="來源"
                      className="source-filter"
                      value={filters.question_source}
                      aria-label="查詢來源"
                      onChange={(value) => updateQuestionFilter("question_source", value)}
                      options={[
                        { value: "", label: "全部來源" },
                        ...questionSources.map((source) => ({ value: source, label: source })),
                      ]}
                    />
                    <IconOnlyButton
                      type="button"
                      title="清除篩選"
                      onClick={() => {
                        setFilters(emptyFilters);
                        clearQuestionState();
                      }}
                    >
                      <FaTimes />
                    </IconOnlyButton>
                  </FilterBar>

                  <TablePanel>
                    {!shouldLoadQuestionResults ? (
                      <EmptyLine>
                        請選擇全部年級、全部單元或篩選條件後顯示題目
                      </EmptyLine>
                    ) : questionLoading && questions.length === 0 ? (
                      <InlineLoading>
                        <LoadingText>Loading...</LoadingText>
                      </InlineLoading>
                    ) : (
                      <>
                        {displayQuestions.length > 0 && (
                          <BulkActionBar $active={questionSelectionMode}>
                            <BulkSelectionSummary>
                              <IconTextButton
                                type="button"
                                $primary={questionSelectionMode}
                                aria-pressed={questionSelectionMode}
                                disabled={saving}
                                onClick={() => {
                                  cancelQuestionPreview();
                                  setSelectedQuestionIds([]);
                                  setQuestionSelectionMode(!questionSelectionMode);
                                }}
                              >
                                {questionSelectionMode ? (
                                  <FaCheck aria-hidden="true" />
                                ) : (
                                  <FaRegEdit aria-hidden="true" />
                                )}
                                {questionSelectionMode ? "完成勾選" : "批量勾選"}
                              </IconTextButton>
                              <CountText aria-live="polite" aria-atomic="true">
                                {questionSelectionMode
                                  ? `已勾選 ${selectedVisibleQuestionIds.length} / 已載入 ${visibleQuestionIds.length} 題`
                                  : `已載入 ${visibleQuestionIds.length} 題`}
                              </CountText>
                              {questionSelectionMode && (
                                <BulkSelectionControls>
                                  <IconTextButton
                                    type="button"
                                    disabled={saving || allVisibleQuestionsSelected}
                                    onClick={() => setSelectedQuestionIds([...visibleQuestionIds])}
                                  >
                                    全選已載入
                                  </IconTextButton>
                                  <IconTextButton
                                    type="button"
                                    disabled={saving || selectedVisibleQuestionIds.length === 0}
                                    onClick={() => setSelectedQuestionIds([])}
                                  >
                                    清除勾選
                                  </IconTextButton>
                                </BulkSelectionControls>
                              )}
                            </BulkSelectionSummary>
                            {questionSelectionMode && (
                              <>
                                <SmallMuted>
                                  全選只包含已載入且符合篩選的題目
                                </SmallMuted>
                                <BulkActionGroup>
                                  <BulkActionField role="group" aria-label="批量更新狀態">
                                    <label htmlFor={`bulk-question-status-${subject}`}>狀態</label>
                                    <CompactSelect
                                      id={`bulk-question-status-${subject}`}
                                      value={bulkStatusAction}
                                      onChange={(event) =>
                                        setBulkStatusAction(event.target.value)
                                      }
                                      disabled={saving}
                                      aria-label="批次狀態"
                                    >
                                      {questionStatuses.map((status) => (
                                        <option key={status.value} value={status.value}>
                                          {status.label}
                                        </option>
                                      ))}
                                    </CompactSelect>
                                    <IconTextButton
                                      type="button"
                                      $tone="status"
                                      onClick={() =>
                                        handleBulkUpdateQuestions(
                                          { status: bulkStatusAction },
                                          getStatusLabel(bulkStatusAction),
                                        )
                                      }
                                      disabled={saving || selectedVisibleQuestionIds.length === 0}
                                    >
                                      <FaSyncAlt aria-hidden="true" />
                                      套用狀態
                                    </IconTextButton>
                                  </BulkActionField>
                                  <BulkActionField role="group" aria-label="批量更新可見性">
                                    <label htmlFor={`bulk-question-visibility-${subject}`}>可見性</label>
                                    <CompactSelect
                                      id={`bulk-question-visibility-${subject}`}
                                      value={bulkVisibilityAction}
                                      onChange={(event) =>
                                        setBulkVisibilityAction(event.target.value)
                                      }
                                      disabled={saving}
                                      aria-label="批次可見性"
                                    >
                                      {questionVisibilities.map((visibility) => (
                                        <option key={visibility.value} value={visibility.value}>
                                          {visibility.label}
                                        </option>
                                      ))}
                                    </CompactSelect>
                                    <IconTextButton
                                      type="button"
                                      $tone="visibility"
                                      onClick={() =>
                                        handleBulkUpdateQuestions(
                                          { visibility: bulkVisibilityAction },
                                          getVisibilityLabel(bulkVisibilityAction),
                                        )
                                      }
                                      disabled={saving || selectedVisibleQuestionIds.length === 0}
                                    >
                                      <FaGlobe aria-hidden="true" />
                                      套用可見性
                                    </IconTextButton>
                                  </BulkActionField>
                                  <BulkDangerAction>
                                    <IconTextButton
                                      type="button"
                                      $danger
                                      onClick={handleBulkDeleteQuestions}
                                      disabled={saving || selectedVisibleQuestionIds.length === 0}
                                    >
                                      <FaTrash aria-hidden="true" />
                                      刪除勾選（{selectedVisibleQuestionIds.length}）
                                    </IconTextButton>
                                  </BulkDangerAction>
                                </BulkActionGroup>
                              </>
                            )}
                          </BulkActionBar>
                        )}
                        <TableScroll>
                          <DataTable>
                            <thead>
                              <tr>
                                {questionSelectionMode && (
                                  <CheckboxCell as="th" aria-label="勾選題目">
                                    <QuestionSelectionLabel>
                                      <input
                                        ref={selectAllQuestionsRef}
                                        type="checkbox"
                                        checked={allVisibleQuestionsSelected}
                                        disabled={saving}
                                        aria-label="全選已載入題目"
                                        aria-checked={
                                          allVisibleQuestionsSelected
                                            ? "true"
                                            : someVisibleQuestionsSelected
                                              ? "mixed"
                                              : "false"
                                        }
                                        onChange={toggleVisibleQuestionSelection}
                                      />
                                      <span>全選</span>
                                    </QuestionSelectionLabel>
                                  </CheckboxCell>
                                )}
                                <th>題目</th>
                                <th>分類</th>
                                <th>狀態</th>
                                <th>圖片</th>
                                <th>操作</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayQuestions.map((question) => {
                                const questionId = getRecordId(question);
                                const isSelected =
                                  selectedQuestionIdSet.has(questionId);

                                return (
                                  <tr
                                    key={questionId}
                                    data-question-preview="true"
                                    data-selected={isSelected ? "true" : undefined}
                                    tabIndex={0}
                                    aria-label="單擊預覽題目，雙擊編輯題目"
                                    aria-haspopup="dialog"
                                    title="單擊預覽題目，雙擊編輯題目"
                                    onClick={(event) => queueQuestionPreview(event, question)}
                                    onDoubleClick={(event) => {
                                      if (event.target.closest("button, input, a")) return;
                                      cancelQuestionPreview();
                                      openQuestionEditor(question);
                                    }}
                                    onKeyDown={(event) => {
                                      if (
                                        event.target === event.currentTarget &&
                                        (event.key === "Enter" || event.key === " ")
                                      ) {
                                        event.preventDefault();
                                        setPreviewQuestion(question);
                                      }
                                    }}
                                  >
                                    {questionSelectionMode && (
                                      <CheckboxCell
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          cancelQuestionPreview();
                                        }}
                                        onDoubleClick={(event) => event.stopPropagation()}
                                      >
                                        <QuestionSelectionLabel $selected={isSelected}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={saving}
                                            aria-label={`勾選題目 ${questionId}`}
                                            onChange={() => toggleQuestionSelection(questionId)}
                                          />
                                          <span>{isSelected ? "已選" : "勾選"}</span>
                                        </QuestionSelectionLabel>
                                      </CheckboxCell>
                                    )}
                                    <td>
                                      <QuestionIdLine>
                                        <SmallIconButton
                                          type="button"
                                          title="複製題目 UUID"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            copyQuestionId(questionId);
                                          }}
                                        >
                                          <FaCopy />
                                        </SmallIconButton>
                                        <span>UUID：{shortId(questionId)}</span>
                                        {question.is_good_question && (
                                          <GoodQuestionBadge title="好題">
                                            <FaStar />
                                            好題
                                          </GoodQuestionBadge>
                                        )}
                                      </QuestionIdLine>
                                      <QuestionText>
                                        <MathText
                                          source={question.prompt_md}
                                          fallback=""
                                        />
                                      </QuestionText>
                                      {question.status === "draft" && question.review_note && (
                                        <SmallMuted role="note">草稿疑義：{question.review_note}</SmallMuted>
                                      )}
                                      <QuestionDetailBlock $tone="answer" $empty={!hasAnswerContent(question)}>
                                        <QuestionDetailLabel $tone="answer">
                                          答案
                                        </QuestionDetailLabel>
                                        <QuestionDetailText>
                                          <MathText
                                            source={getAnswerDisplayText(question)}
                                            fallback=""
                                          />
                                        </QuestionDetailText>
                                      </QuestionDetailBlock>
                                      {question.solution_md && (
                                        <QuestionDetailBlock>
                                          <QuestionDetailLabel>
                                            詳解
                                          </QuestionDetailLabel>
                                          <QuestionDetailText>
                                            <MathText
                                              source={question.solution_md}
                                              fallback=""
                                            />
                                          </QuestionDetailText>
                                        </QuestionDetailBlock>
                                      )}
                                      <QuestionMetaLine>
                                        <AuthorBadge
                                          $source={
                                            question.created_by_auth_type
                                          }
                                        >
                                          作者
                                        </AuthorBadge>
                                        <span>
                                          ：{formatQuestionAuthor(question)}
                                        </span>
                                      </QuestionMetaLine>
                                      {formatThinking(question.thinking) && (
                                        <SmallMuted>
                                          {formatThinking(question.thinking)}
                                        </SmallMuted>
                                      )}
                                      {normalizeQuestionSourceValue(
                                        question.question_source,
                                      ) && (
                                        <SmallMuted>
                                          來源：
                                          {normalizeQuestionSourceValue(
                                            question.question_source,
                                          )}
                                        </SmallMuted>
                                      )}
                                    </td>
                                    <td>
                                      <strong>{question.grade?.name}</strong>
                                      <SmallMuted>
                                        {question.unit?.name}
                                      </SmallMuted>
                                    </td>
                                    <td>
                                      <VisibilityBadge
                                        $visibility={question.visibility}
                                        title={getVisibilityLabel(
                                          question.visibility,
                                        )}
                                      >
                                        {question.visibility === "private" ? (
                                          <FaLock />
                                        ) : (
                                          <FaGlobe />
                                        )}
                                        {getVisibilityLabel(
                                          question.visibility,
                                        )}
                                      </VisibilityBadge>
                                      <SmallMuted>
                                        <StatusBadge $status={question.status}>
                                          {getStatusIcon(question.status)}
                                          {getStatusLabel(question.status)}
                                        </StatusBadge>
                                      </SmallMuted>
                                      <SmallMuted>
                                        <QuestionDifficultyBadge difficulty={question.difficulty} />
                                      </SmallMuted>
                                      <SmallMuted>{question.type}</SmallMuted>
                                    </td>
                                    <td>
                                      <AssetImagePreviews
                                        assets={question.assets || []}
                                        compact
                                        limit={3}
                                      />
                                      <SmallMuted>
                                        {getPreviewableAssets(
                                          question.assets || [],
                                        ).length
                                          ? `${getPreviewableAssets(question.assets || []).length} 個預覽素材`
                                          : `${question.assets?.length || 0} 個素材`}
                                      </SmallMuted>
                                      {getAssetSourceCode(question.assets) && (
                                        <SmallIconButton
                                          type="button"
                                          title="Copy Figure Code"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            copyAssetSourceCode(
                                              getAssetSourceCode(
                                                question.assets,
                                              ),
                                            );
                                          }}
                                        >
                                          <FaCopy />
                                        </SmallIconButton>
                                      )}
                                    </td>
                                    <td>
                                      <RowActions>
                                        <IconOnlyButton
                                          type="button"
                                          title="編輯題目"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openQuestionEditor(question);
                                          }}
                                        >
                                          <FaEdit />
                                        </IconOnlyButton>
                                        <IconOnlyButton
                                          type="button"
                                          title="刪除題目"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteQuestion(question);
                                          }}
                                        >
                                          <FaTrash />
                                        </IconOnlyButton>
                                      </RowActions>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </DataTable>
                          {questions.length > 0 && (
                            <QuestionListFooter ref={questionSentinelRef}>
                              {loadingMoreQuestions ? (
                                <>
                                  <SpinningIcon aria-hidden="true" />
                                  載入更多題目...
                                </>
                              ) : hasMoreQuestions ? (
                                "往下滑載入更多"
                              ) : (
                                "已載入全部符合條件的題目"
                              )}
                            </QuestionListFooter>
                          )}
                        </TableScroll>
                        {questions.length > 0 &&
                          displayQuestions.length === 0 && (
                            <EmptyLine>
                              目前已載入題目中沒有符合條件的題目
                            </EmptyLine>
                          )}
                        {questions.length === 0 && (
                          <EmptyLine>沒有符合條件的題目</EmptyLine>
                        )}
                      </>
                    )}
                  </TablePanel>
                </QuestionLookupSection>
              </SplitLayout>
            </QuestionWorkspace>
          )}

          {activeTab === "families" && <QuestionFamilyWorkspace subject={subject} authToken={authToken} grades={grades} units={units} sources={questionSources} isActive={isActive && routeActive} onEditQuestion={openQuestionEditor} onChanged={refreshQuestionFamilies} />}

          {activeTab === "grades" && (
            <TaxonomyGrid>
              <EditorPanel onSubmit={handleSaveGrade}>
                <PanelHeader>
                  <PanelTitle>
                    {editingGradeId ? "編輯年級" : "新增年級"}
                  </PanelTitle>
                  {editingGradeId && (
                    <IconOnlyButton
                      type="button"
                      title="取消編輯"
                      onClick={resetGradeForm}
                    >
                      <FaTimes />
                    </IconOnlyButton>
                  )}
                </PanelHeader>
                <FieldGrid>
                  <Field>
                    <label>名稱</label>
                    <input
                      value={gradeForm.name}
                      onChange={(event) =>
                        setGradeForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field>
                    <label>排序</label>
                    <input
                      type="number"
                      value={gradeForm.order_index}
                      onChange={(event) =>
                        setGradeForm((prev) => ({
                          ...prev,
                          order_index: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <ToggleLabel>
                    <input
                      type="checkbox"
                      checked={gradeForm.is_active}
                      onChange={(event) =>
                        setGradeForm((prev) => ({
                          ...prev,
                          is_active: event.target.checked,
                        }))
                      }
                    />
                    啟用
                  </ToggleLabel>
                </FieldGrid>
                <FormActions>
                  <IconTextButton type="submit" $primary disabled={saving}>
                    <FaSave />
                    {editingGradeId ? "更新年級" : "新增年級"}
                  </IconTextButton>
                  <IconTextButton type="button" onClick={resetGradeForm}>
                    <FaTimes />
                    清空
                  </IconTextButton>
                </FormActions>
              </EditorPanel>

              <TablePanel>
                <TaxonomyTableHeader>
                  <PanelTitle>年級列表</PanelTitle>
                  <SectionCount>
                    顯示 {filteredGradesForGradeForm.length} / {grades.length}
                  </SectionCount>
                </TaxonomyTableHeader>
                {filteredGradesForGradeForm.length ? (
                  <TableScroll>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>名稱</th>
                          <th>排序</th>
                          <th>狀態</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredGradesForGradeForm.map((grade) => (
                          <tr key={getRecordId(grade)}>
                            <td>
                              <div>{grade.name}</div>
                              <QuestionIdLine>
                                {getRecordId(grade)}
                                <SmallIconButton
                                  type="button"
                                  title="複製年級 UUID"
                                  onClick={() =>
                                    copyRecordId(getRecordId(grade), "年級")
                                  }
                                >
                                  <FaCopy />
                                </SmallIconButton>
                              </QuestionIdLine>
                            </td>
                            <td>{grade.order_index}</td>
                            <td>
                              {grade.is_active ? <FaCheck /> : <FaTimes />}
                            </td>
                            <td>
                              <RowActions>
                                <IconOnlyButton
                                  type="button"
                                  title="編輯年級"
                                  onClick={() => {
                                    setEditingGradeId(getRecordId(grade));
                                    setGradeForm({
                                      name: grade.name,
                                      order_index: grade.order_index,
                                      is_active: grade.is_active,
                                    });
                                  }}
                                >
                                  <FaEdit />
                                </IconOnlyButton>
                                <IconOnlyButton
                                  type="button"
                                  title="刪除年級"
                                  onClick={() => handleDeleteGrade(grade)}
                                >
                                  <FaTrash />
                                </IconOnlyButton>
                              </RowActions>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </TableScroll>
                ) : (
                  <EmptyLine>沒有符合目前輸入的年級</EmptyLine>
                )}
              </TablePanel>
            </TaxonomyGrid>
          )}

          {activeTab === "units" && (
            <TaxonomyGrid>
              <EditorPanel onSubmit={handleSaveUnit}>
                <PanelHeader>
                  <PanelTitle>
                    {editingUnitId ? "編輯單元" : "新增單元"}
                  </PanelTitle>
                  {editingUnitId && (
                    <IconOnlyButton
                      type="button"
                      title="取消編輯"
                      onClick={resetUnitForm}
                    >
                      <FaTimes />
                    </IconOnlyButton>
                  )}
                </PanelHeader>
                <FieldGrid>
                  <Field>
                    <label>年級</label>
                    <select
                      value={unitForm.grade_id}
                      onChange={(event) =>
                        setUnitForm((prev) => ({
                          ...prev,
                          grade_id: event.target.value,
                          parent_id: "",
                        }))
                      }
                    >
                      <option value="">選擇年級</option>
                      {grades.map((grade) => (
                        <option
                          key={getRecordId(grade)}
                          value={getRecordId(grade)}
                        >
                          {grade.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    <label>上層單元</label>
                    <SearchableSelect
                      value={unitForm.parent_id}
                      aria-label="上層單元"
                      onChange={(value) =>
                        setUnitForm((prev) => ({
                          ...prev,
                          parent_id: value,
                        }))
                      }
                      options={[
                        { value: "", label: "無" },
                        ...filteredUnitsForUnitForm.filter((unit) => getRecordId(unit) !== editingUnitId)
                          .map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                      ]}
                    />
                  </Field>
                  <Field>
                    <label>名稱</label>
                    <input
                      value={unitForm.name}
                      onChange={(event) =>
                        setUnitForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field>
                    <label>排序</label>
                    <input
                      type="number"
                      value={unitForm.order_index}
                      onChange={(event) =>
                        setUnitForm((prev) => ({
                          ...prev,
                          order_index: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <ToggleLabel>
                    <input
                      type="checkbox"
                      checked={unitForm.is_active}
                      onChange={(event) =>
                        setUnitForm((prev) => ({
                          ...prev,
                          is_active: event.target.checked,
                        }))
                      }
                    />
                    啟用
                  </ToggleLabel>
                </FieldGrid>
                <FormActions>
                  <IconTextButton type="submit" $primary disabled={saving}>
                    <FaSave />
                    {editingUnitId ? "更新單元" : "新增單元"}
                  </IconTextButton>
                  <IconTextButton type="button" onClick={resetUnitForm}>
                    <FaTimes />
                    清空
                  </IconTextButton>
                </FormActions>
              </EditorPanel>

              <TablePanel>
                <TaxonomyTableHeader>
                  <PanelTitle>單元列表</PanelTitle>
                  <SectionCount>
                    顯示 {filteredUnitsForUnitList.length} / {units.length}
                  </SectionCount>
                </TaxonomyTableHeader>
                {filteredUnitsForUnitList.length ? (
                  <TableScroll>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>名稱</th>
                          <th>年級</th>
                          <th>上層</th>
                          <th>排序</th>
                          <th>狀態</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUnitsForUnitList.map((unit) => (
                          <tr key={getRecordId(unit)}>
                            <td>
                              <div>{unit.name}</div>
                              <QuestionIdLine>
                                {getRecordId(unit)}
                                <SmallIconButton
                                  type="button"
                                  title="複製單元 UUID"
                                  onClick={() =>
                                    copyRecordId(getRecordId(unit), "單元")
                                  }
                                >
                                  <FaCopy />
                                </SmallIconButton>
                              </QuestionIdLine>
                            </td>
                            <td>{unit.grade?.name}</td>
                            <td>{unit.parent?.name || "-"}</td>
                            <td>{unit.order_index}</td>
                            <td>
                              {unit.is_active ? <FaCheck /> : <FaTimes />}
                            </td>
                            <td>
                              <RowActions>
                                <IconOnlyButton
                                  type="button"
                                  title="編輯單元"
                                  onClick={() => {
                                    setEditingUnitId(getRecordId(unit));
                                    setUnitForm({
                                      grade_id: getUnitGradeId(unit),
                                      parent_id: getUnitParentId(unit),
                                      name: unit.name,
                                      order_index: unit.order_index,
                                      is_active: unit.is_active,
                                    });
                                  }}
                                >
                                  <FaEdit />
                                </IconOnlyButton>
                                <IconOnlyButton
                                  type="button"
                                  title="刪除單元"
                                  onClick={() => handleDeleteUnit(unit)}
                                >
                                  <FaTrash />
                                </IconOnlyButton>
                              </RowActions>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </TableScroll>
                ) : (
                  <EmptyLine>沒有符合目前輸入的單元</EmptyLine>
                )}
              </TablePanel>
            </TaxonomyGrid>
          )}

          {activeTab === "sources" && (
            <TaxonomyGrid>
              <EditorPanel onSubmit={handleSaveSource}>
                <PanelHeader>
                  <PanelTitle>
                    {editingSourceId ? "編輯來源" : "新增來源"}
                  </PanelTitle>
                  {editingSourceId && (
                    <IconOnlyButton
                      type="button"
                      title="取消編輯"
                      onClick={resetSourceForm}
                    >
                      <FaTimes />
                    </IconOnlyButton>
                  )}
                </PanelHeader>
                <FieldGrid>
                  <Field>
                    <label>名稱</label>
                    <input
                      value={sourceForm.name}
                      onChange={(event) =>
                        setSourceForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field>
                    <label>排序</label>
                    <input
                      type="number"
                      value={sourceForm.order_index}
                      onChange={(event) =>
                        setSourceForm((prev) => ({
                          ...prev,
                          order_index: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <ToggleLabel>
                    <input
                      type="checkbox"
                      checked={sourceForm.is_active}
                      onChange={(event) =>
                        setSourceForm((prev) => ({
                          ...prev,
                          is_active: event.target.checked,
                        }))
                      }
                    />
                    啟用
                  </ToggleLabel>
                </FieldGrid>
                <FormActions>
                  <IconTextButton type="submit" $primary disabled={saving}>
                    <FaSave />
                    {editingSourceId ? "更新來源" : "新增來源"}
                  </IconTextButton>
                  <IconTextButton type="button" onClick={resetSourceForm}>
                    <FaTimes />
                    清空
                  </IconTextButton>
                </FormActions>
              </EditorPanel>

              <TablePanel>
                <TaxonomyTableHeader>
                  <PanelTitle>來源列表</PanelTitle>
                  <SectionCount>
                    顯示 {filteredSourcesForSourceForm.length} / {sources.length}
                  </SectionCount>
                </TaxonomyTableHeader>
                {filteredSourcesForSourceForm.length ? (
                  <TableScroll>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>名稱</th>
                          <th>排序</th>
                          <th>狀態</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSourcesForSourceForm.map((source) => (
                          <tr key={getRecordId(source)}>
                            <td>{source.name}</td>
                            <td>{source.order_index}</td>
                            <td>
                              {source.is_active ? <FaCheck /> : <FaTimes />}
                            </td>
                            <td>
                              <RowActions>
                                <IconOnlyButton
                                  type="button"
                                  title="編輯來源"
                                  onClick={() => {
                                    setEditingSourceId(getRecordId(source));
                                    setSourceForm({
                                      name: source.name,
                                      order_index: source.order_index,
                                      is_active: source.is_active,
                                    });
                                  }}
                                >
                                  <FaEdit />
                                </IconOnlyButton>
                                <IconOnlyButton
                                  type="button"
                                  title="刪除來源"
                                  onClick={() => handleDeleteSource(source)}
                                >
                                  <FaTrash />
                                </IconOnlyButton>
                              </RowActions>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </TableScroll>
                ) : (
                  <EmptyLine>沒有符合目前輸入的來源</EmptyLine>
                )}
              </TablePanel>
            </TaxonomyGrid>
          )}

          {activeTab === "json" && (
            <ApiGuidePanel>
              <PanelHeader>
                <PanelTitleGroup>
                  <PanelTitle>JSON API 串接</PanelTitle>
                  <ApiGuideIntro>
                    下載的 `math-bank-questions.json` 是批次包裝格式，API
                    一次只新增一題，所以要把 `questions[]` 逐題送到 題庫
                    API。預設使用目前登入的 JWT；填 Staff API Key 時才改用 staff
                    API。
                  </ApiGuideIntro>
                </PanelTitleGroup>
              </PanelHeader>

              <ApiStepList>
                <li>把 `math-bank-questions.json` 放在要執行指令的資料夾。</li>
                <li>
                  預設使用目前登入的 JWT。若要改用 staff API，再輸入 Staff API
                  Key。
                </li>
                <li>選擇年級與單元，macOS 與 Windows 程式碼會自動帶入。</li>
                <li>
                  確認後端在 `http://localhost:8000` 運行，再執行對應環境指令。
                </li>
                <li>
                  預設遇到重複題會顯示 `DUPLICATE` 並略過；要強制匯入再開啟
                  `FORCE_DUPLICATES`。
                </li>
              </ApiStepList>

              <ApiKeyField>
                <label htmlFor="math-bank-staff-api-key">Staff API Key</label>
                <input
                  id="math-bank-staff-api-key"
                  type="password"
                  value={staffApiKey}
                  placeholder="選填；留空使用目前登入的 JWT"
                  onChange={(event) => updateStaffApiKey(event.target.value)}
                />
                <span>
                  填入時會改用 staff API；留空時複製指令會使用目前登入的 JWT。
                </span>
              </ApiKeyField>

              <ApiKeyField>
                <label htmlFor="math-bank-staff-author-label">
                  作者顯示名稱
                </label>
                <input
                  id="math-bank-staff-author-label"
                  type="text"
                  value={staffAuthorLabel}
                  placeholder="例如：Sunny Tseng"
                  maxLength={150}
                  onChange={(event) =>
                    updateStaffAuthorLabel(event.target.value)
                  }
                />
                <span>
                  會以 author_label 送入 staff API；留空會顯示 API key
                  名稱，不會套用 JWT 登入者名稱。
                </span>
              </ApiKeyField>

              <FieldGrid>
                <Field>
                  <FieldHeader>
                    <label htmlFor="math-bank-api-import-grade">入題年級</label>
                  </FieldHeader>
                  <CompactSelect
                    id="math-bank-api-import-grade"
                    value={apiImportScope.grade_id}
                    onChange={(event) =>
                      setApiImportScope((prev) => ({
                        ...prev,
                        grade_id: event.target.value,
                        unit_id: "",
                      }))
                    }
                  >
                    <option value="">請選擇年級</option>
                    {grades.map((grade) => (
                      <option
                        key={getRecordId(grade)}
                        value={getRecordId(grade)}
                      >
                        {grade.name} / {getRecordId(grade)}
                      </option>
                    ))}
                  </CompactSelect>
                  <SmallMuted>複製的入題程式碼會自動帶入 grade_id。</SmallMuted>
                </Field>

                <Field>
                  <FieldHeader>
                    <label htmlFor="math-bank-api-import-unit">入題單元</label>
                  </FieldHeader>
                  <SearchableSelect
                    id="math-bank-api-import-unit"
                    value={apiImportScope.unit_id}
                    aria-label="入題單元"
                    onChange={(value) =>
                      setApiImportScope((prev) => ({
                        ...prev,
                        unit_id: value,
                      }))
                    }
                    disabled={!apiImportScope.grade_id}
                    options={[
                      { value: "", label: "請選擇單元" },
                      ...filteredUnitsForApiImport.map((unit) => ({ value: getRecordId(unit), label: unit.name, description: getRecordId(unit) })),
                    ]}
                  />
                  <SmallMuted>會覆寫 JSON 裡每題原本的 unit_id。</SmallMuted>
                </Field>

                <Field>
                  <FieldHeader>
                    <label htmlFor="math-bank-api-import-visibility">
                      可見度
                    </label>
                  </FieldHeader>
                  <CompactSelect
                    id="math-bank-api-import-visibility"
                    value={apiImportScope.visibility}
                    onChange={(event) =>
                      setApiImportScope((prev) => ({
                        ...prev,
                        visibility: event.target.value,
                      }))
                    }
                  >
                    {questionVisibilities.map((visibility) => (
                      <option key={visibility.value} value={visibility.value}>
                        {visibility.label}
                      </option>
                    ))}
                  </CompactSelect>
                  <SmallMuted>
                    預設公開，會覆寫 JSON 裡每題原本的 visibility。
                  </SmallMuted>
                </Field>

                <Field>
                  <FieldHeader>
                    <label>題目來源</label>
                  </FieldHeader>
                  <QuestionSourceField
                    id="math-bank-api-import-source"
                    inputId="math-bank-api-import-source-input"
                    value={apiImportScope.question_source}
                    sourceOptions={questionSources}
                    onChange={(value) =>
                      setApiImportScope((prev) => ({
                        ...prev,
                        question_source: value,
                      }))
                    }
                  />
                  <SmallMuted>留空就是無，會覆寫 JSON 裡每題原本的 question_source。</SmallMuted>
                </Field>

                <Field>
                  <FieldHeader>
                    <label htmlFor="math-bank-api-import-status">狀態</label>
                  </FieldHeader>
                  <CompactSelect
                    id="math-bank-api-import-status"
                    value={apiImportScope.status}
                    onChange={(event) =>
                      setApiImportScope((prev) => ({
                        ...prev,
                        status: event.target.value,
                      }))
                    }
                  >
                    {questionStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </CompactSelect>
                  <SmallMuted>
                    預設草稿，會覆寫 JSON 裡每題原本的 status。
                  </SmallMuted>
                </Field>
              </FieldGrid>

              <ApiGuideGrid>
                <ApiGuideCard>
                  <CodeHeader>
                    <span>
                      <FaApple />
                      macOS / Linux 簡易版
                    </span>
                    <SmallIconButton
                      type="button"
                      title="複製 macOS 簡易版"
                      onClick={() =>
                        copyApiImportCommand(
                          macSimpleImportCommand,
                          "macOS 簡易版已複製",
                          "macOS 簡易版複製失敗",
                        )
                      }
                    >
                      <FaCopy />
                    </SmallIconButton>
                  </CodeHeader>
                  <ApiCodeBlock>{macSimpleImportCommand}</ApiCodeBlock>
                </ApiGuideCard>

                <ApiGuideCard>
                  <CodeHeader>
                    <span>
                      <FaApple />
                      macOS / Linux 時間軸版
                    </span>
                    <SmallIconButton
                      type="button"
                      title="複製 macOS 時間軸版"
                      onClick={() =>
                        copyApiImportCommand(
                          macTimelineImportCommand,
                          "macOS 時間軸版已複製",
                          "macOS 時間軸版複製失敗",
                        )
                      }
                    >
                      <FaCopy />
                    </SmallIconButton>
                  </CodeHeader>
                  <ApiCodeBlock>{macTimelineImportCommand}</ApiCodeBlock>
                </ApiGuideCard>

                <ApiGuideCard>
                  <CodeHeader>
                    <span>
                      <FaWindows />
                      Windows PowerShell 簡易版
                    </span>
                    <SmallIconButton
                      type="button"
                      title="複製 Windows 簡易版"
                      onClick={() =>
                        copyApiImportCommand(
                          windowsSimpleImportCommand,
                          "Windows 簡易版已複製",
                          "Windows 簡易版複製失敗",
                        )
                      }
                    >
                      <FaCopy />
                    </SmallIconButton>
                  </CodeHeader>
                  <ApiCodeBlock>{windowsSimpleImportCommand}</ApiCodeBlock>
                </ApiGuideCard>

                <ApiGuideCard>
                  <CodeHeader>
                    <span>
                      <FaWindows />
                      Windows PowerShell 時間軸版
                    </span>
                    <SmallIconButton
                      type="button"
                      title="複製 Windows 時間軸版"
                      onClick={() =>
                        copyApiImportCommand(
                          windowsTimelineImportCommand,
                          "Windows 時間軸版已複製",
                          "Windows 時間軸版複製失敗",
                        )
                      }
                    >
                      <FaCopy />
                    </SmallIconButton>
                  </CodeHeader>
                  <ApiCodeBlock>{windowsTimelineImportCommand}</ApiCodeBlock>
                </ApiGuideCard>
              </ApiGuideGrid>
            </ApiGuidePanel>
          )}

          {activeTab === "duplicate-scan" && (
            <QuestionWorkspace>
              <DuplicateScanPanel>
                <PanelHeader>
                  <PanelTitleGroup>
                    <PanelTitle>
                      <FaFlag />
                      重複題目檢查
                    </PanelTitle>
                    <ApiGuideIntro>
                      掃描題庫中的題目文字、答案文字與數字、圖片描述及 Python
                      圖形程式碼。相關的兩題以上會依相似關聯合併為一組，
                      組內每題不一定彼此相同，請檢查後再一次處理整組。
                      已處理組新增相關題目或內容變更後，重新掃描會回到待處理。
                      年級、單元與來源決定掃描範圍，只比對範圍內的題目；
                      未指定範圍時會掃描整個題庫。搜尋欄位用來篩選已掃描的結果。
                    </ApiGuideIntro>
                  </PanelTitleGroup>
                </PanelHeader>

                <DuplicateReviewNavigation aria-label="重複題目處理狀態">
                  <AnswerStateButton
                    type="button"
                    $active={duplicateReviewStatus === "pending"}
                    aria-pressed={duplicateReviewStatus === "pending"}
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onClick={() => changeDuplicateReviewStatus("pending")}
                  >
                    待處理{duplicateScanResult ? ` (${duplicateScanResult.pending_count ?? getDuplicateGroups(duplicateScanResult).length})` : ""}
                  </AnswerStateButton>
                  <DuplicateHistoryButton
                    type="button"
                    $active={duplicateReviewStatus === "handled"}
                    aria-label="已處理紀錄"
                    aria-pressed={duplicateReviewStatus === "handled"}
                    title="已處理紀錄"
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onClick={() => changeDuplicateReviewStatus("handled")}
                  >
                    <FaHistory />
                    {duplicateScanResult && <span>{duplicateScanResult.handled_count ?? 0}</span>}
                  </DuplicateHistoryButton>
                  <SmallMuted>
                    {duplicateReviewStatus === "handled" ? "已處理紀錄" : "待處理題目"}
                  </SmallMuted>
                </DuplicateReviewNavigation>

                <DuplicateScanToolbar>
                  <SearchBox>
                    <FaSearch />
                    <input
                      value={duplicateScanSearch}
                      placeholder="篩選掃描結果的 UUID／題目／答案／來源"
                      onChange={(event) =>
                        setDuplicateScanSearch(event.target.value)
                      }
                    />
                  </SearchBox>
                  <QuestionFilterSelect
                    value={duplicateScanFilters.grade_id}
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onChange={(event) =>
                      selectGradeForDuplicateScan(event.target.value)
                    }
                    aria-label="重複檢查年級"
                  >
                    <option value="">全部年級</option>
                    {grades.map((grade) => (
                      <option
                        key={getRecordId(grade)}
                        value={getRecordId(grade)}
                      >
                        {grade.name}
                      </option>
                    ))}
                  </QuestionFilterSelect>
                  <SearchableSelect
                    allowMultiple placeholderValues={["", "__none__"]} placeholder="單元"
                    value={duplicateScanFilters.unit_id}
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onChange={(value) =>
                      updateDuplicateScanFilters((current) => ({
                        ...current,
                        unit_id: value,
                      }))
                    }
                    aria-label="重複檢查單元"
                    options={[
                      { value: "", label: "全部單元" },
                      ...duplicateScanUnits.map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                    ]}
                  />
                  <SearchableSelect
                    allowMultiple placeholderValues={["", "__none__"]} placeholder="來源"
                    value={duplicateScanFilters.question_source}
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onChange={(value) =>
                      updateDuplicateScanFilters((current) => ({
                        ...current,
                        question_source: value,
                      }))
                    }
                    aria-label="重複檢查來源"
                    options={[
                      { value: "", label: "全部來源" },
                      ...questionSources.map((source) => ({ value: source, label: source })),
                    ]}
                  />
                  <IconTextButton
                    type="button"
                    $primary
                    disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                    onClick={() => scanDuplicateQuestions()}
                  >
                    {duplicateScanLoading ? <SpinningIcon /> : <FaSearch />}
                    {duplicateScanLoading ? "掃描中" : hasDuplicateScanScope ? "掃描所選範圍" : "掃描整個題庫"}
                  </IconTextButton>
                </DuplicateScanToolbar>

                {duplicateScanJob && (
                  <DuplicateScanProgress aria-live="polite">
                    <DuplicatePairHeader>
                      <strong>
                        {duplicateScanJob.status === "failed" ? "掃描失敗"
                          : duplicateScanPhaseLabels[duplicateScanJob.phase] || "掃描中"}
                        {` · ${Math.min(100, Math.max(0, Number(duplicateScanJob.progress) || 0))}%`}
                      </strong>
                      <span>已等待 {duplicateScanJob.elapsed_seconds || 0} 秒</span>
                    </DuplicatePairHeader>
                    <DuplicateScanProgressBar
                      aria-label="重複題目掃描進度"
                      max={100}
                      value={Math.min(100, Math.max(0, Number(duplicateScanJob.progress) || 0))}
                    />
                    <DuplicateScanSummary>
                      <span>已讀取 {duplicateScanJob.processed_count || 0}／{duplicateScanJob.total_count || 0} 題</span>
                      <span>已比對 {duplicateScanJob.compared_count || 0} 次</span>
                      <span>相似配對 {duplicateScanJob.matched_count || 0} 對</span>
                    </DuplicateScanSummary>
                    {duplicateScanConnectionLost && duplicateScanLoading && (
                      <span role="status">進度連線暫時中斷，正在重新連線...</span>
                    )}
                    {duplicateScanJob.worker_waiting && duplicateScanLoading && (
                      <SmallMuted role="status">工作已排隊，正在等待掃描程序接手；若持續等待，請確認 worker 已啟動。</SmallMuted>
                    )}
                  </DuplicateScanProgress>
                )}

                {duplicateScanResult && !duplicateScanLoading && (
                  <DuplicateScanSummary>
                    <span>已掃描 {duplicateScanResult.scanned_count} 題</span>
                    <span>找到 {duplicateScanResult.group_count ?? duplicateScanResult.candidate_count} 組</span>
                    <span>目前顯示 {visibleDuplicateGroups.length} 組</span>
                    {duplicateScanResult.truncated && (
                      <span>結果已達顯示上限，可縮小範圍繼續檢查</span>
                    )}
                    {duplicateScanResult.fuzzy_scan_incomplete && (
                      <span role="status">部分大量相似答案的題目尚未完整比對。</span>
                    )}
                  </DuplicateScanSummary>
                )}

                {duplicateScanResult?.schema_version === 2 && !duplicateScanLoading && (
                  <DuplicateScanSummary>
                    <IconTextButton type="button" disabled={duplicateResultsLoading || duplicateReviewSavingKey || (duplicateScanResult.page || 1) <= 1}
                      onClick={() => loadDuplicateResults(duplicateScanResult.scan_job_id, duplicateReviewStatus, duplicateScanResult.page - 1, duplicateScanSearch)}>
                      上一頁
                    </IconTextButton>
                    <span>第 {duplicateScanResult.page || 1} 頁 · 共 {duplicateScanResult.total_groups ?? 0} 組</span>
                    <IconTextButton type="button" disabled={duplicateResultsLoading || duplicateReviewSavingKey || !duplicateScanResult.has_more}
                      onClick={() => loadDuplicateResults(duplicateScanResult.scan_job_id, duplicateReviewStatus, duplicateScanResult.page + 1, duplicateScanSearch)}>
                      下一頁
                    </IconTextButton>
                    <IconTextButton type="button" disabled={duplicateResultsLoading || duplicateReviewSavingKey}
                      onClick={() => loadDuplicateResults(duplicateScanResult.scan_job_id, duplicateReviewStatus, duplicateScanResult.page || 1, duplicateScanSearch)}>
                      重新載入結果
                    </IconTextButton>
                  </DuplicateScanSummary>
                )}

                {duplicateScanLoading || duplicateResultsLoading ? (
                  <InlineLoading>
                    <LoadingText>{duplicateScanLoading ? "掃描中..." : "載入結果中..."}</LoadingText>
                  </InlineLoading>
                ) : duplicateScanResult && !visibleDuplicateGroups.length ? (
                  <EmptyLine>
                    {normalizeSearchText(duplicateScanSearch)
                      ? "目前搜尋沒有符合的重複題目群組"
                      : Number(duplicateReviewStatus === "handled" ? duplicateScanResult.handled_count : duplicateScanResult.pending_count) > 0
                        ? "本次載入的群組已處理完，請重新掃描以載入其他群組。"
                        : duplicateReviewStatus === "handled"
                          ? "目前條件下沒有已處理紀錄"
                          : "目前條件下沒有待處理的疑似重複題目"}
                  </EmptyLine>
                ) : (
                  <DuplicatePairList>
                    {visibleDuplicateGroups.map((group) => (
                      <DuplicatePairCard
                        key={group.group_key}
                        $level={group.level}
                        aria-label={`疑似重複群組：${group.question_count ?? group.questions.length} 題`}
                      >
                        <DuplicatePairHeader>
                          <DuplicateScore $level={group.level}>
                            最高相似度 {group.score}%
                          </DuplicateScore>
                          <SmallMuted>同組 {group.question_count ?? group.questions.length} 題</SmallMuted>
                          <BulkQuestionFlags>
                            {(group.reasons || []).map((reason) => (
                              <BulkFlag key={reason} $tone="duplicate">
                                {reason}
                              </BulkFlag>
                            ))}
                            {group.signals?.image_text_incomplete && (
                              <BulkFlag>部分圖片沒有 OCR 文字</BulkFlag>
                            )}
                          </BulkQuestionFlags>
                          {group.signature && (
                            <IconTextButton
                              type="button"
                              disabled={saving || Boolean(duplicateReviewSavingKey) || duplicateScanLoading}
                              onClick={() => saveDuplicateReview(group, duplicateReviewStatus === "handled" ? "pending" : "handled")}
                            >
                              {duplicateReviewSavingKey === group.group_key
                                ? <SpinningIcon />
                                : duplicateReviewStatus === "handled" ? <FaSyncAlt /> : <FaCheck />}
                              {duplicateReviewSavingKey === group.group_key
                                ? "儲存中..."
                                : duplicateReviewStatus === "handled" ? "回到待處理" : "已處理"}
                            </IconTextButton>
                          )}
                        </DuplicatePairHeader>
                        {group.review_status === "handled" && group.reviewed_at && (
                          <SmallMuted>處理時間：{formatDraftUpdatedAt(group.reviewed_at)}</SmallMuted>
                        )}
                        <DuplicatePairGrid>
                          {group.questions.map((question) => (
                            <DuplicateQuestionCard
                              key={question.id}
                              role="button"
                              tabIndex={0}
                              onClick={(event) => queueQuestionPreview(event, question)}
                              onDoubleClick={async (event) => {
                                if (event.target.closest("button, input, a")) return;
                                cancelQuestionPreview();
                                try {
                                  const fullQuestion = await getQuestion({ authToken, id: question.id });
                                  openQuestionEditor(fullQuestion);
                                } catch (error) {
                                  setNotice({ tone: "error", text: error.message || "無法載入題目編輯資料" });
                                }
                              }}
                              onKeyDown={(event) => {
                                if (
                                  event.target === event.currentTarget &&
                                  (event.key === "Enter" || event.key === " ")
                                ) {
                                  event.preventDefault();
                                  showQuestionPreview(question);
                                }
                              }}
                              aria-label="單擊預覽疑似重複題目，雙擊編輯題目"
                              title="單擊預覽題目，雙擊編輯題目"
                            >
                              <DuplicateQuestionHeader>
                                <QuestionIdLine>
                                  <SmallIconButton
                                    type="button"
                                    title="複製題目 UUID"
                                    aria-label={`複製題目 UUID ${question.id}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      copyQuestionId(question.id);
                                    }}
                                  >
                                    <FaCopy />
                                  </SmallIconButton>
                                  <span>{question.id}</span>
                                </QuestionIdLine>
                                <DuplicateDeleteButton
                                  type="button"
                                  title="刪除題目"
                                  aria-label={`刪除題目 ${question.id}`}
                                  disabled={saving || duplicateScanLoading || Boolean(duplicateReviewSavingKey)}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    cancelQuestionPreview();
                                    handleDeleteQuestion(question, { refreshDuplicates: true });
                                  }}
                                >
                                  <FaTrash />
                                </DuplicateDeleteButton>
                              </DuplicateQuestionHeader>
                              <BulkQuestionMeta>
                                <span>{question.grade?.name || "未分類"}</span>
                                <span>{question.unit?.name || "未分類"}</span>
                                <span>{question.question_source || "無來源"}</span>
                              </BulkQuestionMeta>
                              <QuestionText>
                                <MathText
                                  source={question.prompt_md || "只有圖片的題目"}
                                  fallback=""
                                />
                              </QuestionText>
                              <QuestionDetailBlock $tone="answer" $empty={!hasAnswerContent(question)}>
                                <QuestionDetailLabel $tone="answer">
                                  答案
                                </QuestionDetailLabel>
                                <QuestionDetailText>
                                  <MathText
                                    source={getAnswerDisplayText(question, "尚無答案")}
                                    fallback=""
                                  />
                                </QuestionDetailText>
                              </QuestionDetailBlock>
                              <AssetImagePreviews
                                assets={question.assets || []}
                                compact
                                limit={3}
                              />
                            </DuplicateQuestionCard>
                          ))}
                        </DuplicatePairGrid>
                        {duplicateScanResult?.schema_version === 2 && (!group.questions_page || group.has_more_questions) && (
                          <IconTextButton type="button" disabled={Boolean(duplicateGroupLoading) || duplicateResultsLoading}
                            onClick={() => loadDuplicateGroup(group)}>
                            {duplicateGroupLoading === group.group_key ? "載入題目中..."
                              : group.questions_page ? "載入更多同組題目" : "載入完整同組題目"}
                          </IconTextButton>
                        )}
                      </DuplicatePairCard>
                    ))}
                  </DuplicatePairList>
                )}
              </DuplicateScanPanel>
            </QuestionWorkspace>
          )}

          {activeTab === "bulk-import" && (
            <BulkImportGrid>
              <BulkImportPanel>
                <PanelHeader>
                  <PanelTitleGroup>
                    <PanelTitle>
                      <FaLayerGroup />
                      大量入題
                    </PanelTitle>
                    <ApiGuideIntro>
                      貼上題目文字後先切割成預覽清單，再逐題檢查、開 modal
                      編輯，最後批次送入題庫。
                    </ApiGuideIntro>
                  </PanelTitleGroup>
                </PanelHeader>

                <BulkDraftToolbar>
                  <BulkProgress>
                    <strong>暫存</strong>
                    <span>{bulkDraftSessions.length} 筆紀錄</span>
                    {selectedBulkDraftId && <span>已載入</span>}
                  </BulkProgress>
                  <BulkActionGroup>
                    <CompactSelect
                      value={selectedBulkDraftId}
                      disabled={bulkDraftLoading || bulkDraftSaving}
                      onChange={(event) => {
                        const draftId = event.target.value;
                        if (draftId) {
                          loadBulkDraftSession(draftId);
                        } else {
                          setSelectedBulkDraftId("");
                        }
                      }}
                      aria-label="大量入題暫存歷史"
                    >
                      <option value="">
                        {bulkDraftLoading ? "讀取暫存中" : "選擇暫存歷史"}
                      </option>
                      {bulkDraftSessions.map((draft) => (
                        <option key={draft.id} value={draft.id}>
                          {draft.title} {formatDraftUpdatedAt(draft.updated_at)}
                        </option>
                      ))}
                    </CompactSelect>
                    <IconTextButton
                      type="button"
                      onClick={saveBulkDraftSession}
                      title="儲存暫存（Command/Ctrl + S）"
                      disabled={
                        bulkDraftSaving || bulkImporting || bulkWordParsing
                      }
                    >
                      {bulkDraftSaving ? <SpinningIcon /> : <FaSave />}
                      {bulkDraftSaving ? "暫存中" : "儲存暫存"}
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      $danger
                      onClick={deleteBulkDraftSession}
                      disabled={
                        !selectedBulkDraftId ||
                        bulkDraftLoading ||
                        bulkDraftSaving
                      }
                    >
                      <FaTrash />
                      刪除暫存
                    </IconTextButton>
                  </BulkActionGroup>
                </BulkDraftToolbar>

                <FieldGrid>
                  <Field>
                    <label>年級</label>
                    <CompactSelect
                      value={bulkImportScope.grade_id}
                      onChange={(event) =>
                        updateBulkImportScope("grade_id", event.target.value)
                      }
                    >
                      <option value="">選擇年級</option>
                      {grades.map((grade) => (
                        <option
                          key={getRecordId(grade)}
                          value={getRecordId(grade)}
                        >
                          {grade.name}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>單元</label>
                    <SearchableSelect
                      value={bulkImportScope.unit_id}
                      disabled={!bulkImportScope.grade_id}
                      aria-label="大量入題單元"
                      onChange={(value) => updateBulkImportScope("unit_id", value)}
                      options={[
                        { value: "", label: "選擇單元" },
                        ...filteredUnitsForBulkImport.map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                      ]}
                    />
                  </Field>
                  {subjectCategories[subject] && (
                    <Field as="div">
                      <QuestionCategorySelect
                        subject={subject}
                        value={bulkImportScope.category}
                        label="匯入分類"
                        onChange={(value) => updateBulkImportScope("category", value)}
                      />
                    </Field>
                  )}
                  <Field>
                    <label>題型</label>
                    <CompactSelect
                      value={bulkImportScope.type}
                      onChange={(event) =>
                        updateBulkImportScope("type", event.target.value)
                      }
                    >
                      {questionTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>難度</label>
                    <CompactSelect
                      value={bulkImportScope.difficulty}
                      onChange={(event) =>
                        updateBulkImportScope("difficulty", event.target.value)
                      }
                    >
                      {questionDifficulties.map((difficulty) => (
                        <option key={difficulty.value} value={difficulty.value}>
                          {difficulty.label}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>狀態</label>
                    <CompactSelect
                      value={bulkImportScope.status}
                      onChange={(event) =>
                        updateBulkImportScope("status", event.target.value)
                      }
                    >
                      {questionStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>可見性</label>
                    <CompactSelect
                      value={bulkImportScope.visibility}
                      onChange={(event) =>
                        updateBulkImportScope("visibility", event.target.value)
                      }
                    >
                      {questionVisibilities.map((visibility) => (
                        <option key={visibility.value} value={visibility.value}>
                          {visibility.label}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>思維/思路</label>
                    <input
                      value={bulkImportScope.thinking}
                      placeholder="用逗號分隔，例如：移項, 化簡"
                      onChange={(event) =>
                        updateBulkImportScope("thinking", event.target.value)
                      }
                    />
                  </Field>
                  <Field>
                    <label>作者</label>
                    <input
                      value={bulkImportScope.author_label}
                      placeholder="留空則使用登入者名稱"
                      maxLength={150}
                      onChange={(event) =>
                        updateBulkImportScope(
                          "author_label",
                          event.target.value,
                        )
                      }
                    />
                  </Field>
                  <Field>
                    <label>題目來源</label>
                    <QuestionSourceField
                      id="bulk-question-source-select"
                      inputId="bulk-question-source-input"
                      value={bulkImportScope.question_source}
                      sourceOptions={questionSources}
                      onChange={(value) =>
                        updateBulkImportScope("question_source", value)
                      }
                    />
                  </Field>
                  <Field>
                    <label>好題</label>
                    <ToggleLabel>
                      <input
                        type="checkbox"
                        checked={bulkImportScope.is_good_question}
                        onChange={(event) =>
                          updateBulkImportScope(
                            "is_good_question",
                            event.target.checked,
                          )
                        }
                      />
                      好題星星
                    </ToggleLabel>
                  </Field>
                  <ToggleLabel>
                    <input
                      type="checkbox"
                      checked={bulkAllowDuplicate}
                      onChange={(event) =>
                        setBulkAllowDuplicate(event.target.checked)
                      }
                    />
                    允許重複題入題
                  </ToggleLabel>
                  <Field>
                    <label>Word 解析</label>
                    <CompactSelect
                      value={bulkWordMode}
                      disabled={bulkWordParsing || bulkImporting}
                      onChange={(event) => setBulkWordMode(event.target.value)}
                    >
                      <option value="fast">快速解析</option>
                      <option value="accurate">精準解析</option>
                    </CompactSelect>
                  </Field>
                  <Field>
                    <label>切題方式</label>
                    <CompactSelect
                      value={bulkSplitMode}
                      disabled={bulkWordParsing || bulkImporting}
                      onChange={(event) => setBulkSplitMode(event.target.value)}
                    >
                      {bulkSplitModeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </CompactSelect>
                  </Field>
                </FieldGrid>

                <BulkWordDropZone
                  type="button"
                  $dragOver={bulkWordDragOver}
                  disabled={bulkWordParsing || bulkImporting}
                  onClick={() => bulkWordInputRef.current?.click()}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setBulkWordDragOver(true);
                  }}
                  onDragLeave={() => setBulkWordDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setBulkWordDragOver(false);
                    const wordFile = Array.from(
                      event.dataTransfer.files || [],
                    ).find(isWordDocumentFile);
                    handleBulkWordFile(wordFile);
                  }}
                >
                  {bulkWordParsing ? <SpinningIcon /> : <FaFileWord />}
                  <strong>
                    {bulkWordParsing ? "Word 解析中" : "拖拉或貼上 Word 檔切割入題"}
                  </strong>
                  <span>.docx 會先解析成題目文字，再建立下方預覽清單。</span>
                  {bulkWordParsing && (
                    <BulkWordProgressPanel
                      $status={bulkWordProgress.status}
                      data-status={bulkWordProgress.status}
                    >
                      <BulkWordProgressHeader>
                        <BulkWordProgressTitle>
                          {bulkWordProgress.status === "success" ? (
                            <FaCheck />
                          ) : bulkWordProgress.status === "error" ? (
                            <FaTimes />
                          ) : (
                            <FaSyncAlt />
                          )}
                          <strong>
                            {bulkWordProgress.status === "success"
                              ? "Word 解析完成"
                              : bulkWordProgress.status === "error"
                                ? "Word 解析失敗"
                                : "Word 解析中"}
                          </strong>
                        </BulkWordProgressTitle>
                        <BulkWordProgressMeta>
                          {bulkWordProgress.fileName || "Word 檔案"}
                          {!!bulkWordProgress.elapsedSeconds && (
                            <span>{bulkWordProgress.elapsedSeconds}s</span>
                          )}
                        </BulkWordProgressMeta>
                      </BulkWordProgressHeader>
                      <BulkWordProgress>
                        <BulkWordProgressBar
                          role="progressbar"
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-valuenow={bulkWordProgress.percent}
                          $active={bulkWordProgress.status === "loading"}
                          $indeterminate={
                            bulkWordProgress.status === "loading" &&
                            bulkWordProgress.percent < 18
                          }
                        >
                          <span
                            style={{ width: `${bulkWordProgress.percent}%` }}
                          />
                        </BulkWordProgressBar>
                        <BulkWordProgressFooter>
                          <span>
                            {bulkWordProgress.status === "error"
                              ? bulkWordProgress.error || "Word 文件解析失敗"
                              : `${bulkWordProgress.label || "解析中"} · ${bulkWordProgress.percent
                              }%`}
                          </span>
                        </BulkWordProgressFooter>
                      </BulkWordProgress>
                    </BulkWordProgressPanel>
                  )}
                </BulkWordDropZone>
                <HiddenFileInput
                  ref={bulkWordInputRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(event) =>
                    handleBulkWordFile(event.target.files?.[0])
                  }
                />

                <BulkImportSource>
                  <FieldHeader>
                    <label>批次題目原文</label>
                  </FieldHeader>
                  <textarea
                    value={bulkImportText}
                    placeholder={
                      "問題 1. 題目文字\\n答案：...\\n詳解：...\\n\\n第 2 題\\n下一題..."
                    }
                    onChange={(event) => setBulkImportText(event.target.value)}
                  />
                </BulkImportSource>

                <BulkImportToolbar>
                  <BulkProgress>
                    <strong>{bulkQuestions.length} 題</strong>
                    <span>已勾選 {selectedBulkCount}</span>
                    <span>可入題 {bulkReadyCount}</span>
                    <span>已編輯 {bulkEditedCount}</span>
                    <span>待補 {bulkMissingCount}</span>
                    <span>待辨識 {bulkFlagSummary.needsReview}</span>
                    <span>含公式 {bulkFlagSummary.formulas}</span>
                    <span>含圖片 {bulkFlagSummary.imageHints}</span>
                    <span>成功 {bulkStatusCounts.success || 0}</span>
                    <span>重複 {bulkStatusCounts.duplicate || 0}</span>
                    <span>失敗 {bulkStatusCounts.error || 0}</span>
                  </BulkProgress>
                  <BulkActionGroup>
                    <IconTextButton
                      type="button"
                      onClick={parseBulkQuestions}
                      disabled={!bulkImportText.trim() || bulkImporting}
                    >
                      <FaSearch />
                      切割預覽
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={applyBulkScopeToQuestions}
                      disabled={!bulkQuestions.length || bulkImporting}
                    >
                      <FaSyncAlt />
                      套用設定
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={() => setAllBulkQuestionsSelected(true)}
                      disabled={!bulkQuestions.length || bulkImporting}
                    >
                      <FaCheck />
                      全選
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={() => setAllBulkQuestionsSelected(false)}
                      disabled={!bulkQuestions.length || bulkImporting}
                    >
                      <FaTimes />
                      取消全選
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={copyBulkQuestionTags}
                      disabled={!bulkCreatedQuestionCount || bulkImporting}
                      title="複製所有已入題或重複題目的 math-question 標籤"
                    >
                      <FaCopy />
                      複製 {bulkCreatedQuestionCount} 題標籤
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={runEveryBulkQuestionSolutionApi}
                      disabled={
                        !bulkQuestions.length ||
                        bulkSolutionProgress.running ||
                        bulkImporting ||
                        bulkWordParsing
                      }
                    >
                      {bulkSolutionProgress.running ? (
                        <SpinningIcon />
                      ) : (
                        <FaMagic />
                      )}
                      {bulkSolutionProgress.running
                        ? `詳解 ${bulkSolutionProgress.current}/${bulkSolutionProgress.total}`
                        : "每題寫詳解"}
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      $primary
                      onClick={importSelectedBulkQuestions}
                      disabled={!selectedBulkCount || bulkImporting || saving}
                    >
                      {bulkImporting ? <SpinningIcon /> : <FaSave />}
                      {bulkImporting
                        ? "入題中"
                        : `入題 ${selectedBulkCount} 題`}
                    </IconTextButton>
                  </BulkActionGroup>
                </BulkImportToolbar>
              </BulkImportPanel>

              <BulkQuestionList>
                {bulkQuestions.length ? (
                  bulkQuestions.map((item, index) => {
                    const flags = detectBulkQuestionFlags(item.form);
                    const missingFields = getQuestionMissingFields(item.form);
                    const gradeName =
                      grades.find(
                        (grade) => getRecordId(grade) === item.form.grade_id,
                      )?.name || "未選年級";
                    const unitName =
                      units.find(
                        (unit) => getRecordId(unit) === item.form.unit_id,
                      )?.name || "未選單元";

                    return (
                      <BulkQuestionCard
                        key={item.localId}
                        data-tab-scroll-anchor={`bulk-question-${item.localId}`}
                        $status={item.importStatus}
                        role="button"
                        tabIndex={0}
                        aria-label="單擊預覽題目，雙擊編輯題目"
                        aria-haspopup="dialog"
                        title="單擊預覽題目，雙擊編輯題目"
                        onClick={(event) => queueQuestionPreview(event, {
                          ...item.form,
                          _bulkLocalId: item.localId,
                          grade: { name: gradeName },
                          unit: { name: unitName },
                        })}
                        onDoubleClick={(event) => {
                          if (event.target.closest("button, input, a")) return;
                          event.stopPropagation();
                          cancelQuestionPreview();
                          if (!bulkImporting) openBulkQuestionEditor(item);
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.target === event.currentTarget &&
                            (event.key === "Enter" || event.key === " ")
                          ) {
                            event.preventDefault();
                            setPreviewQuestion({
                              ...item.form,
                              _bulkLocalId: item.localId,
                              grade: { name: gradeName },
                              unit: { name: unitName },
                            });
                          }
                        }}
                      >
                        <BulkQuestionHeader>
                          <ToggleLabel>
                            <input
                              type="checkbox"
                              checked={item.selected}
                              disabled={bulkImporting}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => toggleBulkQuestion(item.localId)}
                            />
                            第 {item.questionNumber || index + 1} 題
                            {item.form.is_good_question && (
                              <GoodQuestionBadge title="好題">
                                <FaStar />
                                好題
                              </GoodQuestionBadge>
                            )}
                          </ToggleLabel>
                          <RowActions>
                            <InlineToolButton
                              type="button"
                              aria-label={`第 ${item.questionNumber || index + 1} 題題目編輯`}
                              title="編輯題目 Markdown"
                              disabled={bulkImporting}
                              onClick={(event) => {
                                event.stopPropagation();
                                openBulkMarkdownEditor(
                                  item.localId,
                                  "prompt_md",
                                );
                              }}
                            >
                              題目
                            </InlineToolButton>
                            <InlineToolButton
                              type="button"
                              aria-label={`第 ${item.questionNumber || index + 1} 題詳解編輯`}
                              title="編輯詳解 Markdown"
                              disabled={bulkImporting}
                              onClick={(event) => {
                                event.stopPropagation();
                                openBulkMarkdownEditor(
                                  item.localId,
                                  "solution_md",
                                );
                              }}
                            >
                              詳解
                            </InlineToolButton>
                            <InlineToolButton
                              type="button"
                              aria-label={`第 ${item.questionNumber || index + 1} 題 AI 寫詳解`}
                              title="使用 AI 產生這一題的詳解"
                              disabled={
                                bulkImporting ||
                                bulkSolutionProgress.running ||
                                item.aiStatus === "loading"
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                runBulkQuestionSolutionApi(item);
                              }}
                            >
                              {item.aiStatus === "loading" ? (
                                <SpinningIcon />
                              ) : (
                                <FaMagic />
                              )}
                              {item.aiStatus === "loading"
                                ? "產生中"
                                : "AI 寫詳解"}
                            </InlineToolButton>
                            <IconOnlyButton
                              type="button"
                              title="編輯題目"
                              onClick={(event) => {
                                event.stopPropagation();
                                openBulkQuestionEditor(item);
                              }}
                            >
                              <FaEdit />
                            </IconOnlyButton>
                            <IconOnlyButton
                              type="button"
                              title="在下方插入空白題"
                              aria-label={`在第 ${item.questionNumber || index + 1} 題下方插入空白題`}
                              disabled={bulkImporting}
                              onClick={(event) => {
                                event.stopPropagation();
                                insertBlankBulkQuestionBelow(item.localId);
                              }}
                            >
                              <FaPlus />
                            </IconOnlyButton>
                            <IconOnlyButton
                              type="button"
                              title="移除"
                              disabled={bulkImporting}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeBulkQuestion(item.localId);
                              }}
                            >
                              <FaTrash />
                            </IconOnlyButton>
                          </RowActions>
                        </BulkQuestionHeader>
                        <BulkQuestionMeta>
                          <span>{gradeName}</span>
                          <span>{unitName}</span>
                          <span>{item.form.type}</span>
                          <QuestionDifficultyBadge difficulty={item.form.difficulty} />
                          {item.edited && <strong>已編輯</strong>}
                          {item.importStatus !== "idle" && (
                            <strong>
                              {item.importStatus === "loading"
                                ? "入題中"
                                : item.importStatus === "success"
                                  ? "已入題"
                                  : item.importStatus === "duplicate"
                                    ? "重複"
                                    : "失敗"}
                            </strong>
                          )}
                        </BulkQuestionMeta>
                        <BulkQuestionBody>
                          <QuestionText>
                            <MathText
                              source={getQuestionPrompt(item.form) || "尚未輸入題目"}
                              fallback=""
                            />
                          </QuestionText>
                          <QuestionDetailBlock
                            $tone="answer"
                            $empty={!hasAnswerContent(item.form)}
                          >
                            <QuestionDetailLabel $tone="answer">
                              答案
                            </QuestionDetailLabel>
                            <QuestionDetailText>
                              <MathText
                                source={getAnswerDisplayText(item.form)}
                                fallback=""
                              />
                            </QuestionDetailText>
                          </QuestionDetailBlock>
                          <QuestionDetailBlock $empty={!item.form.solution_md}>
                            <QuestionDetailLabel>詳解</QuestionDetailLabel>
                            <QuestionDetailText>
                              <MathText
                                source={item.form.solution_md || "尚未輸入詳解"}
                                fallback=""
                              />
                            </QuestionDetailText>
                          </QuestionDetailBlock>
                        </BulkQuestionBody>
                        <AssetImagePreviews
                          assets={item.form.assets}
                          compact
                          limit={4}
                        />
                        <BulkQuestionFlags>
                          {flags.map((flag) => (
                            <BulkFlag key={flag}>{flag}</BulkFlag>
                          ))}
                          {missingFields.map((field) => (
                            <BulkFlag key={field} $tone="error">
                              缺 {field}
                            </BulkFlag>
                          ))}
                          {item.importMessage && (
                            <BulkFlag $tone={item.importStatus}>
                              {item.importMessage}
                              {item.createdId
                                ? ` · ${shortId(item.createdId)}`
                                : ""}
                            </BulkFlag>
                          )}
                          {item.aiMessage && (
                            <BulkFlag
                              $tone={
                                item.aiStatus === "error" ? "error" : "success"
                              }
                            >
                              {item.aiMessage}
                            </BulkFlag>
                          )}
                        </BulkQuestionFlags>
                      </BulkQuestionCard>
                    );
                  })
                ) : (
                  <EmptyLine>
                    貼上題目後按「切割預覽」建立大量入題清單
                  </EmptyLine>
                )}
              </BulkQuestionList>
            </BulkImportGrid>
          )}
        </>
      )}
      <QuestionPreviewModal
        question={previewQuestion}
        onClose={() => setPreviewQuestion(null)}
        previewMatplotlibCode={previewMatplotlibCode}
        onAssetsRendered={handlePreviewAssetsRendered}
        meta={previewQuestion ? [
          previewQuestion.grade?.name,
          previewQuestion.unit?.name,
        ] : []}
      />
      {bulkEditingQuestion && (
        <BulkModalBackdrop>
          <BulkModal
            ref={bulkModalRef}
            role="dialog"
            aria-label="編輯大量入題"
            aria-modal="true"
            tabIndex={-1}
          >
            <PanelHeader>
              <PanelTitleGroup>
                <PanelTitle>編輯大量入題</PanelTitle>
                <ApiGuideIntro>
                  按「完成」保留修改到預覽清單；按叉叉捨棄本次修改。按大量入題前都不會寫入題庫。
                </ApiGuideIntro>
              </PanelTitleGroup>
              <IconOnlyButton
                type="button"
                title="關閉"
                aria-label="關閉並捨棄本次修改"
                onClick={closeBulkQuestionEditor}
              >
                <FaTimes />
              </IconOnlyButton>
            </PanelHeader>

            <BulkModalBody
              onChange={(event) => {
                if (event.target instanceof HTMLSelectElement) {
                  bulkModalRef.current?.focus();
                }
              }}
            >
              <FieldGrid>
                <Field>
                  <label>年級</label>
                  <select
                    value={bulkEditingQuestion.form.grade_id}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        grade_id: event.target.value,
                        unit_id: "",
                      })
                    }
                  >
                    <option value="">選擇年級</option>
                    {grades.map((grade) => (
                      <option
                        key={getRecordId(grade)}
                        value={getRecordId(grade)}
                      >
                        {grade.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>單元</label>
                  <SearchableSelect
                    value={bulkEditingQuestion.form.unit_id}
                    disabled={!bulkEditingQuestion.form.grade_id}
                    aria-label="編修題目單元"
                    onChange={(value) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        unit_id: value,
                      })
                    }
                    options={[
                      { value: "", label: "選擇單元" },
                      ...units.filter((unit) => getUnitGradeId(unit) === bulkEditingQuestion.form.grade_id)
                        .map((unit) => ({ value: getRecordId(unit), label: unit.name })),
                    ]}
                  />
                </Field>
                {subjectCategories[subject] && (
                  <Field as="div">
                    <QuestionCategorySelect
                      subject={subject}
                      value={bulkEditingQuestion.form.category}
                      label="編修分類"
                      onChange={(category) => updateBulkQuestionForm(bulkEditingQuestion.localId, { category })}
                    />
                  </Field>
                )}
                <Field>
                  <label>題型</label>
                  <select
                    value={bulkEditingQuestion.form.type}
                    onChange={(event) => {
                      const nextType = event.target.value;
                      if (nextType === "choice") {
                        const choiceParts = splitPromptChoiceOptions(
                          bulkEditingQuestion.form.prompt_md,
                        );
                        updateBulkQuestionForm(bulkEditingQuestion.localId, {
                          type: nextType,
                          prompt_md: choiceParts.prompt_md,
                          choice_options: choiceParts.choice_options,
                        });
                        return;
                      }
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        type: nextType,
                        prompt_md:
                          bulkEditingQuestion.form.type === "choice"
                            ? getQuestionPrompt(bulkEditingQuestion.form)
                            : bulkEditingQuestion.form.prompt_md,
                        choice_options: createEmptyChoiceOptions(),
                      });
                    }}
                  >
                    {questionTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>難度</label>
                  <select
                    value={bulkEditingQuestion.form.difficulty}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        difficulty: event.target.value,
                      })
                    }
                  >
                    {questionDifficulties.map((difficulty) => (
                      <option key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>好題</label>
                  <ToggleLabel>
                    <input
                      type="checkbox"
                      checked={bulkEditingQuestion.form.is_good_question}
                      onChange={(event) =>
                        updateBulkQuestionForm(bulkEditingQuestion.localId, {
                          is_good_question: event.target.checked,
                        })
                      }
                    />
                    好題星星
                  </ToggleLabel>
                </Field>
                <Field>
                  <label>狀態</label>
                  <select
                    value={bulkEditingQuestion.form.status}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        status: event.target.value,
                      })
                    }
                  >
                    {questionStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label htmlFor="bulk-question-review-note">草稿疑義／待處理</label>
                  <textarea id="bulk-question-review-note" value={bulkEditingQuestion.form.review_note || ""}
                    onChange={(event) => updateBulkQuestionForm(bulkEditingQuestion.localId, { review_note: event.target.value })} />
                </Field>
                <Field>
                  <label>可見性</label>
                  <select
                    value={bulkEditingQuestion.form.visibility}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        visibility: event.target.value,
                      })
                    }
                  >
                    {questionVisibilities.map((visibility) => (
                      <option key={visibility.value} value={visibility.value}>
                        {visibility.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>思維/思路</label>
                  <input
                    value={bulkEditingQuestion.form.thinking}
                    placeholder="用逗號分隔"
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        thinking: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field>
                  <label>作者</label>
                  <input
                    value={bulkEditingQuestion.form.author_label}
                    placeholder="留空則使用登入者名稱"
                    maxLength={150}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        author_label: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field>
                  <label>題目來源</label>
                  <QuestionSourceField
                    id={`bulk-edit-question-source-select-${bulkEditingQuestion.localId}`}
                    inputId={`bulk-edit-question-source-input-${bulkEditingQuestion.localId}`}
                    value={bulkEditingQuestion.form.question_source}
                    sourceOptions={questionSources}
                    onChange={(value) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        question_source: value,
                      })
                    }
                  />
                </Field>
              </FieldGrid>

              <Field as="div">
                <FieldHeader>
                  <label>
                    {bulkEditingQuestion.form.type === "choice"
                      ? "題幹 Markdown / KaTeX"
                      : "題目 Markdown / KaTeX"}
                  </label>
                  <InlineToolButton
                    type="button"
                    onClick={() =>
                      openBulkMarkdownEditor(
                        bulkEditingQuestion.localId,
                        "prompt_md",
                      )
                    }
                  >
                    <FaExpand />
                    Vim 編輯
                  </InlineToolButton>
                </FieldHeader>
                <textarea
                  rows="5"
                  style={getMarkdownTextareaStyle(
                    bulkEditingQuestion.form.prompt_md,
                    5,
                  )}
                  value={bulkEditingQuestion.form.prompt_md}
                  onChange={(event) =>
                    updateBulkQuestionForm(bulkEditingQuestion.localId, {
                      prompt_md: event.target.value,
                    })
                  }
                />
              </Field>

              {bulkEditingQuestion.form.type === "choice" && (
                <Field as="div">
                  <FieldHeader>
                    <label>選項</label>
                  </FieldHeader>
                  <ChoiceOptionGrid>
                    {(
                      bulkEditingQuestion.form.choice_options ||
                      createEmptyChoiceOptions()
                    ).map((option, optionIndex) => (
                      <ChoiceOptionRow key={option.label}>
                        <strong>({option.label})</strong>
                        <textarea
                          rows="2"
                          value={option.text}
                          placeholder={`輸入 ${option.label} 選項，可用 Markdown / KaTeX`}
                          onChange={(event) => {
                            const options = [
                              ...(bulkEditingQuestion.form.choice_options ||
                                createEmptyChoiceOptions()),
                            ];
                            options[optionIndex] = {
                              ...options[optionIndex],
                              text: event.target.value,
                            };
                            updateBulkQuestionForm(
                              bulkEditingQuestion.localId,
                              {
                                choice_options: options,
                              },
                            );
                          }}
                        />
                      </ChoiceOptionRow>
                    ))}
                  </ChoiceOptionGrid>
                </Field>
              )}

              <TwoColumnText>
                <Field as="div">
                  <FieldHeader>
                    <label>答案 Markdown / KaTeX</label>
                    <InlineToolButton
                      type="button"
                      onClick={() =>
                        openBulkMarkdownEditor(
                          bulkEditingQuestion.localId,
                          "answer_md",
                        )
                      }
                    >
                      <FaExpand />
                      Vim 編輯
                    </InlineToolButton>
                  </FieldHeader>
                  <textarea
                    rows="4"
                    style={getMarkdownTextareaStyle(
                      bulkEditingQuestion.form.answer_md,
                      4,
                    )}
                    value={bulkEditingQuestion.form.answer_md}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        answer_md: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field as="div">
                  <FieldHeader>
                    <label>詳解 Markdown / KaTeX</label>
                    <InlineToolButton
                      type="button"
                      onClick={() =>
                        openBulkMarkdownEditor(
                          bulkEditingQuestion.localId,
                          "solution_md",
                        )
                      }
                    >
                      <FaExpand />
                      Vim 編輯
                    </InlineToolButton>
                  </FieldHeader>
                  <textarea
                    rows="4"
                    style={getMarkdownTextareaStyle(
                      bulkEditingQuestion.form.solution_md,
                      4,
                    )}
                    value={bulkEditingQuestion.form.solution_md}
                    onChange={(event) =>
                      updateBulkQuestionForm(bulkEditingQuestion.localId, {
                        solution_md: event.target.value,
                      })
                    }
                  />
                </Field>
              </TwoColumnText>

              <QuestionPreviewPanel>
                <PreviewHeader>
                  <PanelTitle>預覽</PanelTitle>
                </PreviewHeader>
                <PreviewGrid>
                  <PreviewBlock>
                    <PreviewLabel>題目</PreviewLabel>
                    <PreviewContent
                      $empty={!hasQuestionContent(bulkEditingQuestion.form)}
                    >
                      <MathText
                        source={
                          getQuestionPrompt(bulkEditingQuestion.form) ||
                          "尚未輸入題目"
                        }
                        fallback=""
                      />
                      <AssetImagePreviews
                        assets={getPreviewableAssets(
                          bulkEditingQuestion.form.assets,
                          "prompt",
                        )}
                      />
                    </PreviewContent>
                  </PreviewBlock>
                  <PreviewBlock $tone="answer" $empty={!hasAnswerContent(bulkEditingQuestion.form)}>
                    <PreviewLabel>答案</PreviewLabel>
                    <PreviewContent
                      $empty={
                        !bulkEditingQuestion.form.answer_md.trim() &&
                        !getPreviewableAssets(
                          bulkEditingQuestion.form.assets,
                          "answer",
                        ).length
                      }
                    >
                      <MathText
                        source={
                          bulkEditingQuestion.form.answer_md.trim() ||
                          (getPreviewableAssets(
                            bulkEditingQuestion.form.assets,
                            "answer",
                          ).length
                            ? ""
                            : "尚未輸入答案")
                        }
                      />
                      <AssetImagePreviews
                        assets={getPreviewableAssets(
                          bulkEditingQuestion.form.assets,
                          "answer",
                        )}
                      />
                    </PreviewContent>
                  </PreviewBlock>
                  <PreviewBlock>
                    <PreviewLabel>詳解</PreviewLabel>
                    <PreviewContent
                      $empty={
                        !bulkEditingQuestion.form.solution_md.trim() &&
                        !getPreviewableAssets(
                          bulkEditingQuestion.form.assets,
                          "solution",
                        ).length
                      }
                    >
                      <MathText
                        source={
                          bulkEditingQuestion.form.solution_md.trim() ||
                          (getPreviewableAssets(
                            bulkEditingQuestion.form.assets,
                            "solution",
                          ).length
                            ? ""
                            : "尚未輸入詳解")
                        }
                      />
                      <AssetImagePreviews
                        assets={getPreviewableAssets(
                          bulkEditingQuestion.form.assets,
                          "solution",
                        )}
                      />
                    </PreviewContent>
                  </PreviewBlock>
                </PreviewGrid>
              </QuestionPreviewPanel>

              <AssetSection>
                <AssetHeader>
                  <PanelTitle>
                    <FaImage />
                    圖片素材
                  </PanelTitle>
                  <IconTextButton
                    type="button"
                    onClick={() =>
                      addBulkQuestionAsset(bulkEditingQuestion.localId)
                    }
                  >
                    <FaPlus />
                    新增圖片
                  </IconTextButton>
                </AssetHeader>
                {bulkEditingQuestion.form.assets.length === 0 ? (
                  <EmptyLine>無圖片</EmptyLine>
                ) : (
                  <AssetList>
                    {bulkEditingQuestion.form.assets.map((asset, index) => (
                      <AssetRow key={index}>
                        <QuestionAssetInputMode asset={asset}>
                          {(inputMode, modeControl) => (
                            <>
                              <AssetMetadata>
                                <strong>
                                  <FaImage /> 圖片 {index + 1}
                                </strong>
                                {modeControl}
                                <Field>
                                  <label>排序</label>
                                  <input
                                    type="number"
                                    value={asset.sort_order}
                                    onChange={(event) =>
                                      updateBulkQuestionAsset(
                                        bulkEditingQuestion.localId,
                                        index,
                                        { sort_order: event.target.value },
                                      )
                                    }
                                  />
                                </Field>
                                <IconOnlyButton
                                  type="button"
                                  title="移除圖片"
                                  onClick={() =>
                                    removeBulkQuestionAsset(
                                      bulkEditingQuestion.localId,
                                      index,
                                    )
                                  }
                                >
                                  <FaTrash />
                                </IconOnlyButton>
                              </AssetMetadata>
                              <QuestionAssetRoleButtons
                                value={asset.role}
                                onChange={(role) =>
                                  updateBulkQuestionAsset(
                                    bulkEditingQuestion.localId,
                                    index,
                                    { role },
                                  )
                                }
                              />
                              <AssetWorkspace>
                                {inputMode !== "url" ? (
                                  <AssetSourcePanel>
                                    <AssetCodeToolbar>
                                      {inputMode === "code" && (
                                        <Field>
                                          <label>來源語言</label>
                                          <select
                                            value={asset.source_language}
                                            onChange={(event) =>
                                              updateBulkQuestionAsset(
                                                bulkEditingQuestion.localId,
                                                index,
                                                {
                                                  source_language:
                                                    event.target.value,
                                                },
                                              )
                                            }
                                          >
                                            {assetLanguages.map((language) => (
                                              <option
                                                key={language.value}
                                                value={language.value}
                                              >
                                                {language.label}
                                              </option>
                                            ))}
                                          </select>
                                        </Field>
                                      )}
                                      <div>
                                        <IconTextButton
                                          type="button"
                                          title={
                                            asset.source_language === "svg"
                                              ? "用 Vim 編輯 SVG 圖"
                                              : "用 Vim 編輯 Python 圖"
                                          }
                                          onClick={() => {
                                            updateBulkQuestionAsset(
                                              bulkEditingQuestion.localId,
                                              index,
                                              {
                                                source_language:
                                                  asset.source_language ||
                                                  "python",
                                              },
                                            );
                                            openBulkAssetSourceEditor(
                                              bulkEditingQuestion.localId,
                                              index,
                                              Boolean(asset.url),
                                            );
                                          }}
                                        >
                                          <FaExpand /> Vim 編輯
                                        </IconTextButton>
                                        <IconTextButton
                                          type="button"
                                          title="Copy Figure Code"
                                          disabled={!asset.source_code?.trim()}
                                          onClick={() =>
                                            copyAssetSourceCode(
                                              asset.source_code,
                                            )
                                          }
                                        >
                                          <FaCopy /> 複製程式碼
                                        </IconTextButton>
                                      </div>
                                    </AssetCodeToolbar>
                                    <Field>
                                      <label>
                                        {asset.source_language === "svg"
                                          ? "SVG 原始碼"
                                          : "Figure Code"}
                                      </label>
                                      <textarea
                                        rows="9"
                                        value={asset.source_code}
                                        placeholder={
                                          asset.source_language === "svg"
                                            ? "貼上 SVG 原始碼，右側即時預覽"
                                            : "輸入 Python 繪圖程式碼，右側即時預覽"
                                        }
                                        onChange={(event) => {
                                          const nextCode = event.target.value;
                                          updateBulkQuestionAsset(
                                            bulkEditingQuestion.localId,
                                            index,
                                            {
                                              source_language:
                                                asset.source_language ||
                                                "python",
                                              source_code: nextCode,
                                              ...(nextCode.trim()
                                                ? {
                                                    url: "",
                                                    storage_key: "",
                                                    mime_type: "",
                                                    preview_status: "idle",
                                                    preview_message: "",
                                                    _preview_url: "",
                                                    _preview_source_code: "",
                                                  }
                                                : {}),
                                            },
                                          );
                                        }}
                                      />
                                    </Field>
                                  </AssetSourcePanel>
                                ) : (
                                  <AssetSourcePanel>
                                    <Field>
                                      <label>圖片 URL</label>
                                      <input
                                        value={asset.url}
                                        placeholder="https://assets.sunnytseng.com/..."
                                        onChange={(event) => {
                                          const nextUrl = event.target.value;
                                          updateBulkQuestionAsset(
                                            bulkEditingQuestion.localId,
                                            index,
                                            {
                                              url: nextUrl,
                                              ...(nextUrl.trim()
                                                ? {
                                                    source_language: "",
                                                    source_code: "",
                                                    mime_type: "",
                                                    preview_status: "idle",
                                                    preview_message: "",
                                                    _preview_url: "",
                                                    _preview_source_code: "",
                                                  }
                                                : {}),
                                            },
                                          );
                                        }}
                                      />
                                    </Field>
                                    <p>貼上圖片連結，即可在右側確認圖片。</p>
                                  </AssetSourcePanel>
                                )}
                                <QuestionAssetEditorPreview
                                  asset={asset}
                                  inputMode={inputMode}
                                />
                              </AssetWorkspace>
                              <AssetDetails>
                                <Field>
                                  <label>圖片描述</label>
                                  <input
                                    value={asset.alt_text}
                                    placeholder="圖片描述，可空白"
                                    onChange={(event) =>
                                      updateBulkQuestionAsset(
                                        bulkEditingQuestion.localId,
                                        index,
                                        { alt_text: event.target.value },
                                      )
                                    }
                                  />
                                </Field>{" "}
                                <Field>
                                  <label>判讀說明</label>
                                  <textarea
                                    rows="3"
                                    value={asset.marking_guide}
                                    placeholder="給審核或人工判讀看的說明"
                                    onChange={(event) =>
                                      updateBulkQuestionAsset(
                                        bulkEditingQuestion.localId,
                                        index,
                                        { marking_guide: event.target.value },
                                      )
                                    }
                                  />
                                </Field>
                              </AssetDetails>
                            </>
                          )}
                        </QuestionAssetInputMode>
                      </AssetRow>
                    ))}
                  </AssetList>
                )}
              </AssetSection>
            </BulkModalBody>

            <BulkModalFooter>
              <IconTextButton
                type="button"
                title="按 Enter 完成；Shift + Enter 換行；Vim 編輯時 Enter 仍可換行"
                onClick={finishBulkQuestionEditor}
              >
                <FaCheck />
                完成
              </IconTextButton>
            </BulkModalFooter>
          </BulkModal>
        </BulkModalBackdrop>
      )}
      <ExpandedEditorModal
        editor={expandedEditorConfig}
        onClose={() => {
          setExpandedMarkdownField(null);
          setExpandedAssetSourceIndex(null);
          setExpandedBulkMarkdownField(null);
          setExpandedBulkAssetSource(null);
        }}
        onCopy={copyMarkdownText}
      />
    </PageShell>
  );
}
